import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Upload, FileText, AlertTriangle, CheckCircle2, Check,
  Users, ChevronRight,
  Receipt, Search, ArrowLeft, Wrench, Briefcase, CalendarDays, Building2,
  ChevronsUp, ChevronsDown, User, FileSpreadsheet, RefreshCw, HeartPulse,
  Save, Pencil, BedDouble, BarChart3, Files
} from 'lucide-react';
import toast from '../../../utils/toast';
import { staffAPI } from '../../../services/api';
import { useTabPersistence, usePersistence } from '../../../hooks/usePersistence';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import AccordionSection from '../../../components/UI/AccordionSection';
import { LeaveTypeBadge } from '../../../components/UI/LeaveTypeBadge';
import { getLeaveTypeGroupRowClass } from '../../../utils/leaveTypeBadgeColors';
import PillToggle from '../../../components/UI/PillToggle';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import { TradeBadge } from '../../../components/UI/TradeBadge';
import { getDepartmentBadgeColor, getDepartmentIcon, getDepartmentColor } from '../../../config/departmentConfig';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { CountBadge } from '../../../components/UI/CountBadge';
import MonthNavigator from '../../../components/UI/MonthNavigator';
import { Pagination } from '../../../components/UI/Pagination';
import ProcessingModal from '../../../components/UI/ProcessingModal';
import { getCurrentMonthKeySingapore } from '../../../utils/dateUtils';
import { APP_SHELL_STICKY_TOP_CLASS } from '../../../constants/layout';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const VALID_TABS = ['combined', 'levy-cost', 'salary-cost', 'mom-levy-bill', 'dorm-cost', 'pcp'];

// Invoice upload tabs (PCP, MOM Levy Bill) target the billing month as invoices
// arrive — including the in-progress current month. Cost-report tabs stay
// closed-period (last completed month only).
const UPLOAD_TABS = new Set(['pcp', 'mom-levy-bill']);

// PLC is a closed-period report: a month is only viewable once it has fully
// ended. The latest selectable period is therefore last month (e.g. on
// 13 Jun 2026 the newest viewable month is May 2026), never the in-progress
// current month. Mirrors the server-side guard in plc_available_months.
const getLastCompletedPeriod = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

// True when (year, month) is strictly after the last completed period.
const isAfterLastCompleted = (year, month) => {
  const { year: ly, month: lm } = getLastCompletedPeriod();
  return year > ly || (year === ly && month > lm);
};

const fmtCurrency = (val) => {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '$0.00';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtHours = (val) => {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num) || num === 0) return '-';
  const hasDecimal = num % 1 !== 0;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: hasDecimal ? 1 : 0,
    maximumFractionDigits: 1,
  });
};

// Renders the "% Allocation" cell on the By-Employee table. Computes
// the row's share of the employee's full-month salary cost
// (rowCost / employeeTotalCost) and displays it as "89.58%". Returns
// "-" when the denominator is zero or the inputs are not numeric.
//
// Cost-share, NOT hours-share: for non-consolidation rows the two are
// numerically identical (cost is proportional to hours), but the
// Lentoria-style contract consolidation in
// ``apply_employee_cost_consolidation`` produces synthetic 0-hour rows
// that hold real cost. Cost-share renders those rows correctly;
// hours-share would mis-show them as 0%.
const fmtPct = (rowCost, denomCost) => {
  const r = typeof rowCost === 'string' ? parseFloat(rowCost) : rowCost;
  const d = typeof denomCost === 'string' ? parseFloat(denomCost) : denomCost;
  if (isNaN(r) || isNaN(d) || !d) return '-';
  return `${((r / d) * 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const CostAllocationTableHeader = ({ costLabel, sticky = false }) => (
  <thead
    className={`table-header${sticky ? ` sticky ${APP_SHELL_STICKY_TOP_CLASS} z-10` : ''}`}
  >
    <tr className={sticky ? 'shadow-[0_1px_0_0_theme(colors.gray.200)]' : ''}>
      <th className="table-header-cell-compact">Project / Category</th>
      <th className="table-header-cell-compact text-right">Normal Hrs</th>
      <th className="table-header-cell-compact text-right">WD OT Hrs</th>
      <th className="table-header-cell-compact text-right">RD OT Hrs</th>
      <th className="table-header-cell-compact text-right">{costLabel}</th>
      <th className="table-header-cell-compact text-right">WD OT Cost</th>
      <th className="table-header-cell-compact text-right">RD OT Cost</th>
      <th className="table-header-cell-compact text-right">Total</th>
    </tr>
  </thead>
);

const LEVY_COST_FIELDS = [
  'total_normal_hours', 'total_weekday_ot_hours', 'total_rest_day_ot_hours',
  'workday_cost', 'weekday_ot_cost', 'sunday_ot_cost', 'total_cost',
];

const SALARY_COST_FIELDS = [
  'total_normal_hours', 'total_weekday_ot_hours', 'total_rest_day_ot_hours',
  'basic_cost', 'weekday_ot_cost', 'rest_day_ot_cost', 'total_cost',
];

const sumSection = (rows, fields = LEVY_COST_FIELDS) => {
  const totals = {};
  fields.forEach(f => {
    totals[f] = rows.reduce((sum, r) => sum + (parseFloat(r[f]) || 0), 0);
  });
  return totals;
};

const partitionConsolidated = (rows) => {
  const cgMap = {};
  const regular = [];
  rows.forEach(r => {
    if (r.consolidation_group) {
      const g = r.consolidation_group;
      if (!cgMap[g]) cgMap[g] = { label: g, allRows: [], byCode: {} };
      cgMap[g].allRows.push(r);
      const code = r.project_code || r.category;
      if (!cgMap[g].byCode[code]) cgMap[g].byCode[code] = [];
      cgMap[g].byCode[code].push(r);
    } else {
      regular.push(r);
    }
  });
  const consolidated = Object.values(cgMap).sort(
    (a, b) => b.allRows.reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0)
             - a.allRows.reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0)
  );
  return { consolidated, regular };
};

// Main-office project codes whose work-sites are shown as standalone rows
// (these are overhead offices, not client projects; each work-site is its
// own cost centre: 18 Office, 18 Factory, 38 Office, 38 Factory, etc.)
const MAIN_OFFICE_CODES = new Set(['P0000-018', 'P0000-038']);

// User-facing project label for a row in any PLC tab.
//
// For main-office codes (P0000-018 / P0000-038) each work-site is its own
// cost centre, so we prefer `project_name` ("18 Office", "18 Factory",
// "38 Office", "38 Factory") over the umbrella `parent_project_name`
// ("WEPL Main Office") -- otherwise every work-site collapses into a
// single ambiguous "WEPL Main Office" label and users can't tell them
// apart on the Salary / Levy / Dorm / Combined tabs (or in the XLSX
// exports, which mirror this helper via _MAIN_OFFICE_CODES guards in
// staff_core/services/plc_*_export_service.py).
//
// For customer projects we keep the historical preference of
// `parent_project_name` first, because that is the authoritative
// Project.project_name and it correctly rolls sub-projects (e.g. an
// "East Yard" work-site under "ABC Customer") up to the customer-facing
// label.
const getProjectDisplayName = (row) => {
  if (!row) return '';
  const pcode = row.project_code;
  if (pcode && MAIN_OFFICE_CODES.has(pcode)) {
    return row.project_name || row.parent_project_name || pcode;
  }
  return row.parent_project_name || row.project_name || pcode || row.category || '';
};

// Renders the label cell for a ghost row in the "Allocated to Projects
// Above" section AND the leave-breakdown sub-rows under each project
// accordion.  Two ghost shapes can both carry an office project_code:
//   1. Office-pool work redistribution ghost (row_type='project'): the
//      P0000-018 / P0000-038 office project itself was redistributed
//      across destination projects.  Render as project name + code.
//   2. Leave ghost (row_type='overhead'): a leave session whose project
//      attribution happens to land on an office project (e.g. OSL on a
//      Tue worked at WEPL Main Office; _infer_osl_project / EA's
//      activity_project_code resolve the project).  Render as the leave
//      type badge — this is still an OSL / EA / etc. semantically.
//
// Pre-2026-05-03 this function used `if pcode && MAIN_OFFICE_CODES.has(pcode)`
// alone, which incorrectly caught case #2 and showed "WEPL Main Office"
// instead of "Outpatient Sick Leave" / "External Activity" / etc.  The
// row_type guard added below is the correct discriminator (carried
// through from project_salary_service.py:_compute_worker_salary_allocations).
const renderAllocatedRowLabel = (row) => {
  const pcode = row.project_code;
  if (pcode && MAIN_OFFICE_CODES.has(pcode) && row.row_type === 'project') {
    const pname = row.project_name || row.category || pcode;
    return (
      <div>
        <div className="font-medium text-gray-700">{pname}</div>
        <div className="text-[11px] text-gray-400 font-mono leading-tight">{pcode}</div>
      </div>
    );
  }
  const cat = (row.category || '').trim().toLowerCase();
  // External Activity - Others used to render as a plain gray <span>,
  // breaking the "every leave/activity row gets its canonical chip"
  // pattern from the design system (frontend/src/styles/design-system-reference.md).
  // LeaveTypeBadge resolves the cyan EA chip via getLeaveTypeBadgeClass's
  // `n.includes('external activity')` matcher (leaveTypeBadgeColors.js),
  // so passing the full "External Activity – Others" name keeps the
  // "– Others" qualifier visible inside the cyan chip without us having
  // to special-case anything in the badge component.
  if (!pcode && cat === 'external activity') {
    return <LeaveTypeBadge leaveTypeName="External Activity – Others" size="xs" />;
  }
  return <LeaveTypeBadge leaveTypeName={row.category} size="xs" />;
};

const AllocatedToProjectsHint = ({ row }) => {
  if (!row.is_allocated_to_projects) return null;
  const leaveHours = parseFloat(row.leave_hours_taken) || 0;
  const leaveCost = parseFloat(row.leave_cost_allocated) || 0;
  const projectCount = (row.allocation_destinations || []).length;
  const workerCount = parseInt(row.allocated_worker_count, 10) || 0;
  const details = [
    `${fmtHours(leaveHours)}h`,
    projectCount > 0 ? `${projectCount} project${projectCount === 1 ? '' : 's'}` : null,
    workerCount > 0 ? `${workerCount} worker${workerCount === 1 ? '' : 's'}` : null,
    `${fmtCurrency(leaveCost)} allocated`,
  ].filter(Boolean);
  return (
    <div className="mt-0.5 text-[11px] leading-tight text-gray-500">
      {details.join(' · ')} to project rows above
    </div>
  );
};

const AllocationAuditRows = ({
  row,
  rowKey,
  isExpanded,
  onToggle,
  costComponentFields,
}) => {
  const destinations = row.allocation_destinations || [];
  const canExpand = destinations.length > 0;
  const leaveHours = parseFloat(row.leave_hours_taken) || 0;
  const leaveCost = parseFloat(row.leave_cost_allocated) || 0;
  const workerCount = parseInt(row.allocated_worker_count, 10) || 0;
  const [primaryCostField, secondaryCostField, tertiaryCostField] = costComponentFields;

  return (
    <React.Fragment key={rowKey}>
      <tr className="table-row-dense bg-blue-50/10 hover:bg-blue-50/40 transition-colors">
        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
          <button
            type="button"
            onClick={canExpand ? onToggle : undefined}
            disabled={!canExpand}
            aria-expanded={canExpand ? isExpanded : undefined}
            className="flex min-h-9 w-full items-center gap-2 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-default"
          >
            {canExpand && (
              <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            )}
            <div className={canExpand ? '' : 'ml-5'}>
              <div className="flex items-center gap-1.5">
                <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
                {canExpand && (
                  <span className="text-xs text-gray-400">
                    · {destinations.length} project{destinations.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] leading-tight text-gray-500">
                {workerCount > 0 && (
                  <>{workerCount} worker{workerCount === 1 ? '' : 's'} · </>
                )}
                {fmtCurrency(leaveCost)} already included in project rows above
              </div>
            </div>
          </button>
        </td>
        <td className="px-4 py-2 text-right tabular-nums">
          <div className="text-sm text-gray-700">{fmtHours(leaveHours)}</div>
          <div className="text-[10px] leading-tight text-blue-600">allocated</div>
        </td>
        <td className="px-4 py-2 text-sm text-gray-400 text-right">—</td>
        <td className="px-4 py-2 text-sm text-gray-400 text-right">—</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row[primaryCostField])}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row[secondaryCostField])}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row[tertiaryCostField])}</td>
        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
      </tr>
      {canExpand && isExpanded && destinations.map((destination) => (
        <tr
          key={destination.project_code || destination.project_name}
          className="table-row-dense bg-blue-50/30 hover:bg-blue-50/50 transition-colors"
        >
          <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
            <div className="font-medium">{destination.project_name || destination.project_code}</div>
            {destination.project_code && (
              <div className="text-[11px] text-gray-400 font-mono leading-tight">
                {destination.project_code}
              </div>
            )}
          </td>
          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(destination.hours)}</td>
          <td className="px-4 py-1.5 text-sm text-gray-400 text-right">—</td>
          <td className="px-4 py-1.5 text-sm text-gray-400 text-right">—</td>
          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(destination.cost)}</td>
          <td className="px-4 py-1.5 text-sm text-gray-400 text-right">—</td>
          <td className="px-4 py-1.5 text-sm text-gray-400 text-right">—</td>
          <td className="px-4 py-1.5 text-right tabular-nums">
            <div className="text-sm font-semibold text-gray-700">{fmtCurrency(destination.cost)}</div>
            <div className="text-[10px] leading-tight text-blue-600">included above</div>
          </td>
        </tr>
      ))}
    </React.Fragment>
  );
};

const WorkerSalaryAllocationLabel = ({ row }) => {
  if (row.row_type === 'project') {
    return (
      <>
        <div className="font-medium">{getProjectDisplayName(row) || row.category}</div>
        {row.project_code && (
          <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
        )}
      </>
    );
  }

  return (
    <>
      <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
      <AllocatedToProjectsHint row={row} />
    </>
  );
};

const isExternalActivityRow = (row) =>
  (row?.category || '').trim().toLowerCase() === 'external activity';

const getLeaveChildDisplayName = (row) => {
  if (!row?.project_code && isExternalActivityRow(row)) return 'Others';
  return getProjectDisplayName(row);
};

const getLeaveGroupCountText = (rows) => {
  const count = rows.length;
  const hasOthers = rows.some(row => !row.project_code);
  const noun = hasOthers ? 'item' : 'project';
  return `${count} ${count !== 1 ? `${noun}s` : noun}`;
};

const getLeaveConsolidationKey = (row) => (
  `${row.category}::${row.is_allocated_to_projects ? 'allocated' : 'standalone'}`
);

const mergeAllocationDestinations = (existing = [], incoming = []) => {
  const merged = new Map();
  [...existing, ...incoming].forEach((destination) => {
    const key = destination.project_code || `name::${destination.project_name || ''}`;
    const current = merged.get(key) || {
      project_code: destination.project_code || null,
      project_name: destination.project_name || destination.project_code || null,
      hours: 0,
      cost: 0,
    };
    current.hours += parseFloat(destination.hours) || 0;
    current.cost += parseFloat(destination.cost) || 0;
    merged.set(key, current);
  });
  return Array.from(merged.values()).sort((a, b) => (
    (a.project_code || a.project_name || '').localeCompare(
      b.project_code || b.project_name || ''
    )
  ));
};

const mergeAllocationAuditFields = (target, source) => {
  ['leave_hours_taken', 'leave_cost_allocated'].forEach(field => {
    target[field] = (parseFloat(target[field]) || 0) + (parseFloat(source[field]) || 0);
  });
  target.allocated_worker_count =
    (parseInt(target.allocated_worker_count, 10) || 0)
    + (parseInt(source.allocated_worker_count, 10) || 0);
  target.allocation_destinations = mergeAllocationDestinations(
    target.allocation_destinations,
    source.allocation_destinations,
  );
};

const splitLeaveRowsForDisplay = (rows) => {
  const regularRows = [];
  const projectCodeGroupMap = {};

  rows.forEach(row => {
    if (row.project_code || isExternalActivityRow(row)) {
      const category = row.category || 'Other';
      if (!projectCodeGroupMap[category]) projectCodeGroupMap[category] = [];
      projectCodeGroupMap[category].push(row);
    } else {
      regularRows.push(row);
    }
  });

  return {
    regularRows,
    projectCodeGroups: Object.entries(projectCodeGroupMap),
  };
};

// Row identity for the Combined tab's population-merge and project grouping.
// Rules (mirror of backend allocation_group_key in plc_allocation_utils.py):
//   1. Overhead rows (leave) with a project_code split by category so
//      "Annual Leave on P0000-018" stays distinct from "Paid Leave on
//      P0000-018".
//   2. Main-office project rows split by project_name so each work-site
//      ("18 Office", "18 Factory") renders as its own row.
//   3. Every other project row rolls up to its project_code.
//   4. Rows without a project_code use their category (leave type name).
//   5. Allocated-to-project audit rows stay separate from live-cost rows.
const getRowIdentity = (r) => {
  const pc = r.project_code;
  const cat = r.category || '';
  const allocationSuffix = r.is_allocated_to_projects
    ? '::allocated-to-projects'
    : '';
  if (pc) {
    if (r.row_type === 'overhead') {
      if (MAIN_OFFICE_CODES.has(pc)) {
        return `${pc}__${r.project_name || ''}__oh__${cat}${allocationSuffix}`;
      }
      return `${pc}::oh::${cat}${allocationSuffix}`;
    }
    if (MAIN_OFFICE_CODES.has(pc)) {
      return `${pc}__${r.project_name || ''}${allocationSuffix}`;
    }
    return `${pc}${allocationSuffix}`;
  }
  return `cat__${cat}${allocationSuffix}`;
};

// Sort priority for main-office work-site rows.
// Returns 1..6 for main offices, null otherwise.
// 1-3: P0000-018 (<N> Office → <N> Factory → other, e.g. WEPL Main Office)
// 4-6: P0000-038 (<N> Office → <N> Factory → other)
//
// The numeric-prefix guard is important: "WEPL Main Office" contains
// the word "office" but is NOT a work-site — it's the umbrella project
// label used for sessions without a work_site FK.  Requiring the name
// to start with a digit filters it into the "other" slot where it
// belongs (after the Factory row).
const getMainOfficePriority = (projectCode, projectName) => {
  const pn = (projectName || '').toLowerCase();
  const isWorkSiteName = /^\d/.test(pn);
  if (projectCode === 'P0000-018') {
    if (isWorkSiteName && pn.includes('office')) return 1;
    if (isWorkSiteName && pn.includes('factory')) return 2;
    return 3;
  }
  if (projectCode === 'P0000-038') {
    if (isWorkSiteName && pn.includes('office')) return 4;
    if (isWorkSiteName && pn.includes('factory')) return 5;
    return 6;
  }
  return null;
};

const MERGE_COST_FIELDS = [
  'total_normal_hours', 'total_weekday_ot_hours', 'total_rest_day_ot_hours',
  'workday_cost', 'weekday_ot_cost', 'sunday_ot_cost', 'total_cost',
  'levy_cost', 'salary_cost', 'dorm_cost',
  'leave_hours_taken', 'leave_cost_allocated',
];

// Merge worker + staff rows that share the same row identity, summing
// numeric cost/hour fields.  Only used when viewFilter === 'all' so the
// Workers / Staff pill toggle still inspects each population in isolation.
const mergeByPopulation = (rows) => {
  if (!rows || rows.length === 0) return rows;
  const merged = new Map();
  for (const r of rows) {
    const key = getRowIdentity(r);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, { ...r });
      continue;
    }
    for (const f of MERGE_COST_FIELDS) {
      existing[f] = (parseFloat(existing[f]) || 0) + (parseFloat(r[f]) || 0);
    }
    existing.allocated_worker_count =
      (parseInt(existing.allocated_worker_count, 10) || 0)
      + (parseInt(r.allocated_worker_count, 10) || 0);
    existing.allocation_destinations = mergeAllocationDestinations(
      existing.allocation_destinations,
      r.allocation_destinations,
    );
    if (existing.population !== r.population) existing.population = 'mixed';
  }
  return Array.from(merged.values());
};

// ============================================================================
// PROJECT LABOUR COST PAGE - TABBED REPORTING DASHBOARD
// ============================================================================

const ProjectLabourCostPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useTabPersistence('plc-report', 'combined', VALID_TABS);

  // Default to the latest completed month; honour a persisted selection only
  // if it isn't a current/future (in-progress) month, otherwise clamp it down.
  const initialPeriod = useMemo(() => {
    const fallback = getLastCompletedPeriod();
    const sy = Number(sessionStorage.getItem('plc-report-year'));
    const sm = Number(sessionStorage.getItem('plc-report-month'));
    if (sy && sm && !isAfterLastCompleted(sy, sm)) {
      return { year: sy, month: sm };
    }
    return fallback;
  }, []);

  const [selectedYear, setSelectedYear] = useState(initialPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState(initialPeriod.month);

  const maxMonth = useMemo(() => {
    if (UPLOAD_TABS.has(activeTab)) {
      return getCurrentMonthKeySingapore();
    }
    const { year, month } = getLastCompletedPeriod();
    return `${year}-${String(month).padStart(2, '0')}`;
  }, [activeTab]);

  useEffect(() => {
    sessionStorage.setItem('plc-report-year', String(selectedYear));
    sessionStorage.setItem('plc-report-month', String(selectedMonth));
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    staffAPI.plcGetAvailableMonths().catch(() => {});
  }, []);

  // Leaving an upload tab while on the in-progress month — clamp to last completed.
  useEffect(() => {
    if (!UPLOAD_TABS.has(activeTab) && isAfterLastCompleted(selectedYear, selectedMonth)) {
      const { year, month } = getLastCompletedPeriod();
      setSelectedYear(year);
      setSelectedMonth(month);
    }
  }, [activeTab, selectedYear, selectedMonth]);

  const selectedPeriod = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const handlePeriodChange = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    if (UPLOAD_TABS.has(activeTab)) {
      if (ym > getCurrentMonthKeySingapore()) return;
    } else if (isAfterLastCompleted(y, m)) {
      return;
    }
    setSelectedYear(y);
    setSelectedMonth(m);
  };

  const tabs = [
    { id: 'combined', label: 'Project Labour Cost', icon: BarChart3 },
    { id: 'salary-cost', label: 'Salary Cost', icon: DollarSign },
    { id: 'levy-cost', label: 'Levy Cost', icon: Receipt },
    { id: 'dorm-cost', label: 'Dormitory Cost', icon: Building2 },
    { id: 'mom-levy-bill', label: 'MOM Levy Bill', icon: Upload },
    { id: 'pcp', label: 'PCP', icon: HeartPulse },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => navigate('/admin/reports')}
            aria-label="Back to Reports"
            title="Back to Reports"
            className="icon-button-admin flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div className="space-y-1 min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Project Labour Cost</h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">Cost allocation reports by project code</p>
          </div>
        </div>

        <MonthNavigator
          month={selectedPeriod}
          onChange={handlePeriodChange}
          maxMonth={maxMonth}
          theme="admin"
          size="compact"
        />
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${activeTab === tab.id ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'combined' && (
        <CombinedPLCTab
          year={selectedYear}
          month={selectedMonth}
          onOpenTab={setActiveTab}
        />
      )}
      {activeTab === 'levy-cost' && (
        <LevyCostTab
          year={selectedYear}
          month={selectedMonth}
          onOpenMomLevyBill={() => setActiveTab('mom-levy-bill')}
        />
      )}
      {activeTab === 'salary-cost' && (
        <SalaryCostTab year={selectedYear} month={selectedMonth} />
      )}
      {activeTab === 'mom-levy-bill' && (
        <MomLevyBillTab
          year={selectedYear}
          month={selectedMonth}
          onMonthSync={(y, m) => { setSelectedYear(y); setSelectedMonth(m); }}
        />
      )}
      {activeTab === 'dorm-cost' && (
        <DormCostTab year={selectedYear} month={selectedMonth} />
      )}
      {activeTab === 'pcp' && (
        <PCPTab year={selectedYear} month={selectedMonth} />
      )}
    </div>
  );
};


// ============================================================================
// COMBINED PROJECT LABOUR COST TAB
// ============================================================================

const COMBINED_SOURCE_FIX_TABS = {
  levy: { tab: 'mom-levy-bill', label: 'Open MOM Levy Bill' },
  salary: { tab: 'salary-cost', label: 'Open Salary Cost' },
  dorm: { tab: 'dorm-cost', label: 'Open Dormitory Cost' },
};

const CombinedPLCTab = ({ year, month, onOpenTab }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewFilter, setViewFilter] = usePersistence('plc-combined-view-filter', 'all', ['all', 'workers', 'staff']);
  const [xlsxExporting, setXlsxExporting] = useState(false);
  const [workbookExporting, setWorkbookExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await staffAPI.plcGetCombinedReport({ year, month });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load combined report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExportXlsx = async () => {
    if (xlsxExporting) return;
    setXlsxExporting(true);
    try {
      const population = viewFilter === 'workers' ? 'workers' : viewFilter === 'staff' ? 'staff' : 'all';
      const res = await staffAPI.plcExportCombinedReport({ year, month, population });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const popSuffix = population === 'all' ? '' : `_${population.charAt(0).toUpperCase() + population.slice(1)}`;
      // Filename uses ``Summary`` to align with the worksheet title and
      // the bundled-workbook Tab 1 name.  Backend matches in
      // staff_core/views_plc.py :: plc_combined_report_xlsx.
      a.download = `PLC_Summary${popSuffix}_${monthNames[month - 1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XLSX export failed', err);
      toast.error('Export failed — please try again');
    } finally {
      setXlsxExporting(false);
    }
  };

  // Bundled workbook export -- single XLSX with 5 tabs:
  //   Summary | Salary | Levy | MOM Levy Bill | Dormitory
  // (finance "report by materiality" order). The Summary tab carries
  // the same content as the standalone Combined export above; the
  // Salary / Levy / Dormitory tabs carry the same content as their
  // respective standalone exports, while MOM Levy Bill carries the complete
  // source invoice regardless of the workbook population filter. See backend
  // staff_core/services/plc_workbook_export_service.py.
  const handleExportWorkbook = async () => {
    if (workbookExporting) return;
    setWorkbookExporting(true);
    try {
      const population = viewFilter === 'workers' ? 'workers' : viewFilter === 'staff' ? 'staff' : 'all';
      const res = await staffAPI.plcExportWorkbook({ year, month, population });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const popLabel = population === 'workers' ? 'Workers'
                     : population === 'staff' ? 'Staff'
                     : 'All';
      a.download = `PLC_${popLabel}_${monthNames[month - 1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Workbook export failed', err);
      toast.error('Workbook export failed — please try again');
    } finally {
      setWorkbookExporting(false);
    }
  };

  const {
    data_status: ds = {},
    rows: allRows = [],
    worker_rows: workerRowsRaw = [],
    staff_rows: staffRowsRaw = [],
    grand_totals: gt = {},
    worker_totals: workerTotals = {},
    staff_totals: staffTotals = {},
    vacancy_rows = [],
    total_vacancy_cost: vacancyCostStr = '0',
    combined_grand_total: combinedStr = '0',
    source_totals: srcTotals = {},
  } = report || {};

  const rows = useMemo(() => {
    const base = viewFilter === 'workers' ? workerRowsRaw
               : viewFilter === 'staff' ? staffRowsRaw
               : allRows;
    // Collapse worker + staff rows that share identity so the Projects
    // accordion doesn't show visually-identical duplicate children.
    // Workers / Staff pill views keep their split via the raw arrays.
    return viewFilter === 'all' ? mergeByPopulation(base) : base;
  }, [viewFilter, workerRowsRaw, staffRowsRaw, allRows]);
  const activeGt = viewFilter === 'workers' ? workerTotals : viewFilter === 'staff' ? staffTotals : gt;

  const showVacancy = viewFilter !== 'staff';
  const vacancyCost = parseFloat(vacancyCostStr) || 0;
  const activeTotal = parseFloat(activeGt.total_cost) || 0;
  const combinedGrandTotal = viewFilter === 'all'
    ? (parseFloat(combinedStr) || 0)
    : activeTotal + (showVacancy ? vacancyCost : 0);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    return rows.filter(r => r.category?.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [rows, searchTerm]);

  const projectRows = useMemo(() => filteredRows.filter(r => r.row_type === 'project'), [filteredRows]);
  const overheadRows = useMemo(() => filteredRows.filter(r => r.row_type !== 'project'), [filteredRows]);
  const allocatedOverheadRows = useMemo(
    () => overheadRows.filter(r => r.is_allocated_to_projects),
    [overheadRows],
  );
  const standaloneOverheadRows = useMemo(
    () => overheadRows.filter(r => !r.is_allocated_to_projects),
    [overheadRows],
  );
  const hasBothSections = projectRows.length > 0 && overheadRows.length > 0;

  const { projectTotal, overheadTotal } = useMemo(() => {
    let proj = 0, over = 0;
    for (const row of rows) {
      const cost = parseFloat(row.total_cost) || 0;
      if (row.row_type === 'project') proj += cost;
      else over += cost;
    }
    return { projectTotal: proj, overheadTotal: over };
  }, [rows]);

  const levyTotal = parseFloat(srcTotals.levy) || 0;
  const salaryTotal = parseFloat(srcTotals.salary) || 0;
  const dormTotal = parseFloat(srcTotals.dorm) || 0;

  const allGrandTotal = parseFloat(combinedStr) || 0;
  const levyPct = allGrandTotal > 0 ? ((levyTotal / allGrandTotal) * 100).toFixed(1) : '0.0';
  const salaryPct = allGrandTotal > 0 ? ((salaryTotal / allGrandTotal) * 100).toFixed(1) : '0.0';
  const dormPct = allGrandTotal > 0 ? ((dormTotal / allGrandTotal) * 100).toFixed(1) : '0.0';

  if (loading) {
    return <CombinedPLCSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Report</h3>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  const sources = [
    { key: 'levy', label: 'Levy', icon: Receipt },
    { key: 'salary', label: 'Salary', icon: DollarSign },
    { key: 'dorm', label: 'Dormitory', icon: Building2 },
  ];

  const availableSources = sources.filter(s => ds[s.key]?.available);
  const unavailableSources = sources.filter(s => !ds[s.key]?.available);

  const noData = availableSources.length === 0;

  const renderDataRow = (row, idx) => {
    if (row.is_allocated_to_projects) {
      const allocationKey = `combined__row_${row.category}_${idx}`;
      return (
        <AllocationAuditRows
          key={allocationKey}
          row={row}
          rowKey={allocationKey}
          isExpanded={!!expandedGroups[allocationKey]}
          onToggle={() => setExpandedGroups(prev => ({
            ...prev,
            [allocationKey]: !prev[allocationKey],
          }))}
          costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
        />
      );
    }
    return (
    <tr key={idx} className="table-row-dense">
      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
        {row.row_type === 'project' ? (
          <>
            <div className="font-medium">{row.project_name || row.category}</div>
            {row.project_code && (
              <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
            )}
          </>
        ) : (
          <>
            <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
            <AllocatedToProjectsHint row={row} />
          </>
        )}
      </td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
      <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
    </tr>
    );
  };

  const renderSectionSubtotal = (label, sectionRows) => {
    const t = sumSection(sectionRows, LEVY_COST_FIELDS);
    return (
      <tr className="bg-gray-100/80 border-t border-gray-200">
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
        <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Incomplete sources — only when something is missing */}
      {unavailableSources.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-800">
              {unavailableSources.length} of 3 cost sources unavailable
            </p>
            <ul className="mt-1 space-y-0.5">
              {unavailableSources.map(s => (
                <li key={s.key} className="text-xs text-amber-600">
                  {s.label}: {ds[s.key]?.reason || 'unavailable'}
                </li>
              ))}
            </ul>
            {onOpenTab && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {unavailableSources.map(s => {
                  const fix = COMBINED_SOURCE_FIX_TABS[s.key];
                  if (!fix) return null;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => onOpenTab(fix.tab)}
                      className="text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 rounded"
                    >
                      {fix.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {noData ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            None of the cost sources (Levy, Salary, Dormitory) have data for {MONTH_NAMES[month - 1]} {year}.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {viewFilter === 'all' ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <SummaryCard
                icon={DollarSign}
                label="Total Project Labour Cost"
                value={fmtCurrency(combinedGrandTotal)}
                subtitle={`${availableSources.length} of 3 sources`}
                accent="red"
              />
              <SummaryCard
                icon={Receipt}
                label="Levy"
                value={fmtCurrency(levyTotal)}
                subtitle={ds.levy?.available ? `${levyPct}% of total` : 'No data'}
                accent={ds.levy?.available ? 'blue' : 'gray'}
              />
              <SummaryCard
                icon={DollarSign}
                label="Salary"
                value={fmtCurrency(salaryTotal)}
                subtitle={ds.salary?.available ? `${salaryPct}% of total` : 'No data'}
                accent={ds.salary?.available ? 'green' : 'gray'}
              />
              <SummaryCard
                icon={Building2}
                label="Dormitory"
                value={fmtCurrency(dormTotal)}
                subtitle={ds.dorm?.available ? `${dormPct}% of total` : 'No data'}
                accent={ds.dorm?.available ? 'amber' : 'gray'}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
              <SummaryCard
                icon={DollarSign}
                label={viewFilter === 'workers' ? 'Worker Labour Cost' : 'Staff Labour Cost'}
                value={fmtCurrency(combinedGrandTotal)}
                subtitle={viewFilter === 'workers' ? 'Workers only' : 'Staff only'}
                accent="red"
              />
              <SummaryCard
                icon={Briefcase}
                label="Project Allocation"
                value={fmtCurrency(projectTotal)}
                subtitle={combinedGrandTotal > 0 ? `${((projectTotal / combinedGrandTotal) * 100).toFixed(1)}% of total` : '-'}
                accent="blue"
              />
              <SummaryCard
                icon={CalendarDays}
                label="Leave"
                value={fmtCurrency(overheadTotal)}
                subtitle={combinedGrandTotal > 0 ? `${((overheadTotal / combinedGrandTotal) * 100).toFixed(1)}% of total` : '-'}
                accent="amber"
              />
            </div>
          )}

          {/* Toolbar: Search + Toggle + Export */}
          <div className="flex items-center justify-between gap-3">
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search project / category..."
              theme="admin"
              className="max-w-xl flex-1"
            />
            <div className="flex items-center gap-2">
              <PillToggle
                value={viewFilter}
                onChange={setViewFilter}
                options={[
                  { id: 'all', label: 'All' },
                  { id: 'workers', label: 'Workers' },
                  { id: 'staff', label: 'Staff' },
                ]}
                variant="text"
                theme="admin"
                ariaLabel="Filter by employee classification"
              />
              <button
                onClick={handleExportXlsx}
                className="icon-button-admin"
                title="Download this tab only (Summary)"
                aria-label="Download Summary only"
                disabled={xlsxExporting || filteredRows.length === 0}
              >
                {xlsxExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              </button>
              <button
                onClick={handleExportWorkbook}
                className="icon-button-admin"
                title="Download full workbook (Summary + Salary + Levy + MOM Levy Bill + Dormitory)"
                aria-label="Download full PLC workbook"
                disabled={workbookExporting || filteredRows.length === 0}
              >
                {workbookExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Files className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Allocation Table */}
          {filteredRows.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchTerm ? 'No matching categories found' : 'No allocation data for this period'}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border overflow-x-auto md:overflow-visible">
              <table className="w-full min-w-[700px] md:min-w-0 table-fixed divide-y divide-gray-200">
                <colgroup>
                  <col style={{ width: '27%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11.5%' }} />
                  <col style={{ width: '11.5%' }} />
                  <col style={{ width: '11.5%' }} />
                  <col style={{ width: '11.5%' }} />
                </colgroup>
                <CostAllocationTableHeader costLabel="Workday Cost" sticky />

                {hasBothSections ? (
                  <>
                    <tbody className="divide-y divide-gray-100">
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 py-1.5">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            <Briefcase className="w-3 h-3" />
                            Projects
                          </div>
                        </td>
                      </tr>
                      {(() => {
                        const { consolidated, regular } = partitionConsolidated(projectRows);

                        // Build a unified list of rendered items with sort
                        // metadata.  Main-office work-site rows take priority
                        // 1..6; everything else (regular + consolidated groups
                        // like Lentoria) interleaves by project_code ASC.
                        const items = [];

                        consolidated.forEach(({ label, allRows, byCode }) => {
                          const accKey = `combined__cg_${label}`;
                          const isExpanded = !!expandedGroups[accKey];
                          const t = sumSection(allRows, LEVY_COST_FIELDS);
                          const codeKeys = Object.keys(byCode).sort();
                          const sortCode = codeKeys[0] || 'zzzz';
                          items.push({
                            sortPriority: null,
                            sortCode,
                            jsx: (
                              <React.Fragment key={`cg_${label}`}>
                                <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedGroups(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                      <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                </tr>
                                {isExpanded && codeKeys.map(code => {
                                  const codeRows = byCode[code];
                                  const ct = sumSection(codeRows, LEVY_COST_FIELDS);
                                  const codeName = getProjectDisplayName(codeRows[0]) || code;
                                  return (
                                    <tr key={code} className="bg-gray-50/50 table-row-dense">
                                      <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                        <div className="font-medium">{codeName}</div>
                                        <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                      </td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.workday_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.sunday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            ),
                          });
                        });

                        const projectGroups = {};
                        regular.forEach(r => {
                          const key = getRowIdentity(r);
                          if (!projectGroups[key]) projectGroups[key] = [];
                          projectGroups[key].push(r);
                        });

                        Object.keys(projectGroups).forEach(key => {
                          const group = projectGroups[key];
                          const firstRow = group[0];
                          const priority = getMainOfficePriority(firstRow.project_code, firstRow.project_name);
                          const sortCode = firstRow.project_code || key;

                          let jsx;
                          if (group.length === 1) {
                            const r = group[0];
                            // For single rows, prefer parent_project_name
                            // (authoritative Project.project_name) except
                            // for main-office work-site rows, which must
                            // retain their work-site label.
                            if (
                              r.row_type === 'project'
                              && r.parent_project_name
                              && !MAIN_OFFICE_CODES.has(r.project_code)
                            ) {
                              r.project_name = r.parent_project_name;
                            }
                            jsx = renderDataRow(r, key);
                          } else {
                            const accKey = `combined__proj_${key}`;
                            const isExpanded = !!expandedGroups[accKey];
                            const t = sumSection(group, LEVY_COST_FIELDS);
                            const parentName = getProjectDisplayName(firstRow) || firstRow.project_code || key;
                            jsx = (
                              <React.Fragment key={key}>
                                <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedGroups(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                      <div>
                                        <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                        <div className="text-[11px] text-gray-400 font-mono leading-tight">{firstRow.project_code || key}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                </tr>
                                {isExpanded && group.map((row, idx) => (
                                  <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                    <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                      <div className="font-medium">{row.project_name || row.project_code}</div>
                                    </td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          }

                          items.push({ sortPriority: priority, sortCode, jsx });
                        });

                        items.sort((a, b) => {
                          if (a.sortPriority !== null && b.sortPriority !== null) {
                            return a.sortPriority - b.sortPriority;
                          }
                          if (a.sortPriority !== null) return -1;
                          if (b.sortPriority !== null) return 1;
                          return (a.sortCode || '').localeCompare(b.sortCode || '');
                        });

                        return items.map(item => item.jsx);
                      })()}
                      {renderSectionSubtotal('Projects Subtotal', projectRows)}
                    </tbody>
                    {allocatedOverheadRows.length > 0 && (
                      <tbody className="divide-y divide-gray-100">
                        <tr className="bg-blue-50/60">
                          <td colSpan={8} className="px-4 py-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                              <CalendarDays className="w-3 h-3" />
                              Allocated to Projects Above
                            </div>
                          </td>
                        </tr>
                        {allocatedOverheadRows.map((row, idx) => {
                          const allocationKey = `combined__allocated_${row.category}_${idx}`;
                          return (
                            <AllocationAuditRows
                              key={allocationKey}
                              row={row}
                              rowKey={allocationKey}
                              isExpanded={!!expandedGroups[allocationKey]}
                              onToggle={() => setExpandedGroups(prev => ({
                                ...prev,
                                [allocationKey]: !prev[allocationKey],
                              }))}
                              costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
                            />
                          );
                        })}
                      </tbody>
                    )}
                    {standaloneOverheadRows.length > 0 && (
                    <tbody className="divide-y divide-gray-100">
                      <tr className="bg-amber-50/60">
                        <td colSpan={8} className="px-4 py-1.5">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                            <CalendarDays className="w-3 h-3" />
                            Leave
                          </div>
                        </td>
                      </tr>
                      {(() => {
                        const byCategory = {};
                        standaloneOverheadRows.forEach(r => {
                          const category = r.category || 'Other';
                          const key = getLeaveConsolidationKey({
                            ...r,
                            category,
                          });
                          if (!byCategory[key]) {
                            byCategory[key] = { category, rows: [] };
                          }
                          byCategory[key].rows.push(r);
                        });

                        const categoryEntries = Object.values(byCategory).map(({ category, rows: catRows }) => {
                          const projectRows_cat = catRows.filter(r => r.project_code);
                          const totalCost = catRows.reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                          return { category, allRows: catRows, projectRows: projectRows_cat, totalCost };
                        }).sort((a, b) => b.totalCost - a.totalCost);

                        return categoryEntries.map(({ category, allRows: allCatRows, projectRows: catProjectRows }) => {
                          if (allCatRows.length <= 1) {
                            const consolidated = { ...allCatRows[0] };
                            for (let i = 1; i < allCatRows.length; i++) {
                              LEVY_COST_FIELDS.forEach(f => {
                                consolidated[f] = (parseFloat(consolidated[f]) || 0) + (parseFloat(allCatRows[i][f]) || 0);
                              });
                            }
                            return renderDataRow(consolidated, category);
                          }

                          const t = sumSection(allCatRows, LEVY_COST_FIELDS);
                          const accKey = `combined__leave_${category}`;
                          const isExpanded = !!expandedGroups[accKey];
                          const sortedChildRows = [...allCatRows].sort((a, b) => {
                            const aHasCode = !!a.project_code;
                            const bHasCode = !!b.project_code;
                            if (aHasCode !== bHasCode) return aHasCode ? -1 : 1;
                            return (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0);
                          });
                          const countText = getLeaveGroupCountText(allCatRows);

                          return (
                            <React.Fragment key={category}>
                              <tr
                                className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                onClick={() => setExpandedGroups(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                              >
                                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                    <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                    <span className="text-xs text-gray-400">· {countText}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                              </tr>
                              {isExpanded && sortedChildRows.map((row, idx) => (
                                <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                  <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                    <div className="font-medium">{getLeaveChildDisplayName(row)}</div>
                                    {row.project_code && (
                                      <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                    )}
                                  </td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                  <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        });
                      })()}
                      {renderSectionSubtotal('Leave Subtotal', standaloneOverheadRows)}
                    </tbody>
                    )}

                    {showVacancy && vacancy_rows.length > 0 && (
                      <tbody className="divide-y divide-gray-100">
                        <tr className="bg-gray-50">
                          <td colSpan={8} className="px-4 py-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              <BedDouble className="w-3 h-3" />
                              Unoccupied Beds
                            </div>
                          </td>
                        </tr>
                        {vacancy_rows.map((vr, idx) => (
                          <tr key={idx} className="table-row-dense">
                            <td className="px-4 py-2 text-sm text-gray-900">
                              <div className="font-medium">{vr.dorm_name}</div>
                              <div className="text-[11px] text-gray-400 leading-tight">
                                {vr.vacant_beds_equivalent} vacant bed{vr.vacant_beds_equivalent !== 1 ? 's' : ''} equiv.
                              </div>
                            </td>
                            <td colSpan={6} />
                            <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">
                              {fmtCurrency(vr.vacancy_cost)}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-100/80 border-t border-gray-200">
                          <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Vacancy Subtotal
                          </td>
                          <td colSpan={6} />
                          <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">
                            {fmtCurrency(vacancyCost)}
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </>
                ) : (
                  <tbody className="divide-y divide-gray-100">
                    {filteredRows.map(renderDataRow)}
                  </tbody>
                )}
              </table>
            </div>
          )}

          {/* Grand Total Bar */}
          {filteredRows.length > 0 && (
            <div className="bg-gray-800 text-white rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {viewFilter === 'workers' ? 'Worker Labour Cost' : viewFilter === 'staff' ? 'Staff Labour Cost' : 'Total Project Labour Cost'}
                  </p>
                  <p className="text-xl font-bold">{fmtCurrency(combinedGrandTotal)}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="text-right">
                  <p className="text-gray-400 text-xs">Projects</p>
                  <p className="font-semibold tabular-nums">{fmtCurrency(projectTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs">Leave</p>
                  <p className="font-semibold tabular-nums">{fmtCurrency(overheadTotal)}</p>
                </div>
                {showVacancy && vacancyCost > 0 && (
                  <div className="text-right">
                    <p className="text-gray-400 text-xs">Vacancy</p>
                    <p className="font-semibold tabular-nums">{fmtCurrency(vacancyCost)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CombinedPLCSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <div className="h-6 sm:h-7 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-24 mb-1" />
              <div className="h-3 bg-gray-100 rounded w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="divide-y divide-gray-100">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="px-4 py-3 flex items-center animate-pulse">
            <div className="h-4 bg-gray-200 rounded flex-1 mr-4" />
            <div className="h-4 bg-gray-200 rounded w-14 mr-3" />
            <div className="h-4 bg-gray-200 rounded w-14 mr-3" />
            <div className="h-4 bg-gray-200 rounded w-14 mr-3" />
            <div className="h-4 bg-gray-200 rounded w-20 mr-3" />
            <div className="h-4 bg-gray-200 rounded w-20 mr-3" />
            <div className="h-4 bg-gray-200 rounded w-20 mr-3" />
            <div className="h-4 bg-gray-200 rounded w-20" />
          </div>
        ))}
      </div>
    </div>
  </div>
);


// ============================================================================
// LEVY COST TAB
// ============================================================================

const LevyCostTab = ({ year, month, onOpenMomLevyBill }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOslCategories, setExpandedOslCategories] = useState({});
  const [levyGrouping, setLevyGrouping] = usePersistence('plc-levy-grouping', 'project', ['project', 'department']);
  const [populationFilter, setPopulationFilter] = usePersistence('plc-levy-population', 'all', ['all', 'workers', 'staff']);
  const [deptReport, setDeptReport] = useState(null);
  const [deptLoading, setDeptLoading] = useState(false);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [xlsxExporting, setXlsxExporting] = useState(false);

  const handleExportXlsx = async () => {
    if (xlsxExporting) return;
    setXlsxExporting(true);
    try {
      const population = populationFilter === 'workers' ? 'workers' : populationFilter === 'staff' ? 'staff' : 'all';
      const res = await staffAPI.plcExportLevyReport({ year, month, grouping: levyGrouping, population });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const grpSuffix = levyGrouping === 'department' ? '_by_Dept' : '_by_Project';
      const popSuffix = population === 'all' ? '' : `_${population.charAt(0).toUpperCase() + population.slice(1)}`;
      a.download = `PLC_Levy_Cost${grpSuffix}${popSuffix}_${monthNames[month - 1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XLSX export failed', err);
      toast.error('Export failed — please try again');
    } finally {
      setXlsxExporting(false);
    }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await staffAPI.plcGetLevyReport({ year, month });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load levy report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchDeptReport = useCallback(async () => {
    setDeptLoading(true);
    try {
      const res = await staffAPI.plcGetLevyReportByDepartment({ year, month });
      setDeptReport(res.data);
      const expD = {};
      (res.data.departments || []).forEach(d => { expD[d.department_name] = true; });
      setExpandedDepts(expD);
    } catch {
      toast.error('Failed to load department breakdown');
    } finally {
      setDeptLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (levyGrouping === 'department' && !deptReport) {
      fetchDeptReport();
    }
  }, [levyGrouping, deptReport, fetchDeptReport]);

  useEffect(() => {
    setDeptReport(null);
  }, [year, month]);

  const {
    rows: allRows = [],
    worker_rows: workerRowsRaw = [],
    staff_rows: staffRowsRaw = [],
    grand_totals: gt = {},
    worker_totals: wt = {},
    staff_totals: st = {},
    bill_info,
    worker_count: reportWorkerCount = 0,
    staff_count: reportStaffCount = 0,
  } = report || {};

  const rows = populationFilter === 'workers' ? workerRowsRaw : populationFilter === 'staff' ? staffRowsRaw : allRows;
  const popGt = populationFilter === 'workers' ? wt : populationFilter === 'staff' ? st : gt;

  const allDeptDepartments = deptReport?.departments || [];
  const workerDeptDepartments = deptReport?.worker_departments || [];
  const staffDeptDepartments = deptReport?.staff_departments || [];
  const deptDepartments = populationFilter === 'workers' ? workerDeptDepartments : populationFilter === 'staff' ? staffDeptDepartments : allDeptDepartments;
  const deptGt = populationFilter === 'workers' ? (deptReport?.worker_totals || {}) : populationFilter === 'staff' ? (deptReport?.staff_totals || {}) : (deptReport?.grand_totals || {});
  const activeGt = levyGrouping === 'department' && deptReport ? deptGt : popGt;

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    return rows.filter(row =>
      row.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [rows, searchTerm]);

  const filteredDepts = useMemo(() => {
    if (!searchTerm) return deptDepartments;
    const term = searchTerm.toLowerCase();
    return deptDepartments.filter(d =>
      d.department_name.toLowerCase().includes(term) ||
      d.rows.some(r => r.category?.toLowerCase().includes(term))
    );
  }, [deptDepartments, searchTerm]);

  const projectRows = useMemo(() => filteredRows.filter(r => r.row_type === 'project'), [filteredRows]);
  const overheadRows = useMemo(() => filteredRows.filter(r => r.row_type !== 'project'), [filteredRows]);
  const allocatedOverheadRows = useMemo(
    () => overheadRows.filter(r => r.is_allocated_to_projects),
    [overheadRows],
  );
  const standaloneOverheadRows = useMemo(
    () => overheadRows.filter(r => !r.is_allocated_to_projects),
    [overheadRows],
  );
  const hasBothSections = projectRows.length > 0 && overheadRows.length > 0;

  const { projectTotal, overheadTotal } = useMemo(() => {
    let proj = 0, over = 0;
    for (const row of rows) {
      const cost = parseFloat(row.total_cost) || 0;
      if (row.row_type === 'project') proj += cost;
      else over += cost;
    }
    return { projectTotal: proj, overheadTotal: over };
  }, [rows]);

  const totalCost = parseFloat(activeGt?.total_cost) || 0;
  const projectPct = totalCost > 0 ? ((projectTotal / totalCost) * 100).toFixed(1) : '0.0';
  const overheadPct = totalCost > 0 ? ((overheadTotal / totalCost) * 100).toFixed(1) : '0.0';

  const toggleDept = (name) => setExpandedDepts(prev => ({ ...prev, [name]: !prev[name] }));

  const deptAllExpanded = filteredDepts.length > 0 && filteredDepts.every(d => expandedDepts[d.department_name]);
  const toggleAllDepts = () => {
    const next = {};
    filteredDepts.forEach(d => { next[d.department_name] = !deptAllExpanded; });
    setExpandedDepts(next);
  };

  const hasVisibleData = levyGrouping === 'project'
    ? filteredRows.length > 0
    : filteredDepts.length > 0;

  if (loading) {
    return <LevyCostSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Report</h3>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (!report?.has_bill) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Receipt className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Levy Bill Uploaded</h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto">
          No MOM Levy Bill has been uploaded for {MONTH_NAMES[month - 1]} {year}.
          Switch to the MOM Levy Bill tab to upload the monthly CSV from MOM.
        </p>
      </div>
    );
  }

  const AUDIT_CATEGORIES = ['Others', 'Absent', 'Unpaid Leave'];

  const renderDataRow = (row, idx) => {
    if (row.is_allocated_to_projects) {
      const allocationKey = `levy__row_${row.category}_${idx}`;
      return (
        <AllocationAuditRows
          key={allocationKey}
          row={row}
          rowKey={allocationKey}
          isExpanded={!!expandedOslCategories[allocationKey]}
          onToggle={() => setExpandedOslCategories(prev => ({
            ...prev,
            [allocationKey]: !prev[allocationKey],
          }))}
          costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
        />
      );
    }
    const workers = row.workers || [];
    const showWorkerAudit = row.row_type !== 'project' && AUDIT_CATEGORIES.includes(row.category) && workers.length > 0;

    return (
    <tr key={idx} className="table-row-dense">
      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
        {row.row_type === 'project' ? (
          <>
            <div className="font-medium">{row.project_name || row.category}</div>
            {row.project_code && (
              <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
              {workers.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                  <Users className="w-3 h-3" />
                  {workers.length}
                </span>
              )}
            </div>
            <AllocatedToProjectsHint row={row} />
            {showWorkerAudit && (
              <div className="mt-0.5 space-y-0">
                {workers.slice(0, 5).map((w, i) => (
                  <div key={i} className="text-[11px] text-gray-400 leading-tight">
                    {w.name || w.staff_id}
                    {w.employment_end_date && (
                      <span className="ml-1 text-amber-500 font-medium">
                        (FS: left {new Date(w.employment_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})
                      </span>
                    )}
                  </div>
                ))}
                {workers.length > 5 && (
                  <div className="text-[11px] text-gray-400">+{workers.length - 5} more</div>
                )}
              </div>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
      <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
    </tr>
    );
  };

  const renderSectionSubtotal = (label, sectionRows) => {
    const t = sumSection(sectionRows, LEVY_COST_FIELDS);
    return (
      <tr className="bg-gray-100/80 border-t border-gray-200">
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
        <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
      </tr>
    );
  };

  const renderDeptDataRow = (row, idx) => (
    <tr key={idx} className="table-row-dense">
      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
        {row.row_type === 'project' ? (
          <>
            <div className="font-medium">{row.project_name || row.category}</div>
            {row.project_code && (
              <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
            )}
          </>
        ) : (
          <>
            <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
            <AllocatedToProjectsHint row={row} />
          </>
        )}
      </td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
      <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
    </tr>
  );

  const levyCostColgroup = (
    <colgroup>
      <col style={{ width: '27%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '11.5%' }} />
      <col style={{ width: '11.5%' }} />
      <col style={{ width: '11.5%' }} />
      <col style={{ width: '11.5%' }} />
    </colgroup>
  );

  const levyCountSubtitle = populationFilter === 'workers'
    ? `${reportWorkerCount} workers`
    : populationFilter === 'staff'
      ? `${reportStaffCount} staff`
      : `${bill_info?.total_matched || 0} workers`;
  const levyAllocatedSubtitle = bill_info?.company_name
    ? `${bill_info.company_name} · ${levyCountSubtitle}`
    : levyCountSubtitle;

  return (
    <div className="space-y-4">
      {/* Unmatched workers — billed but not allocated */}
      {bill_info?.total_unmatched > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-800">
              {bill_info.total_unmatched} unmatched worker{bill_info.total_unmatched !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              MOM billed {fmtCurrency(bill_info.total_levy_amount)} for {bill_info.total_entries} workers;
              only {bill_info.total_matched} matched workers are allocated below.
              Fix FIN matching on the MOM Levy Bill tab.
            </p>
            {onOpenMomLevyBill && (
              <button
                type="button"
                onClick={onOpenMomLevyBill}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 rounded"
              >
                Open MOM Levy Bill
              </button>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
        <SummaryCard
          icon={DollarSign}
          label="Total Levy Allocated"
          value={fmtCurrency(activeGt.total_cost)}
          subtitle={levyAllocatedSubtitle}
          accent="red"
        />
        <SummaryCard
          icon={Briefcase}
          label="Project Allocation"
          value={fmtCurrency(projectTotal)}
          subtitle={`${projectPct}% of total`}
          accent="blue"
        />
        <SummaryCard
          icon={CalendarDays}
          label="Leave"
          value={fmtCurrency(overheadTotal)}
          subtitle={`${overheadPct}% of total`}
          accent="amber"
        />
      </div>

      {/* Toolbar: Search + Scope + View + Expand + Export */}
      <div className="flex items-center justify-between gap-3">
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={setSearchTerm}
          placeholder={levyGrouping === 'department' ? 'Search department / category...' : 'Search project / category...'}
          theme="admin"
          className="max-w-xl flex-1"
        />
        <div className="flex items-center gap-2">
          <PillToggle
            value={populationFilter}
            onChange={setPopulationFilter}
            options={[
              { id: 'all', label: 'All' },
              { id: 'workers', label: 'Workers' },
              { id: 'staff', label: 'Staff' },
            ]}
            variant="text"
            theme="admin"
            ariaLabel="Filter by employee classification"
          />
          <PillToggle
            value={levyGrouping}
            onChange={setLevyGrouping}
            options={[
              { id: 'project', label: 'By Project' },
              { id: 'department', label: 'By Dept' },
            ]}
            variant="text"
            theme="admin"
            ariaLabel="Levy grouping mode"
          />
          {levyGrouping === 'department' && filteredDepts.length > 1 && (
            <button
              onClick={toggleAllDepts}
              className="icon-button-admin"
              title={deptAllExpanded ? 'Collapse All' : 'Expand All'}
              aria-label={deptAllExpanded ? 'Collapse All' : 'Expand All'}
            >
              {deptAllExpanded ? <ChevronsUp className="w-4 h-4" /> : <ChevronsDown className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={handleExportXlsx}
            className="icon-button-admin"
            title="Export Excel (.xlsx)"
            aria-label="Export Excel"
            disabled={xlsxExporting || !hasVisibleData}
          >
            {xlsxExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* ---- BY PROJECT (default view) ---- */}
      {levyGrouping === 'project' && (
        <>
          {filteredRows.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchTerm ? 'No matching categories found' : 'No levy allocation data for this period'}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border overflow-x-auto md:overflow-visible">
                <table className="w-full min-w-[700px] md:min-w-0 table-fixed divide-y divide-gray-200">
                  {levyCostColgroup}
                  <CostAllocationTableHeader costLabel="Workday Cost" sticky />

                  {hasBothSections ? (
                    <>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="bg-gray-50">
                          <td colSpan={8} className="px-4 py-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              <Briefcase className="w-3 h-3" />
                              Projects
                            </div>
                          </td>
                        </tr>
                        {(() => {
                          const { consolidated, regular } = partitionConsolidated(projectRows);
                          const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                            const accKey = `levy__cg_${label}`;
                            const isExpanded = !!expandedOslCategories[accKey];
                            const t = sumSection(allRows, LEVY_COST_FIELDS);
                            const codeKeys = Object.keys(byCode).sort((a, b) => {
                              const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                              const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                              return bT - aT;
                            });
                            return (
                              <React.Fragment key={`cg_${label}`}>
                                <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                      <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                </tr>
                                {isExpanded && codeKeys.map(code => {
                                  const codeRows = byCode[code];
                                  const ct = sumSection(codeRows, LEVY_COST_FIELDS);
                                  const codeName = getProjectDisplayName(codeRows[0]) || code;
                                  return (
                                    <tr key={code} className="bg-gray-50/50 table-row-dense">
                                      <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                        <div className="font-medium">{codeName}</div>
                                        <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                      </td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.workday_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.sunday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          });
                          const projectGroups = {};
                          regular.forEach(r => {
                            const key = getRowIdentity(r);
                            if (!projectGroups[key]) projectGroups[key] = [];
                            projectGroups[key].push(r);
                          });
                          const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                            const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                            const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                            return bTotal - aTotal;
                          });
                          const regJsx = sortedKeys.map(key => {
                            const group = projectGroups[key];
                            if (group.length === 1) {
                              const r = group[0];
                              if (
                                r.row_type === 'project'
                                && r.parent_project_name
                                && !MAIN_OFFICE_CODES.has(r.project_code)
                              ) {
                                r.project_name = r.parent_project_name;
                              }
                              return renderDataRow(r, key);
                            }
                            const accKey = `levy__proj_${key}`;
                            const isExpanded = !!expandedOslCategories[accKey];
                            const t = sumSection(group, LEVY_COST_FIELDS);
                            const parentName = getProjectDisplayName(group[0]) || key;
                            return (
                              <React.Fragment key={key}>
                                <tr
                                  className="table-row-dense cursor-pointer"
                                  onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                >
                                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                      <div>
                                        <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                        <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                </tr>
                                {isExpanded && group.map((row, idx) => (
                                  <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                    <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                      <div className="font-medium">{row.project_name || row.project_code}</div>
                                    </td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          });
                          return [...cgJsx, ...regJsx];
                        })()}
                        {renderSectionSubtotal('Projects Subtotal', projectRows)}
                      </tbody>
                      {allocatedOverheadRows.length > 0 && (
                        <tbody className="divide-y divide-gray-100">
                          <tr className="bg-blue-50/60">
                            <td colSpan={8} className="px-4 py-1.5">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                <CalendarDays className="w-3 h-3" />
                                Allocated to Projects Above
                              </div>
                            </td>
                          </tr>
                          {allocatedOverheadRows.map((row, idx) => {
                            const allocationKey = `levy__allocated_${row.category}_${idx}`;
                            return (
                              <AllocationAuditRows
                                key={allocationKey}
                                row={row}
                                rowKey={allocationKey}
                                isExpanded={!!expandedOslCategories[allocationKey]}
                                onToggle={() => setExpandedOslCategories(prev => ({
                                  ...prev,
                                  [allocationKey]: !prev[allocationKey],
                                }))}
                                costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
                              />
                            );
                          })}
                        </tbody>
                      )}
                      {standaloneOverheadRows.length > 0 && (
                      <tbody className="divide-y divide-gray-100">
                        <tr className="bg-amber-50/60">
                          <td colSpan={8} className="px-4 py-1.5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                              <CalendarDays className="w-3 h-3" />
                              Leave
                            </div>
                          </td>
                        </tr>
                        {(() => {
                          const { regularRows, projectCodeGroups } = splitLeaveRowsForDisplay(standaloneOverheadRows);

                          const consolidatedMap = {};
                          regularRows.forEach(r => {
                            const consolidationKey = getLeaveConsolidationKey(r);
                            if (!consolidatedMap[consolidationKey]) {
                              consolidatedMap[consolidationKey] = { ...r, workers: [...(r.workers || [])] };
                            } else {
                              LEVY_COST_FIELDS.forEach(f => {
                                consolidatedMap[consolidationKey][f] =
                                  (parseFloat(consolidatedMap[consolidationKey][f]) || 0) + (parseFloat(r[f]) || 0);
                              });
                              const existing = consolidatedMap[consolidationKey];
                              mergeAllocationAuditFields(existing, r);
                              const seen = new Set((existing.workers || []).map(w => w.staff_id));
                              (r.workers || []).forEach(w => {
                                if (!seen.has(w.staff_id)) { existing.workers.push(w); seen.add(w.staff_id); }
                              });
                            }
                          });
                          const flatRows = Object.values(consolidatedMap)
                            .sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));

                          return (
                            <>
                              {flatRows.map(renderDataRow)}
                              {projectCodeGroups.map(([category, catRows]) => {
                                const accKey = `levy__leave_${category}`;
                                const isExpanded = !!expandedOslCategories[accKey];
                                const t = sumSection(catRows, LEVY_COST_FIELDS);
                                const sortedCatRows = [...catRows].sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));
                                return (
                                  <React.Fragment key={category}>
                                    <tr
                                      className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                      onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                    >
                                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                          <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                          <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                          <span className="text-xs text-gray-400">· {getLeaveGroupCountText(sortedCatRows)}</span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                      <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                    </tr>
                                    {isExpanded && sortedCatRows.map((row, idx) => (
                                      <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                        <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                          <div className="font-medium">{getLeaveChildDisplayName(row)}</div>
                                          {row.project_code && (
                                            <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                          )}
                                        </td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </>
                          );
                        })()}
                        {renderSectionSubtotal('Leave Subtotal', standaloneOverheadRows)}
                      </tbody>
                      )}
                    </>
                  ) : (
                    <tbody className="divide-y divide-gray-100">
                      {filteredRows.map(renderDataRow)}
                    </tbody>
                  )}
                </table>
            </div>
          )}
        </>
      )}

      {/* ---- BY DEPARTMENT ---- */}
      {levyGrouping === 'department' && (
        <>
          {deptLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl border p-5 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-1.5" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                    <div className="h-5 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!deptLoading && filteredDepts.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchTerm ? 'No matching departments found' : 'No levy allocation data for this period'}
                </p>
              </div>
            </div>
          )}

          {!deptLoading && filteredDepts.length > 0 && (
            <div className="space-y-3">
              {filteredDepts.map((dept) => {
                const isUnassigned = dept.department_name === 'Unassigned';
                const hasTradeBreakdown = dept.trades?.length > 0;
                const deptProjectRows = dept.rows.filter(r => r.row_type === 'project');
                const deptOverheadRows = dept.rows.filter(r => r.row_type !== 'project');
                const hasBothDeptSections = deptProjectRows.length > 0 && deptOverheadRows.length > 0;

                const renderDeptSubtotal = (label, sectionRows) => {
                  const t = sumSection(sectionRows, LEVY_COST_FIELDS);
                  return (
                    <tr className="bg-gray-100/80 border-t border-gray-200">
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                      <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                    </tr>
                  );
                };

                const renderTradeContent = () => {
                  return dept.trades.map((trade) => {
                    const tradeKey = `${dept.department_name}__trade_${trade.trade_name}`;
                    const isTradeExpanded = !!expandedOslCategories[tradeKey];
                    const tradeProjectRows = trade.rows.filter(r => r.row_type === 'project');
                    const tradeOverheadRows = trade.rows.filter(r => r.row_type !== 'project');
                    const hasBothTradeSections = tradeProjectRows.length > 0 && tradeOverheadRows.length > 0;

                    return (
                      <tbody key={trade.trade_name} className="divide-y divide-gray-100">
                        <tr
                          className="bg-rose-50/60 cursor-pointer hover:bg-rose-50 transition-colors"
                          onClick={() => setExpandedOslCategories(prev => ({ ...prev, [tradeKey]: !prev[tradeKey] }))}
                        >
                          <td colSpan={8} className="px-4 py-2.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isTradeExpanded ? 'rotate-90' : ''}`} />
                                <Wrench className="w-3.5 h-3.5 text-rose-600" />
                                <span className="text-sm font-semibold text-gray-900">{trade.trade_name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <InfoBadge color="rose" label={`${trade.worker_count} ${trade.worker_count === 1 ? 'worker' : 'workers'}`} size="xs" />
                                <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmtCurrency(trade.subtotals?.total_cost)}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {isTradeExpanded && (() => {
                          if (hasBothTradeSections) {
                            return (
                              <>
                                <tr className="bg-gray-50">
                                  <td colSpan={8} className="pl-10 pr-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                      <Briefcase className="w-3 h-3" />
                                      Projects
                                    </div>
                                  </td>
                                </tr>
                                {(() => {
                                  const { consolidated, regular } = partitionConsolidated(tradeProjectRows);
                                  const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                                    const accKey = `${dept.department_name}__trade_${trade.trade_name}__cg_${label}`;
                                    const isExpanded = !!expandedOslCategories[accKey];
                                    const t = sumSection(allRows, LEVY_COST_FIELDS);
                                    const codeKeys = Object.keys(byCode).sort((a, b) => {
                                      const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                      const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                      return bT - aT;
                                    });
                                    return (
                                      <React.Fragment key={`cg_${label}`}>
                                        <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                          <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                              <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                              <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                        </tr>
                                        {isExpanded && codeKeys.map(code => {
                                          const codeRows = byCode[code];
                                          const ct = sumSection(codeRows, LEVY_COST_FIELDS);
                                          const codeName = getProjectDisplayName(codeRows[0]) || code;
                                          return (
                                            <tr key={code} className="bg-gray-50/50 table-row-dense">
                                              <td className="pl-14 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                <div className="font-medium">{codeName}</div>
                                                <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                              </td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.workday_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.sunday_ot_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                            </tr>
                                          );
                                        })}
                                      </React.Fragment>
                                    );
                                  });
                                  const projectGroups = {};
                                  regular.forEach(r => {
                                    const key = getRowIdentity(r);
                                    if (!projectGroups[key]) projectGroups[key] = [];
                                    projectGroups[key].push(r);
                                  });
                                  const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                                    const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                    const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                    return bTotal - aTotal;
                                  });
                                  const regJsx = sortedKeys.map(key => {
                                    const group = projectGroups[key];
                                    if (group.length === 1) {
                                      const r = group[0];
                                      if (
                                        r.row_type === 'project'
                                        && r.parent_project_name
                                        && !MAIN_OFFICE_CODES.has(r.project_code)
                                      ) {
                                        r.project_name = r.parent_project_name;
                                      }
                                      return (
                                        <tr key={key} className="table-row-dense">
                                          <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                            <div className="font-medium">{r.project_name || r.category}</div>
                                            {r.project_code && <div className="text-[11px] text-gray-400 font-mono leading-tight">{r.project_code}</div>}
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_normal_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.workday_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.weekday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.sunday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(r.total_cost)}</td>
                                        </tr>
                                      );
                                    }
                                    const accKey = `${dept.department_name}__trade_${trade.trade_name}__proj_${key}`;
                                    const isExpanded = !!expandedOslCategories[accKey];
                                    const t = sumSection(group, LEVY_COST_FIELDS);
                                    const parentName = getProjectDisplayName(group[0]) || key;
                                    return (
                                      <React.Fragment key={key}>
                                        <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                          <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                              <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                              <div>
                                                <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                                <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                              </div>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                        </tr>
                                        {isExpanded && group.map((row, idx) => (
                                          <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                            <td className="pl-14 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                              <div className="font-medium">{row.project_name || row.project_code}</div>
                                            </td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    );
                                  });
                                  return [...cgJsx, ...regJsx];
                                })()}
                                {(() => {
                                  const t = sumSection(tradeProjectRows, LEVY_COST_FIELDS);
                                  return (
                                    <tr className="bg-gray-100/60 border-t border-gray-200">
                                      <td className="pl-10 pr-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Projects Subtotal</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-xs font-bold text-gray-600 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                    </tr>
                                  );
                                })()}
                                <tr className="bg-amber-50/60">
                                  <td colSpan={8} className="pl-10 pr-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                      <CalendarDays className="w-3 h-3" />
                                      Leave
                                    </div>
                                  </td>
                                </tr>
                                {(() => {
                                  const { regularRows, projectCodeGroups } = splitLeaveRowsForDisplay(tradeOverheadRows);

                                  const consolidatedMap = {};
                                  regularRows.forEach(r => {
                                    const consolidationKey = getLeaveConsolidationKey(r);
                                    if (!consolidatedMap[consolidationKey]) {
                                      consolidatedMap[consolidationKey] = { ...r };
                                    } else {
                                      LEVY_COST_FIELDS.forEach(f => {
                                        consolidatedMap[consolidationKey][f] =
                                          (parseFloat(consolidatedMap[consolidationKey][f]) || 0) + (parseFloat(r[f]) || 0);
                                      });
                                      mergeAllocationAuditFields(consolidatedMap[consolidationKey], r);
                                    }
                                  });
                                  const flatRows = Object.values(consolidatedMap)
                                    .sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));

                                  return (
                                    <>
                                      {flatRows.map((row, idx) => {
                                        if (row.is_allocated_to_projects) {
                                          const allocationKey = `${dept.department_name}__${trade.trade_name}__allocated_${row.category}_${idx}`;
                                          return (
                                            <AllocationAuditRows
                                              key={allocationKey}
                                              row={row}
                                              rowKey={allocationKey}
                                              isExpanded={!!expandedOslCategories[allocationKey]}
                                              onToggle={() => setExpandedOslCategories(prev => ({
                                                ...prev,
                                                [allocationKey]: !prev[allocationKey],
                                              }))}
                                              costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
                                            />
                                          );
                                        }
                                        return (
                                        <tr key={`leave_${idx}`} className="table-row-dense">
                                          <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                            <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
                                            <AllocatedToProjectsHint row={row} />
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                        </tr>
                                        );
                                      })}
                                      {projectCodeGroups.map(([category, catRows]) => {
                                        const accKey = `${dept.department_name}__trade_${trade.trade_name}__leave_${category}`;
                                        const isExpanded = !!expandedOslCategories[accKey];
                                        const t = sumSection(catRows, LEVY_COST_FIELDS);
                                        const sortedCatRows = [...catRows].sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));
                                        return (
                                          <React.Fragment key={category}>
                                            <tr
                                              className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                              onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                            >
                                              <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                  <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                  <span className="text-xs text-gray-400">· {getLeaveGroupCountText(sortedCatRows)}</span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                              <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                            </tr>
                                            {isExpanded && sortedCatRows.map((row, idx) => (
                                              <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                                <td className="pl-14 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                  <div className="font-medium">{getLeaveChildDisplayName(row)}</div>
                                                  {row.project_code && (
                                                    <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                  )}
                                                </td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                                <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                                {(() => {
                                  const t = sumSection(tradeOverheadRows, LEVY_COST_FIELDS);
                                  return (
                                    <tr className="bg-gray-100/60 border-t border-gray-200">
                                      <td className="pl-10 pr-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Leave Subtotal</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-xs font-semibold text-gray-500 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-xs font-bold text-gray-600 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                    </tr>
                                  );
                                })()}
                                <tr className="bg-rose-50/40 border-t border-rose-200">
                                  <td className="pl-10 pr-4 py-2 text-xs font-bold text-rose-800 uppercase tracking-wider">{trade.trade_name} Total</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-800 text-right tabular-nums">{fmtHours(trade.subtotals?.total_normal_hours)}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-800 text-right tabular-nums">{fmtHours(trade.subtotals?.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-800 text-right tabular-nums">{fmtHours(trade.subtotals?.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.workday_cost)}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.weekday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.sunday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-rose-900 text-right tabular-nums">{fmtCurrency(trade.subtotals?.total_cost)}</td>
                                </tr>
                              </>
                            );
                          }
                          return (() => {
                            const projRows = trade.rows.filter(r => r.row_type === 'project');
                            const leaveRows = trade.rows.filter(r => r.row_type !== 'project');
                            const renderLeave = (() => {
                              const { regularRows: regularLvRows, projectCodeGroups: pcGroups } = splitLeaveRowsForDisplay(leaveRows);

                              const consolidatedMap = {};
                              regularLvRows.forEach(r => {
                                const consolidationKey = getLeaveConsolidationKey(r);
                                if (!consolidatedMap[consolidationKey]) {
                                  consolidatedMap[consolidationKey] = { ...r };
                                } else {
                                  LEVY_COST_FIELDS.forEach(f => {
                                    consolidatedMap[consolidationKey][f] =
                                      (parseFloat(consolidatedMap[consolidationKey][f]) || 0) + (parseFloat(r[f]) || 0);
                                  });
                                  mergeAllocationAuditFields(consolidatedMap[consolidationKey], r);
                                }
                              });
                              const flatLvRows = Object.values(consolidatedMap)
                                .sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));

                              return [
                                ...flatLvRows.map((row, idx) => {
                                  if (row.is_allocated_to_projects) {
                                    const allocationKey = `${dept.department_name}__allocated_${row.category}_${idx}`;
                                    return (
                                      <AllocationAuditRows
                                        key={allocationKey}
                                        row={row}
                                        rowKey={allocationKey}
                                        isExpanded={!!expandedOslCategories[allocationKey]}
                                        onToggle={() => setExpandedOslCategories(prev => ({
                                          ...prev,
                                          [allocationKey]: !prev[allocationKey],
                                        }))}
                                        costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
                                      />
                                    );
                                  }
                                  return (
                                  <tr key={`leave_${idx}`} className="table-row-dense">
                                    <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
                                      <AllocatedToProjectsHint row={row} />
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                  </tr>
                                  );
                                }),
                                ...pcGroups.map(([category, catRows]) => {
                                  const accKey = `${dept.department_name}__trade_${trade.trade_name}__leave_${category}`;
                                  const isExp = !!expandedOslCategories[accKey];
                                  const t = sumSection(catRows, LEVY_COST_FIELDS);
                                  const sortedCatRows = [...catRows].sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));
                                  return (
                                    <React.Fragment key={category}>
                                      <tr
                                        className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                        onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                      >
                                        <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                                            <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                            <span className="text-xs text-gray-400">· {getLeaveGroupCountText(sortedCatRows)}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                      </tr>
                                      {isExp && sortedCatRows.map((row, idx) => (
                                        <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                          <td className="pl-14 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                            <div className="font-medium">{getLeaveChildDisplayName(row)}</div>
                                            {row.project_code && (
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                            )}
                                          </td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                }),
                              ];
                            })();
                            if (!projRows.length) return renderLeave;
                            const { consolidated, regular } = partitionConsolidated(projRows);
                            const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                              const accKey = `${dept.department_name}__trade_${trade.trade_name}__cg_${label}`;
                              const isExp = !!expandedOslCategories[accKey];
                              const t = sumSection(allRows, LEVY_COST_FIELDS);
                              const codeKeys = Object.keys(byCode).sort((a, b) => {
                                const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                return bT - aT;
                              });
                              return (
                                <React.Fragment key={`cg_${label}`}>
                                  <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                    <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                                        <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                  </tr>
                                  {isExp && codeKeys.map(code => {
                                    const codeRows = byCode[code];
                                    const ct = sumSection(codeRows, LEVY_COST_FIELDS);
                                    const codeName = getProjectDisplayName(codeRows[0]) || code;
                                    return (
                                      <tr key={code} className="bg-gray-50/50 table-row-dense">
                                        <td className="pl-14 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                          <div className="font-medium">{codeName}</div>
                                          <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                        </td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.workday_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.sunday_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            });
                            const projectGroups = {};
                            regular.forEach(r => {
                              const key = getRowIdentity(r);
                              if (!projectGroups[key]) projectGroups[key] = [];
                              projectGroups[key].push(r);
                            });
                            const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                              const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                              const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                              return bTotal - aTotal;
                            });
                            const regJsx = sortedKeys.map(key => {
                              const group = projectGroups[key];
                              if (group.length === 1) {
                                const r = group[0];
                                if (
                                  r.row_type === 'project'
                                  && r.parent_project_name
                                  && !MAIN_OFFICE_CODES.has(r.project_code)
                                ) {
                                  r.project_name = r.parent_project_name;
                                }
                                return (
                                  <tr key={key} className="table-row-dense">
                                    <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <div className="font-medium">{r.project_name || r.category}</div>
                                      {r.project_code && <div className="text-[11px] text-gray-400 font-mono leading-tight">{r.project_code}</div>}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.workday_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.sunday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(r.total_cost)}</td>
                                  </tr>
                                );
                              }
                              const accKey = `${dept.department_name}__trade_${trade.trade_name}__proj_${key}`;
                              const isExp = !!expandedOslCategories[accKey];
                              const t = sumSection(group, LEVY_COST_FIELDS);
                              const parentName = getProjectDisplayName(group[0]) || key;
                              return (
                                <React.Fragment key={key}>
                                  <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                    <td className="pl-10 pr-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                                        <div>
                                          <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                          <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                  </tr>
                                  {isExp && group.map((row, idx) => (
                                    <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                      <td className="pl-14 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                        <div className="font-medium">{row.project_name || row.project_code}</div>
                                      </td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            });
                            return [...cgJsx, ...regJsx, ...renderLeave];
                          })();
                        })()}
                      </tbody>
                    );
                  });
                };

                return (
                  <AccordionSection
                    key={dept.department_name}
                    expanded={!!expandedDepts[dept.department_name]}
                    onToggle={() => toggleDept(dept.department_name)}
                    theme="admin"
                    contentVariant="flush"
                    sticky
                    stickyTopClassName={APP_SHELL_STICKY_TOP_CLASS}
                    stickyExtra={
                      <table className="w-full table-fixed border-t border-gray-200">
                        {levyCostColgroup}
                        <CostAllocationTableHeader costLabel="Workday Cost" />
                      </table>
                    }
                    icon={
                      isUnassigned
                        ? <AlertTriangle className="w-4 h-4 text-amber-600" />
                        : (() => { const DI = getDepartmentIcon(dept.department_name); return <DI className={`w-4 h-4 ${getDepartmentColor(dept.department_name).text}`} />; })()
                    }
                    iconBg={isUnassigned ? 'bg-amber-100' : getDepartmentColor(dept.department_name).bg}
                    title={
                      <span className="inline-flex items-center gap-2">
                        <span>{dept.department_name}</span>
                        <CountBadge count={dept.worker_count} theme="neutral" size="md" />
                      </span>
                    }
                    trailing={
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-semibold text-gray-900 tabular-nums">{fmtCurrency(dept.subtotals?.total_cost)}</span>
                      </div>
                    }
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed divide-y divide-gray-200">
                        {levyCostColgroup}

                        {hasTradeBreakdown ? (
                          <>
                            {renderTradeContent()}
                            <tbody>
                              <tr className="bg-gray-800 text-white">
                                <td className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider">Department Total</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtHours(dept.subtotals?.total_normal_hours)}</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtHours(dept.subtotals?.total_weekday_ot_hours)}</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtHours(dept.subtotals?.total_rest_day_ot_hours)}</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtCurrency(dept.subtotals?.workday_cost)}</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtCurrency(dept.subtotals?.weekday_ot_cost)}</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtCurrency(dept.subtotals?.sunday_ot_cost)}</td>
                                <td className="px-4 py-2.5 text-xs font-bold text-right tabular-nums">{fmtCurrency(dept.subtotals?.total_cost)}</td>
                              </tr>
                            </tbody>
                          </>
                        ) : hasBothDeptSections ? (
                          <>
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-gray-50">
                                <td colSpan={8} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <Briefcase className="w-3 h-3" />
                                    Projects
                                  </div>
                                </td>
                              </tr>
                              {(() => {
                                const { consolidated, regular } = partitionConsolidated(deptProjectRows);
                                const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                                  const accKey = `${dept.department_name}__cg_${label}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const t = sumSection(allRows, LEVY_COST_FIELDS);
                                  const codeKeys = Object.keys(byCode).sort((a, b) => {
                                    const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                    const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                    return bT - aT;
                                  });
                                  return (
                                    <React.Fragment key={`cg_${label}`}>
                                      <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                      </tr>
                                      {isExpanded && codeKeys.map(code => {
                                        const codeRows = byCode[code];
                                        const ct = sumSection(codeRows, LEVY_COST_FIELDS);
                                        const codeName = getProjectDisplayName(codeRows[0]) || code;
                                        return (
                                          <tr key={code} className="bg-gray-50/50 table-row-dense">
                                            <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                              <div className="font-medium">{codeName}</div>
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                            </td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.workday_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.sunday_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                });
                                const projectGroups = {};
                                regular.forEach(r => {
                                  const key = getRowIdentity(r);
                                  if (!projectGroups[key]) projectGroups[key] = [];
                                  projectGroups[key].push(r);
                                });
                                const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                                  const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                  const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                  return bTotal - aTotal;
                                });
                                const regJsx = sortedKeys.map(key => {
                                  const group = projectGroups[key];
                                  if (group.length === 1) {
                                    const r = group[0];
                                    if (
                                      r.row_type === 'project'
                                      && r.parent_project_name
                                      && !MAIN_OFFICE_CODES.has(r.project_code)
                                    ) {
                                      r.project_name = r.parent_project_name;
                                    }
                                    return renderDeptDataRow(r, key);
                                  }
                                  const accKey = `${dept.department_name}__proj_${key}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const t = sumSection(group, LEVY_COST_FIELDS);
                                  const parentName = getProjectDisplayName(group[0]) || key;
                                  return (
                                    <React.Fragment key={key}>
                                      <tr
                                        className="table-row-dense cursor-pointer"
                                        onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                      >
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <div>
                                              <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                      </tr>
                                      {isExpanded && group.map((row, idx) => (
                                        <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                          <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                            <div className="font-medium">{row.project_name || row.project_code}</div>
                                          </td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                });
                                return [...cgJsx, ...regJsx];
                              })()}
                              {renderDeptSubtotal('Projects Subtotal', deptProjectRows)}
                            </tbody>
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-amber-50/60">
                                <td colSpan={8} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                    <CalendarDays className="w-3 h-3" />
                                    Leave
                                  </div>
                                </td>
                              </tr>
                              {(() => {
                                const { regularRows, projectCodeGroups } = splitLeaveRowsForDisplay(deptOverheadRows);

                                const consolidatedMap = {};
                                regularRows.forEach(r => {
                                  const consolidationKey = getLeaveConsolidationKey(r);
                                  if (!consolidatedMap[consolidationKey]) {
                                    consolidatedMap[consolidationKey] = { ...r };
                                  } else {
                                    LEVY_COST_FIELDS.forEach(f => {
                                      consolidatedMap[consolidationKey][f] =
                                        (parseFloat(consolidatedMap[consolidationKey][f]) || 0) + (parseFloat(r[f]) || 0);
                                    });
                                    mergeAllocationAuditFields(consolidatedMap[consolidationKey], r);
                                  }
                                });
                                const flatRows = Object.values(consolidatedMap)
                                  .sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));

                                return (
                                  <>
                                    {flatRows.map(renderDeptDataRow)}
                                    {projectCodeGroups.map(([category, catRows]) => {
                                      const accKey = `${dept.department_name}__leave_${category}`;
                                      const isExpanded = !!expandedOslCategories[accKey];
                                      const t = sumSection(catRows, LEVY_COST_FIELDS);
                                      const sortedCatRows = [...catRows].sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));
                                      return (
                                        <React.Fragment key={category}>
                                          <tr
                                            className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                            onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                          >
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                              <div className="flex items-center gap-2">
                                                <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                <span className="text-xs text-gray-400">· {getLeaveGroupCountText(sortedCatRows)}</span>
                                              </div>
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                            <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                          </tr>
                                          {isExpanded && sortedCatRows.map((row, idx) => (
                                            <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                              <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                <div className="font-medium">{getLeaveChildDisplayName(row)}</div>
                                                {row.project_code && (
                                                  <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                )}
                                              </td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                              {renderDeptSubtotal('Leave Subtotal', deptOverheadRows)}
                            </tbody>
                          </>
                        ) : (
                          <tbody className="divide-y divide-gray-100">
                            {dept.rows.map(renderDeptDataRow)}
                          </tbody>
                        )}
                      </table>
                    </div>
                  </AccordionSection>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Grand Total Bar */}
      {hasVisibleData && (
        <div className="bg-gray-800 text-white rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Grand Total</p>
              <p className="text-xl font-bold">{fmtCurrency(activeGt.total_cost)}</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            {levyGrouping === 'department' && deptReport ? (
              filteredDepts.slice(0, 4).map(d => (
                <div key={d.department_name} className="text-right">
                  <p className="text-gray-400 text-xs">{d.department_name}</p>
                  <p className="font-semibold tabular-nums">{fmtCurrency(d.subtotals?.total_cost)}</p>
                </div>
              ))
            ) : (
              <>
                <div className="text-right">
                  <p className="text-gray-400 text-xs">Projects</p>
                  <p className="font-semibold tabular-nums">{fmtCurrency(projectTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs">Leave</p>
                  <p className="font-semibold tabular-nums">{fmtCurrency(overheadTotal)}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LevyCostSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <div className="h-6 sm:h-7 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-24 mb-1" />
              <div className="h-3 bg-gray-100 rounded w-28" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between gap-3 animate-pulse">
      <div className="h-11 bg-gray-200 rounded-full flex-1 max-w-xl" />
      <div className="w-11 h-11 bg-gray-200 rounded-full" />
    </div>
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="p-4 space-y-3 animate-pulse">
        <div className="h-8 bg-gray-100 rounded w-full" />
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-10 bg-gray-50 rounded w-full" />
        ))}
      </div>
    </div>
  </div>
);


// ============================================================================
// SALARY COST TAB (Workers by Trade/Project/Employee + Staff by Dept/Employee)
// ============================================================================

const SalaryCostTab = ({ year, month }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [viewFilter, setViewFilter] = usePersistence('plc-salary-view-filter', 'all', ['all', 'workers', 'staff']);
  const [expandedTrades, setExpandedTrades] = useState({});
  const [expandedDepts, setExpandedDepts] = useState({});
  const [expandedOslCategories, setExpandedOslCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [workerGrouping, setWorkerGrouping] = usePersistence('plc-salary-worker-grouping', 'trade', ['trade', 'project', 'employee']);
  const [staffGrouping, setStaffGrouping] = usePersistence('plc-salary-staff-grouping', 'department', ['department', 'employee']);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [workerEmployeeReport, setWorkerEmployeeReport] = useState(null);
  const [workerEmployeeLoading, setWorkerEmployeeLoading] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [xlsxExporting, setXlsxExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await staffAPI.plcGetSalaryReportByTrade({ year, month });
      setReport(res.data);
      const expT = {};
      (res.data.trades || []).forEach(t => { expT[t.trade_name] = true; });
      setExpandedTrades(expT);
      const expD = {};
      (res.data.departments || []).forEach(d => { expD[d.department_name] = true; });
      setExpandedDepts(expD);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load salary report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchEmployeeReport = useCallback(async () => {
    setEmployeeLoading(true);
    try {
      const res = await staffAPI.plcGetSalaryReportByEmployee({ year, month, population: 'staff' });
      setEmployeeReport(res.data);
      const expE = {};
      (res.data.employees || []).forEach(e => { expE[e.staff_id] = false; });
      setExpandedEmployees(prev => ({ ...prev, ...expE }));
    } catch {
      toast.error('Failed to load staff employee breakdown');
    } finally {
      setEmployeeLoading(false);
    }
  }, [year, month]);

  const fetchWorkerEmployeeReport = useCallback(async () => {
    setWorkerEmployeeLoading(true);
    try {
      const res = await staffAPI.plcGetSalaryReportByEmployee({ year, month, population: 'workers' });
      setWorkerEmployeeReport(res.data);
      const expE = {};
      (res.data.employees || []).forEach(e => { expE[e.staff_id] = false; });
      setExpandedEmployees(prev => ({ ...prev, ...expE }));
    } catch {
      toast.error('Failed to load worker employee breakdown');
    } finally {
      setWorkerEmployeeLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (staffGrouping === 'employee' && !employeeReport) {
      fetchEmployeeReport();
    }
  }, [staffGrouping, employeeReport, fetchEmployeeReport]);

  useEffect(() => {
    if (workerGrouping === 'employee' && !workerEmployeeReport) {
      fetchWorkerEmployeeReport();
    }
  }, [workerGrouping, workerEmployeeReport, fetchWorkerEmployeeReport]);

  useEffect(() => {
    setEmployeeReport(null);
    setWorkerEmployeeReport(null);
  }, [year, month]);

  const trades = report?.trades || [];
  const departments = report?.departments || [];
  const gt = report?.grand_totals || {};
  const workerTotals = report?.worker_totals || {};
  const staffTotals = report?.staff_totals || {};
  const workerCount = report?.worker_count || 0;
  const staffCount = report?.staff_count || 0;

  const showWorkers = viewFilter === 'all' || viewFilter === 'workers';
  const showStaff = viewFilter === 'all' || viewFilter === 'staff';
  const showSectionHeaders = viewFilter === 'all' && trades.length > 0 && departments.length > 0;

  const filteredTrades = useMemo(() => {
    if (!showWorkers) return [];
    if (!searchTerm) return trades;
    const term = searchTerm.toLowerCase();
    return trades
      .filter(trade =>
        trade.trade_name?.toLowerCase().includes(term) ||
        (trade.employees || []).some(e =>
          e.name?.toLowerCase().includes(term) ||
          e.staff_id?.toLowerCase().includes(term)
        ) ||
        trade.rows.some(row => row.category?.toLowerCase().includes(term))
      )
      .map(trade => {
        const employeeMatch = (trade.employees || []).some(e =>
          e.name?.toLowerCase().includes(term) ||
          e.staff_id?.toLowerCase().includes(term)
        );
        if (employeeMatch) return trade;
        return {
          ...trade,
          rows: trade.rows.filter(row =>
            row.category?.toLowerCase().includes(term) ||
            trade.trade_name?.toLowerCase().includes(term)
          ),
        };
      });
  }, [trades, searchTerm, showWorkers]);

  const filteredDepts = useMemo(() => {
    if (!showStaff) return [];
    if (!searchTerm) return departments;
    const term = searchTerm.toLowerCase();
    return departments
      .filter(dept =>
        dept.department_name?.toLowerCase().includes(term) ||
        (dept.employees || []).some(e =>
          e.name?.toLowerCase().includes(term) ||
          e.staff_id?.toLowerCase().includes(term)
        ) ||
        dept.rows.some(row => row.category?.toLowerCase().includes(term))
      )
      .map(dept => {
        const employeeMatch = (dept.employees || []).some(e =>
          e.name?.toLowerCase().includes(term) ||
          e.staff_id?.toLowerCase().includes(term)
        );
        if (employeeMatch) return dept;
        return {
          ...dept,
          rows: dept.rows.filter(row =>
            row.category?.toLowerCase().includes(term) ||
            dept.department_name?.toLowerCase().includes(term)
          ),
        };
      });
  }, [departments, searchTerm, showStaff]);

  const toggleTrade = (name) => setExpandedTrades(prev => ({ ...prev, [name]: !prev[name] }));
  const toggleDept = (name) => setExpandedDepts(prev => ({ ...prev, [name]: !prev[name] }));
  const toggleEmployee = (sid) => setExpandedEmployees(prev => ({ ...prev, [sid]: !prev[sid] }));

  const employees = employeeReport?.employees || [];
  const workerEmployees = workerEmployeeReport?.employees || [];

  const filteredEmployees = useMemo(() => {
    if (!showStaff || staffGrouping !== 'employee') return [];
    if (!searchTerm) return employees;
    const term = searchTerm.toLowerCase();
    return employees.filter(emp =>
      emp.name?.toLowerCase().includes(term)
      || emp.staff_id?.toLowerCase().includes(term)
      || emp.department_name?.toLowerCase().includes(term)
      || emp.rows.some(r => r.category?.toLowerCase().includes(term))
    );
  }, [employees, searchTerm, showStaff, staffGrouping]);

  const filteredWorkerEmployees = useMemo(() => {
    if (!showWorkers || workerGrouping !== 'employee') return [];
    if (!searchTerm) return workerEmployees;
    const term = searchTerm.toLowerCase();
    return workerEmployees.filter(emp =>
      emp.name?.toLowerCase().includes(term)
      || emp.staff_id?.toLowerCase().includes(term)
      || emp.trade_name?.toLowerCase().includes(term)
      || emp.trade_code?.toLowerCase().includes(term)
      || emp.rows.some(r =>
        r.category?.toLowerCase().includes(term)
        || r.project_name?.toLowerCase().includes(term)
        || r.project_code?.toLowerCase().includes(term)
      )
    );
  }, [workerEmployees, searchTerm, showWorkers, workerGrouping]);

  const flatWorkerRows = useMemo(() => {
    if (!showWorkers || workerGrouping !== 'project') return [];
    const all = [];
    trades.forEach(trade => {
      (trade.rows || []).forEach(r => all.push(r));
    });
    if (!searchTerm) return all;
    const term = searchTerm.toLowerCase();
    return all.filter(r =>
      r.category?.toLowerCase().includes(term) ||
      r.project_name?.toLowerCase().includes(term) ||
      r.project_code?.toLowerCase().includes(term)
    );
  }, [trades, searchTerm, showWorkers, workerGrouping]);

  const flatProjectRows = useMemo(() => {
    const raw = flatWorkerRows.filter(r => r.row_type === 'project');
    const grouped = {};
    raw.forEach(r => {
      const key = getRowIdentity(r);
      if (!grouped[key]) {
        grouped[key] = { ...r };
      } else {
        SALARY_COST_FIELDS.forEach(f => {
          grouped[key][f] = (parseFloat(grouped[key][f]) || 0) + (parseFloat(r[f]) || 0);
        });
        ['direct_hours', 'allocated_leave_hours', 'allocated_leave_cost'].forEach(f => {
          grouped[key][f] = (parseFloat(grouped[key][f]) || 0) + (parseFloat(r[f]) || 0);
        });
      }
    });
    return Object.values(grouped);
  }, [flatWorkerRows]);

  const flatOverheadRows = useMemo(() => {
    const raw = flatWorkerRows.filter(r => r.row_type !== 'project');
    const grouped = {};
    raw.forEach(r => {
      const allocationState = r.is_allocated_to_projects ? 'allocated' : 'standalone';
      const key = `${r.category}__${r.project_code || ''}__${allocationState}`;
      if (!grouped[key]) {
        grouped[key] = { ...r };
      } else {
        SALARY_COST_FIELDS.forEach(f => {
          grouped[key][f] = (parseFloat(grouped[key][f]) || 0) + (parseFloat(r[f]) || 0);
        });
        mergeAllocationAuditFields(grouped[key], r);
      }
    });
    return Object.values(grouped);
  }, [flatWorkerRows]);
  const flatAllocatedOverheadRows = useMemo(
    () => flatOverheadRows.filter(r => r.is_allocated_to_projects),
    [flatOverheadRows],
  );
  const flatStandaloneOverheadRows = useMemo(
    () => flatOverheadRows.filter(r => !r.is_allocated_to_projects),
    [flatOverheadRows],
  );
  const flatHasBothSections = flatProjectRows.length > 0 && flatOverheadRows.length > 0;

  const staffSectionCount = showStaff
    ? (staffGrouping === 'employee' ? filteredEmployees.length : filteredDepts.length)
    : 0;
  const workerSectionCount = showWorkers
    ? (workerGrouping === 'employee'
      ? filteredWorkerEmployees.length
      : workerGrouping === 'project'
        ? (flatWorkerRows.length > 0 ? 1 : 0)
        : filteredTrades.length)
    : 0;
  const totalVisible = workerSectionCount + staffSectionCount;

  const allExpanded = useMemo(() => {
    if (totalVisible === 0) return false;
    let workerOk = true;
    if (showWorkers) {
      if (workerGrouping === 'employee') {
        workerOk = filteredWorkerEmployees.every(e => expandedEmployees[e.staff_id]);
      } else if (workerGrouping === 'trade') {
        workerOk = filteredTrades.every(t => expandedTrades[t.trade_name]);
      }
    }
    let staffOk = true;
    if (showStaff) {
      if (staffGrouping === 'employee') {
        staffOk = filteredEmployees.every(e => expandedEmployees[e.staff_id]);
      } else {
        staffOk = filteredDepts.every(d => expandedDepts[d.department_name]);
      }
    }
    return workerOk && staffOk;
  }, [
    filteredTrades, filteredDepts, filteredEmployees, filteredWorkerEmployees,
    expandedTrades, expandedDepts, expandedEmployees, totalVisible,
    showStaff, showWorkers, staffGrouping, workerGrouping,
  ]);

  const toggleAll = () => {
    const val = !allExpanded;
    if (showWorkers && workerGrouping === 'trade') {
      const next = {};
      filteredTrades.forEach(t => { next[t.trade_name] = val; });
      setExpandedTrades(prev => ({ ...prev, ...next }));
    }
    if (showWorkers && workerGrouping === 'employee') {
      const next = {};
      filteredWorkerEmployees.forEach(e => { next[e.staff_id] = val; });
      setExpandedEmployees(prev => ({ ...prev, ...next }));
    }
    if (showStaff && staffGrouping === 'employee') {
      const next = {};
      filteredEmployees.forEach(e => { next[e.staff_id] = val; });
      setExpandedEmployees(prev => ({ ...prev, ...next }));
    } else if (showStaff) {
      const next = {};
      filteredDepts.forEach(d => { next[d.department_name] = val; });
      setExpandedDepts(prev => ({ ...prev, ...next }));
    }
  };

  const handleExportXlsx = async () => {
    if (xlsxExporting) return;
    setXlsxExporting(true);
    try {
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      let res, filename;
      // Filename convention: every PLC export uses the ``PLC_`` prefix
      // so the four artefacts (Levy / Dorm / Salary / Combined) share
      // a stable family in HR's Downloads folder. Mirrors the prefix
      // used by ``PLC_Levy_Cost*.xlsx`` and ``PLC_Dorm_Cost*.xlsx``.
      if (viewFilter === 'all') {
        res = await staffAPI.plcExportSalaryReport({ year, month, grouping: 'all' });
        filename = `PLC_Salary_Cost_All_${monthNames[month - 1]}_${year}.xlsx`;
      } else if (viewFilter === 'staff' && staffGrouping === 'employee') {
        res = await staffAPI.plcExportSalaryByEmployee({ year, month, population: 'staff' });
        filename = `PLC_Salary_Cost_Staff_by_Employee_${monthNames[month - 1]}_${year}.xlsx`;
      } else if (viewFilter === 'staff') {
        res = await staffAPI.plcExportSalaryReport({ year, month, grouping: 'department' });
        filename = `PLC_Salary_Cost_Staff_by_Department_${monthNames[month - 1]}_${year}.xlsx`;
      } else if (workerGrouping === 'employee') {
        res = await staffAPI.plcExportSalaryByEmployee({ year, month, population: 'workers' });
        filename = `PLC_Salary_Cost_Workers_by_Employee_${monthNames[month - 1]}_${year}.xlsx`;
      } else {
        const grp = workerGrouping === 'project' ? 'project' : 'trade';
        res = await staffAPI.plcExportSalaryReport({ year, month, grouping: grp });
        filename = `PLC_Salary_Cost_Workers_by_${grp === 'project' ? 'Project' : 'Trade'}_${monthNames[month - 1]}_${year}.xlsx`;
      }
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XLSX export failed', err);
      toast.error('Export failed — please try again');
    } finally {
      setXlsxExporting(false);
    }
  };

  if (loading) return <SalaryCostSkeleton />;

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Report</h3>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (!report?.has_payroll) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <DollarSign className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payroll Run Found</h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto">
          No completed payroll run exists for {MONTH_NAMES[month - 1]} {year}.
          Run payroll processing first under Payroll &gt; Calculate Payroll.
        </p>
      </div>
    );
  }

  const totalCost = parseFloat(gt.total_cost) || 0;
  const workerCost = parseFloat(workerTotals.total_cost) || 0;
  const staffCost = parseFloat(staffTotals.salary_cost) || 0;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
        <SummaryCard
          icon={DollarSign}
          label="Total Salary Cost"
          value={fmtCurrency(totalCost)}
          subtitle={`${workerCount + staffCount} employees`}
          accent="red"
        />
        <SummaryCard
          icon={Wrench}
          label="Workers"
          value={fmtCurrency(workerCost)}
          subtitle={`${workerCount} workers · ${trades.length} trade${trades.length !== 1 ? 's' : ''}`}
          accent="blue"
        />
        <SummaryCard
          icon={Building2}
          label="Staff"
          value={fmtCurrency(staffCost)}
          subtitle={`${staffCount} staff · ${departments.length} dept${departments.length !== 1 ? 's' : ''}`}
          accent="amber"
        />
      </div>

      {/* Warnings */}
      {(report.unclassified_count > 0 || report.unassigned_count > 0 || report.unassigned_dept_count > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            {report.unclassified_count > 0 && (
              <p className="text-sm font-medium text-amber-800">
                {report.unclassified_count} employee{report.unclassified_count > 1 ? 's' : ''} have no employment classification set &mdash; defaulting to Workers
              </p>
            )}
            {report.unassigned_count > 0 && (
              <p className="text-sm text-amber-700">
                {report.unassigned_count} worker{report.unassigned_count > 1 ? 's' : ''} have no trade category assigned
              </p>
            )}
            {report.unassigned_dept_count > 0 && (
              <p className="text-sm text-amber-700">
                {report.unassigned_dept_count} staff member{report.unassigned_dept_count > 1 ? 's' : ''} have no department assigned
              </p>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={setSearchTerm}
          placeholder="Search name, staff ID, project..."
          theme="admin"
          className="max-w-xl flex-1"
        />
        <div className="flex items-center gap-2">
          <PillToggle
            value={viewFilter}
            onChange={setViewFilter}
            options={[
              { id: 'all', label: 'All' },
              { id: 'workers', label: 'Workers' },
              { id: 'staff', label: 'Staff' },
            ]}
            variant="text"
            theme="admin"
            ariaLabel="Filter by employee classification"
          />
          {totalVisible > 1 && (
            <button
              onClick={toggleAll}
              className="icon-button-admin"
              title={allExpanded ? 'Collapse All' : 'Expand All'}
              aria-label={allExpanded ? 'Collapse All' : 'Expand All'}
            >
              {allExpanded ? <ChevronsUp className="w-4 h-4" /> : <ChevronsDown className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={handleExportXlsx}
            className="icon-button-admin"
            title="Export Excel (.xlsx)"
            aria-label="Export Excel"
            disabled={xlsxExporting || totalVisible === 0}
          >
            {xlsxExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Empty search state */}
      {totalVisible === 0 && searchTerm && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No matching employees, staff IDs, or categories found</p>
        </div>
      )}

      {/* ============ WORKERS SECTION ============ */}
      {showWorkers && (
        <>
          {(showSectionHeaders || viewFilter === 'workers') && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between pt-2">
              <div className="flex items-center gap-2 min-w-0">
                {workerGrouping === 'employee'
                  ? <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  : workerGrouping === 'project'
                    ? <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <Wrench className="w-4 h-4 text-gray-400 flex-shrink-0" />
                }
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate">
                  Workers by {
                    workerGrouping === 'employee' ? 'Employee'
                      : workerGrouping === 'project' ? 'Project' : 'Trade'
                  }
                </span>
              </div>
              <PillToggle
                value={workerGrouping}
                onChange={setWorkerGrouping}
                options={[
                  { id: 'trade', label: 'By Trade' },
                  { id: 'project', label: 'By Project' },
                  { id: 'employee', label: 'By Employee' },
                ]}
                variant="compact"
                theme="admin"
                layout="sectionHeader"
                ariaLabel="Worker grouping mode"
              />
            </div>
          )}

          {/* Worker employee loading state */}
          {workerGrouping === 'employee' && workerEmployeeLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl border p-5 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-1.5" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                    <div className="h-5 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- BY PROJECT (flat table) ---- */}
          {workerGrouping === 'project' && (
            <>
              {flatWorkerRows.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {searchTerm ? 'No matching categories found' : 'No salary allocation data for this period'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm border overflow-x-auto md:overflow-visible">
                  <table className="w-full min-w-[700px] md:min-w-0 table-fixed divide-y divide-gray-200">
                    <colgroup>
                      <col style={{ width: '27%' }} />
                      <col style={{ width: '9%' }} />
                      <col style={{ width: '9%' }} />
                      <col style={{ width: '9%' }} />
                      <col style={{ width: '11.5%' }} />
                      <col style={{ width: '11.5%' }} />
                      <col style={{ width: '11.5%' }} />
                      <col style={{ width: '11.5%' }} />
                    </colgroup>
                    <CostAllocationTableHeader costLabel="Basic Cost" sticky />

                    {flatHasBothSections ? (
                      <>
                        <tbody className="divide-y divide-gray-100">
                          <tr className="bg-gray-50">
                            <td colSpan={8} className="px-4 py-1.5">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                <Briefcase className="w-3 h-3" />
                                Projects
                              </div>
                            </td>
                          </tr>
                          {(() => {
                            const { consolidated, regular } = partitionConsolidated(flatProjectRows);
                            const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                              const accKey = `sal_proj__cg_${label}`;
                              const isExpanded = !!expandedOslCategories[accKey];
                              const t = sumSection(allRows, SALARY_COST_FIELDS);
                              const codeKeys = Object.keys(byCode).sort((a, b) => {
                                const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                return bT - aT;
                              });
                              return (
                                <React.Fragment key={`cg_${label}`}>
                                  <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                        <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                  </tr>
                                  {isExpanded && codeKeys.map(code => {
                                    const codeRows = byCode[code];
                                    const ct = sumSection(codeRows, SALARY_COST_FIELDS);
                                    const codeName = getProjectDisplayName(codeRows[0]) || code;
                                    return (
                                      <tr key={code} className="bg-gray-50/50 table-row-dense">
                                        <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                          <div className="font-medium">{codeName}</div>
                                          <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                        </td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.basic_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.rest_day_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            });
                            const projectGroups = {};
                            regular.forEach(r => {
                              const key = getRowIdentity(r);
                              if (!projectGroups[key]) projectGroups[key] = [];
                              projectGroups[key].push(r);
                            });
                            const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                              const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                              const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                              return bTotal - aTotal;
                            });
                            const regJsx = sortedKeys.map(key => {
                              const group = projectGroups[key];
                              if (group.length === 1) {
                                const r = group[0];
                                if (
                                  r.row_type === 'project'
                                  && r.parent_project_name
                                  && !MAIN_OFFICE_CODES.has(r.project_code)
                                ) {
                                  r.project_name = r.parent_project_name;
                                }
                                return (
                                  <tr key={key} className="table-row-dense">
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <WorkerSalaryAllocationLabel row={r} />
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.basic_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(r.rest_day_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(r.total_cost)}</td>
                                  </tr>
                                );
                              }
                              const accKey = `sal_proj__proj_${key}`;
                              const isExpanded = !!expandedOslCategories[accKey];
                              const t = sumSection(group, SALARY_COST_FIELDS);
                              const parentName = getProjectDisplayName(group[0]) || key;
                              return (
                                <React.Fragment key={key}>
                                  <tr
                                    className="table-row-dense cursor-pointer"
                                    onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                  >
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                        <div>
                                          <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                          <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                  </tr>
                                  {isExpanded && group.map((row, idx) => (
                                    <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                      <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                        <div className="font-medium">{row.project_name || row.project_code}</div>
                                      </td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                                      <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            });
                            return [...cgJsx, ...regJsx];
                          })()}
                          {(() => {
                            const t = sumSection(flatProjectRows, SALARY_COST_FIELDS);
                            return (
                              <tr className="bg-gray-100/80 border-t border-gray-200">
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">Projects Subtotal</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                        {flatAllocatedOverheadRows.length > 0 && (
                          <tbody className="divide-y divide-gray-100">
                            <tr className="bg-blue-50/60">
                              <td colSpan={8} className="px-4 py-1.5">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                  <CalendarDays className="w-3 h-3" />
                                  Allocated to Projects Above
                                </div>
                              </td>
                            </tr>
                            {flatAllocatedOverheadRows.map((row, idx) => {
                              const allocationKey = `salary__allocated_${row.category}_${idx}`;
                              return (
                                <AllocationAuditRows
                                  key={allocationKey}
                                  row={row}
                                  rowKey={allocationKey}
                                  isExpanded={!!expandedOslCategories[allocationKey]}
                                  onToggle={() => setExpandedOslCategories(prev => ({
                                    ...prev,
                                    [allocationKey]: !prev[allocationKey],
                                  }))}
                                  costComponentFields={['basic_cost', 'weekday_ot_cost', 'rest_day_ot_cost']}
                                />
                              );
                            })}
                          </tbody>
                        )}
                        {flatStandaloneOverheadRows.length > 0 && (
                        <tbody className="divide-y divide-gray-100">
                          <tr className="bg-amber-50/60">
                            <td colSpan={8} className="px-4 py-1.5">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                <CalendarDays className="w-3 h-3" />
                                Leave
                              </div>
                            </td>
                          </tr>
                          {(() => {
                            const regularRows = flatStandaloneOverheadRows.filter(r => !r.project_code);
                            const oslGroupMap = {};
                            flatStandaloneOverheadRows.filter(r => r.project_code).forEach(r => {
                              if (!oslGroupMap[r.category]) oslGroupMap[r.category] = [];
                              oslGroupMap[r.category].push(r);
                            });
                            const oslGroups = Object.entries(oslGroupMap);

                            const consolidatedMap = {};
                            regularRows.forEach(r => {
                              const consolidationKey = getLeaveConsolidationKey(r);
                              if (!consolidatedMap[consolidationKey]) {
                                consolidatedMap[consolidationKey] = { ...r };
                              } else {
                                SALARY_COST_FIELDS.forEach(f => {
                                  consolidatedMap[consolidationKey][f] =
                                    (parseFloat(consolidatedMap[consolidationKey][f]) || 0) + (parseFloat(r[f]) || 0);
                                });
                                mergeAllocationAuditFields(consolidatedMap[consolidationKey], r);
                              }
                            });
                            const flatLeaveRows = Object.values(consolidatedMap)
                              .sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));

                            return (
                              <>
                                {flatLeaveRows.map((row, idx) => (
                                  <tr key={idx} className="table-row-dense">
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <WorkerSalaryAllocationLabel row={row} />
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                  </tr>
                                ))}
                                {oslGroups.map(([category, catRows]) => {
                                  const accKey = `sal_proj__leave_${category}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const t = sumSection(catRows, SALARY_COST_FIELDS);
                                  const sortedCatRows = [...catRows].sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));
                                  return (
                                    <React.Fragment key={category}>
                                      <tr
                                        className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                        onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                      >
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                            <span className="text-xs text-gray-400">· {sortedCatRows.length} {sortedCatRows.length !== 1 ? 'projects' : 'project'}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                      </tr>
                                      {isExpanded && sortedCatRows.map((row, idx) => (
                                        <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                          <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                            <div className="font-medium">{getProjectDisplayName(row)}</div>
                                            {row.project_code && (
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                            )}
                                          </td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                              </>
                            );
                          })()}
                          {(() => {
                            const t = sumSection(flatStandaloneOverheadRows, SALARY_COST_FIELDS);
                            return (
                              <tr className="bg-gray-100/80 border-t border-gray-200">
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">Leave Subtotal</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                        )}
                      </>
                    ) : (
                      <tbody className="divide-y divide-gray-100">
                        {[...flatProjectRows, ...flatOverheadRows].map((row, idx) => {
                          if (row.is_allocated_to_projects) {
                            const allocationKey = `salary__row_${row.category}_${idx}`;
                            return (
                              <AllocationAuditRows
                                key={allocationKey}
                                row={row}
                                rowKey={allocationKey}
                                isExpanded={!!expandedOslCategories[allocationKey]}
                                onToggle={() => setExpandedOslCategories(prev => ({
                                  ...prev,
                                  [allocationKey]: !prev[allocationKey],
                                }))}
                                costComponentFields={['basic_cost', 'weekday_ot_cost', 'rest_day_ot_cost']}
                              />
                            );
                          }
                          return (
                          <tr key={idx} className="table-row-dense">
                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                              <WorkerSalaryAllocationLabel row={row} />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                            <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    )}

                    <tfoot>
                      <tr className="bg-red-50 border-t-2 border-red-200">
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800">GRAND TOTAL</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(workerTotals.total_normal_hours)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(workerTotals.total_weekday_ot_hours)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(workerTotals.total_rest_day_ot_hours)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(workerTotals.basic_cost)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(workerTotals.weekday_ot_cost)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(workerTotals.rest_day_ot_cost)}</td>
                        <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(workerTotals.total_cost)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ---- BY TRADE (existing accordion view) ---- */}
          {workerGrouping === 'trade' && filteredTrades.length > 0 && (
          <div className="space-y-3">
            {filteredTrades.map((trade) => {
              const isUnassigned = trade.trade_name === 'Unassigned';
              const projectRows = trade.rows.filter(r => r.row_type === 'project');
              const overheadRows = trade.rows.filter(r => r.row_type !== 'project');
              const allocatedRows = overheadRows.filter(r => r.is_allocated_to_projects);
              const standaloneOverheadRows = overheadRows.filter(r => !r.is_allocated_to_projects);
              const hasBothSections = projectRows.length > 0 && overheadRows.length > 0;

              const renderTradeDataRow = (row, idx) => {
                if (row.is_allocated_to_projects) {
                  const allocationKey = `${trade.trade_name}__allocated_row_${row.category}_${idx}`;
                  return (
                    <AllocationAuditRows
                      key={allocationKey}
                      row={row}
                      rowKey={allocationKey}
                      isExpanded={!!expandedOslCategories[allocationKey]}
                      onToggle={() => setExpandedOslCategories(prev => ({
                        ...prev,
                        [allocationKey]: !prev[allocationKey],
                      }))}
                      costComponentFields={['basic_cost', 'weekday_ot_cost', 'rest_day_ot_cost']}
                    />
                  );
                }
                return (
                <tr key={idx} className="table-row-dense">
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                    <WorkerSalaryAllocationLabel row={row} />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                </tr>
                );
              };

              const renderTradeSubtotal = (label, sectionRows) => {
                const t = sumSection(sectionRows, SALARY_COST_FIELDS);
                return (
                  <tr className="bg-gray-100/80 border-t border-gray-200">
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                    <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                  </tr>
                );
              };

              return (
                <AccordionSection
                  key={trade.trade_name}
                  expanded={!!expandedTrades[trade.trade_name]}
                  onToggle={() => toggleTrade(trade.trade_name)}
                  theme="admin"
                  contentVariant="flush"
                  sticky
                  stickyTopClassName={APP_SHELL_STICKY_TOP_CLASS}
                  stickyExtra={
                    <table className="w-full table-fixed border-t border-gray-200">
                      <colgroup>
                        <col style={{ width: '27%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '11.5%' }} />
                        <col style={{ width: '11.5%' }} />
                        <col style={{ width: '11.5%' }} />
                        <col style={{ width: '11.5%' }} />
                      </colgroup>
                      <CostAllocationTableHeader costLabel="Basic Cost" />
                    </table>
                  }
                  icon={
                    isUnassigned
                      ? <AlertTriangle className="w-4 h-4 text-amber-600" />
                      : <Wrench className="w-4 h-4 text-red-600" />
                  }
                  iconBg={isUnassigned ? 'bg-amber-100' : 'bg-red-100'}
                  title={
                    <div className="flex items-center gap-2">
                      <span>{trade.trade_name}</span>
                      {trade.trade_code && (
                        <TradeBadge code={trade.trade_code} name={trade.trade_name} size="xs" />
                      )}
                    </div>
                  }
                  trailing={
                    <div className="flex items-center gap-3 text-sm">
                      <span className="badge badge-secondary">{trade.worker_count} worker{trade.worker_count !== 1 ? 's' : ''}</span>
                      <span className="font-semibold text-gray-900 tabular-nums">{fmtCurrency(trade.subtotals?.total_cost)}</span>
                    </div>
                  }
                >
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed divide-y divide-gray-200">
                      <colgroup>
                        <col style={{ width: '27%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '11.5%' }} />
                        <col style={{ width: '11.5%' }} />
                        <col style={{ width: '11.5%' }} />
                        <col style={{ width: '11.5%' }} />
                      </colgroup>

                      {hasBothSections ? (
                        <>
                          <tbody className="divide-y divide-gray-100">
                            <tr className="bg-gray-50">
                              <td colSpan={8} className="px-4 py-1.5">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  <Briefcase className="w-3 h-3" />
                                  Projects
                                </div>
                              </td>
                            </tr>
                            {(() => {
                              const { consolidated, regular } = partitionConsolidated(projectRows);
                              const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                                const accKey = `${trade.trade_name}__cg_${label}`;
                                const isExpanded = !!expandedOslCategories[accKey];
                                const t = sumSection(allRows, SALARY_COST_FIELDS);
                                const codeKeys = Object.keys(byCode).sort((a, b) => {
                                  const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                  const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                  return bT - aT;
                                });
                                return (
                                  <React.Fragment key={`cg_${label}`}>
                                    <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                          <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                          <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                      <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                    </tr>
                                    {isExpanded && codeKeys.map(code => {
                                      const codeRows = byCode[code];
                                      const ct = sumSection(codeRows, SALARY_COST_FIELDS);
                                      const codeName = getProjectDisplayName(codeRows[0]) || code;
                                      return (
                                        <tr key={code} className="bg-gray-50/50 table-row-dense">
                                          <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                            <div className="font-medium">{codeName}</div>
                                            <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                          </td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.basic_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.rest_day_ot_cost)}</td>
                                          <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              });
                              const projectGroups = {};
                              regular.forEach(r => {
                                const key = getRowIdentity(r);
                                if (!projectGroups[key]) projectGroups[key] = [];
                                projectGroups[key].push(r);
                              });
                              const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                                const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                return bTotal - aTotal;
                              });
                              const regJsx = sortedKeys.map(key => {
                                const group = projectGroups[key];
                                if (group.length === 1) {
                                  const r = group[0];
                                  if (
                                    r.row_type === 'project'
                                    && r.parent_project_name
                                    && !MAIN_OFFICE_CODES.has(r.project_code)
                                  ) {
                                    r.project_name = r.parent_project_name;
                                  }
                                  return renderTradeDataRow(r, key);
                                }
                                const accKey = `${trade.trade_name}__proj_${key}`;
                                const isExpanded = !!expandedOslCategories[accKey];
                                const t = sumSection(group, SALARY_COST_FIELDS);
                                const parentName = getProjectDisplayName(group[0]) || key;
                                return (
                                  <React.Fragment key={key}>
                                    <tr
                                      className="table-row-dense cursor-pointer"
                                      onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                    >
                                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                          <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                          <div>
                                            <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                            <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                      <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                    </tr>
                                    {isExpanded && group.map((row, idx) => (
                                      <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                        <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                          <div className="font-medium">{row.project_name || row.project_code}</div>
                                        </td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                                        <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              });
                              return [...cgJsx, ...regJsx];
                            })()}
                            {renderTradeSubtotal('Projects Subtotal', projectRows)}
                          </tbody>
                          {allocatedRows.length > 0 && (
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-blue-50/60">
                                <td colSpan={8} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    <CalendarDays className="w-3 h-3" />
                                    Allocated to Projects Above
                                  </div>
                                </td>
                              </tr>
                              {allocatedRows.map(renderTradeDataRow)}
                            </tbody>
                          )}
                          {standaloneOverheadRows.length > 0 && (
                          <tbody className="divide-y divide-gray-100">
                            <tr className="bg-amber-50/60">
                              <td colSpan={8} className="px-4 py-1.5">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                  <CalendarDays className="w-3 h-3" />
                                  Leave
                                </div>
                              </td>
                            </tr>
                            {(() => {
                              const regularRows = standaloneOverheadRows.filter(r => !r.project_code);
                              const oslGroupMap = {};
                              standaloneOverheadRows.filter(r => r.project_code).forEach(r => {
                                if (!oslGroupMap[r.category]) oslGroupMap[r.category] = [];
                                oslGroupMap[r.category].push(r);
                              });
                              const oslGroups = Object.entries(oslGroupMap);

                              return (
                                <>
                                  {regularRows.map(renderTradeDataRow)}
                                  {oslGroups.map(([category, rows]) => {
                                    const oslKey = `${trade.trade_name}__${category}`;
                                    const isExpanded = !!expandedOslCategories[oslKey];
                                    const t = sumSection(rows, SALARY_COST_FIELDS);
                                    return (
                                      <React.Fragment key={category}>
                                        <tr
                                          className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                          onClick={() => setExpandedOslCategories(prev => ({...prev, [oslKey]: !prev[oslKey]}))}
                                        >
                                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                              <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                              <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                              <span className="text-xs text-gray-400">· {rows.length} {rows.length !== 1 ? 'projects' : 'project'}</span>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                          <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                        </tr>
                                        {isExpanded && rows.map((row, idx) => (
                                          <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                            <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                              <div className="font-medium">{row.project_name || row.project_code}</div>
                                              {row.project_code && (
                                                <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                              )}
                                            </td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    );
                                  })}
                                </>
                              );
                            })()}
                            {renderTradeSubtotal('Leave Subtotal', standaloneOverheadRows)}
                          </tbody>
                          )}
                        </>
                      ) : (
                        <tbody className="divide-y divide-gray-100">
                          {trade.rows.map(renderTradeDataRow)}
                        </tbody>
                      )}

                      <tfoot>
                        <tr className="bg-red-50 border-t-2 border-red-200">
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800">SUBTOTAL</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(trade.subtotals?.total_normal_hours)}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(trade.subtotals?.total_weekday_ot_hours)}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(trade.subtotals?.total_rest_day_ot_hours)}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.basic_cost)}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.weekday_ot_cost)}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.rest_day_ot_cost)}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(trade.subtotals?.total_cost)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </AccordionSection>
              );
            })}
          </div>
          )}

          {/* ---- BY EMPLOYEE (workers) ---- */}
          {workerGrouping === 'employee' && !workerEmployeeLoading && filteredWorkerEmployees.length > 0 && (
            <div className="space-y-3">
              {filteredWorkerEmployees.map((emp) => {
                const isUnassigned = emp.trade_name === 'Unassigned';
                const projectRows = emp.rows.filter(r => r.row_type === 'project');
                const overheadRows = emp.rows.filter(r => r.row_type !== 'project');
                const allocatedRows = overheadRows.filter(r => r.is_allocated_to_projects);
                const standaloneOverheadRows = overheadRows.filter(r => !r.is_allocated_to_projects);
                const hasBothSections = projectRows.length > 0 && overheadRows.length > 0;

                const renderWorkerEmpRow = (row, idx) => {
                  if (row.is_allocated_to_projects) {
                    const allocationKey = `${emp.staff_id}__allocated_row_${row.category}_${idx}`;
                    return (
                      <AllocationAuditRows
                        key={allocationKey}
                        row={row}
                        rowKey={allocationKey}
                        isExpanded={!!expandedOslCategories[allocationKey]}
                        onToggle={() => setExpandedOslCategories(prev => ({
                          ...prev,
                          [allocationKey]: !prev[allocationKey],
                        }))}
                        costComponentFields={['basic_cost', 'weekday_ot_cost', 'rest_day_ot_cost']}
                      />
                    );
                  }
                  return (
                  <tr key={idx} className="table-row-dense">
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                      <WorkerSalaryAllocationLabel row={row} />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                  </tr>
                  );
                };

                const renderWorkerEmpSubtotal = (label, sectionRows) => {
                  const t = sumSection(sectionRows, SALARY_COST_FIELDS);
                  return (
                    <tr className="bg-gray-100/80 border-t border-gray-200">
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                      <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                    </tr>
                  );
                };

                return (
                  <AccordionSection
                    key={emp.staff_id}
                    expanded={!!expandedEmployees[emp.staff_id]}
                    onToggle={() => toggleEmployee(emp.staff_id)}
                    theme="admin"
                    contentVariant="flush"
                    sticky
                    stickyTopClassName={APP_SHELL_STICKY_TOP_CLASS}
                    stickyExtra={
                      <table className="w-full table-fixed border-t border-gray-200">
                        <colgroup>
                          <col style={{ width: '27%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '11.5%' }} />
                          <col style={{ width: '11.5%' }} />
                          <col style={{ width: '11.5%' }} />
                          <col style={{ width: '11.5%' }} />
                        </colgroup>
                        <CostAllocationTableHeader costLabel="Basic Cost" />
                      </table>
                    }
                    icon={
                      <span className="text-xs font-medium text-red-800">
                        {emp.name?.split(' ').filter(Boolean).map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
                      </span>
                    }
                    iconBg={isUnassigned ? 'bg-amber-100' : 'bg-red-100'}
                    title={
                      <div className="flex items-start gap-2">
                        <div className="text-left">
                          <span>{emp.name}</span>
                          <div className="text-[11px] text-gray-400 font-mono leading-tight">{emp.staff_id}</div>
                        </div>
                        {isUnassigned ? (
                          <InfoBadge color="amber" label="Unassigned" size="xs" />
                        ) : emp.trade_code ? (
                          <TradeBadge code={emp.trade_code} name={emp.trade_name} size="xs" />
                        ) : (
                          <InfoBadge color="rose" label={emp.trade_name || 'Worker'} size="xs" />
                        )}
                      </div>
                    }
                    trailing={
                      <span className="font-semibold text-gray-900 text-sm tabular-nums">{fmtCurrency(emp.subtotals?.total_cost)}</span>
                    }
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed divide-y divide-gray-200">
                        <colgroup>
                          <col style={{ width: '27%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '11.5%' }} />
                          <col style={{ width: '11.5%' }} />
                          <col style={{ width: '11.5%' }} />
                          <col style={{ width: '11.5%' }} />
                        </colgroup>

                        {hasBothSections ? (
                          <>
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-gray-50">
                                <td colSpan={8} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <Briefcase className="w-3 h-3" />
                                    Projects
                                  </div>
                                </td>
                              </tr>
                              {(() => {
                                const { consolidated, regular } = partitionConsolidated(projectRows);
                                const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                                  const accKey = `${emp.staff_id}__cg_${label}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const t = sumSection(allRows, SALARY_COST_FIELDS);
                                  const codeKeys = Object.keys(byCode).sort((a, b) => {
                                    const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                    const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                                    return bT - aT;
                                  });
                                  return (
                                    <React.Fragment key={`cg_${label}`}>
                                      <tr
                                        className="table-row-dense cursor-pointer"
                                        onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                      >
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <div className="font-medium flex items-center gap-1.5">
                                              {label} <CountBadge count={codeKeys.length} theme="admin" size="sm" />
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                      </tr>
                                      {isExpanded && codeKeys.map(code => {
                                        const codeRows = byCode[code];
                                        const ct = sumSection(codeRows, SALARY_COST_FIELDS);
                                        const codeName = getProjectDisplayName(codeRows[0]) || code;
                                        return (
                                          <tr key={code} className="bg-gray-50/50 table-row-dense">
                                            <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                              <div className="font-medium">{codeName}</div>
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                            </td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.basic_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.rest_day_ot_cost)}</td>
                                            <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                });
                                return (
                                  <>
                                    {cgJsx}
                                    {regular.map(renderWorkerEmpRow)}
                                  </>
                                );
                              })()}
                              {renderWorkerEmpSubtotal('Projects Subtotal', projectRows)}
                            </tbody>
                            {allocatedRows.length > 0 && (
                              <tbody className="divide-y divide-gray-100">
                                <tr className="bg-blue-50/60">
                                  <td colSpan={8} className="px-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                      <CalendarDays className="w-3 h-3" />
                                      Allocated to Projects Above
                                    </div>
                                  </td>
                                </tr>
                                {allocatedRows.map(renderWorkerEmpRow)}
                              </tbody>
                            )}
                            {standaloneOverheadRows.length > 0 && (
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-amber-50/60">
                                <td colSpan={8} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                    <CalendarDays className="w-3 h-3" />
                                    Leave
                                  </div>
                                </td>
                              </tr>
                              {(() => {
                                const regularRows = standaloneOverheadRows.filter(r => !r.project_code);
                                const oslGroupMap = {};
                                standaloneOverheadRows.filter(r => r.project_code).forEach(r => {
                                  if (!oslGroupMap[r.category]) oslGroupMap[r.category] = [];
                                  oslGroupMap[r.category].push(r);
                                });
                                const oslGroups = Object.entries(oslGroupMap);
                                return (
                                  <>
                                    {regularRows.map(renderWorkerEmpRow)}
                                    {oslGroups.map(([category, rows]) => {
                                      const oslKey = `${emp.staff_id}__${category}`;
                                      const isExpanded = !!expandedOslCategories[oslKey];
                                      const t = sumSection(rows, SALARY_COST_FIELDS);
                                      return (
                                        <React.Fragment key={category}>
                                          <tr
                                            className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                            onClick={() => setExpandedOslCategories(prev => ({ ...prev, [oslKey]: !prev[oslKey] }))}
                                          >
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                              <div className="flex items-center gap-2">
                                                <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                <span className="text-xs text-gray-400">· {rows.length} {rows.length !== 1 ? 'projects' : 'project'}</span>
                                              </div>
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.basic_cost)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.rest_day_ot_cost)}</td>
                                            <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                          </tr>
                                          {isExpanded && rows.map((row, idx) => (
                                            <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                              <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                <div className="font-medium">{getProjectDisplayName(row) || row.project_code}</div>
                                                {row.project_code && (
                                                  <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                )}
                                              </td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.basic_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.rest_day_ot_cost)}</td>
                                              <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                      );
                                    })}
                                  </>
                                );
                              })()}
                              {renderWorkerEmpSubtotal('Leave Subtotal', standaloneOverheadRows)}
                            </tbody>
                            )}
                          </>
                        ) : (
                          <tbody className="divide-y divide-gray-100">
                            {emp.rows.map(renderWorkerEmpRow)}
                          </tbody>
                        )}

                        <tfoot>
                          <tr className="bg-red-50 border-t-2 border-red-200">
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800">TOTAL</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(emp.subtotals?.total_normal_hours)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(emp.subtotals?.total_weekday_ot_hours)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(emp.subtotals?.total_rest_day_ot_hours)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(emp.subtotals?.basic_cost)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(emp.subtotals?.weekday_ot_cost)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(emp.subtotals?.rest_day_ot_cost)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(emp.subtotals?.total_cost)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </AccordionSection>
                );
              })}
            </div>
          )}

          {workerGrouping === 'employee' && !workerEmployeeLoading && filteredWorkerEmployees.length === 0 && workerEmployeeReport && !searchTerm && (
            <div className="text-center py-8">
              <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No workers found for this period</p>
            </div>
          )}
        </>
      )}

      {/* ============ STAFF SECTION ============ */}
      {showStaff && (
        <>
          {/* Section header with grouping toggle */}
          {(showSectionHeaders || viewFilter === 'staff') && (
            <div className="flex items-center gap-2 pt-2 min-w-0">
              {staffGrouping === 'employee'
                ? <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                : <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              }
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate">
                Staff by {staffGrouping === 'employee' ? 'Employee' : 'Department'}
              </span>
              <div className="flex-1 border-t border-gray-200 hidden sm:block" />
              <PillToggle
                value={staffGrouping}
                onChange={setStaffGrouping}
                options={[
                  { id: 'department', label: 'By Dept' },
                  { id: 'employee', label: 'By Employee' },
                ]}
                variant="compact"
                theme="admin"
                className="flex-shrink-0"
                ariaLabel="Staff grouping mode"
              />
            </div>
          )}

          {/* Employee loading state */}
          {staffGrouping === 'employee' && employeeLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl border p-5 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-1.5" />
                      <div className="h-3 bg-gray-200 rounded w-20" />
                    </div>
                    <div className="h-5 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- BY DEPARTMENT (existing view) ---- */}
          {staffGrouping === 'department' && filteredDepts.length > 0 && (
            <div className="space-y-3">
              {filteredDepts.map((dept) => {
                const isUnassigned = dept.department_name === 'Unassigned';
                // Ghost rows (is_allocated_to_projects) can have any
                // row_type — leave ghosts are 'overhead', office-pool
                // ghosts are 'project'.  Pull them out into their own
                // section so the Projects / Leave subtotals stay clean.
                const deptProjectRows = dept.rows.filter(
                  r => r.row_type === 'project' && !r.is_allocated_to_projects
                );
                const deptOverheadRows = dept.rows.filter(
                  r => r.row_type !== 'project' && !r.is_allocated_to_projects
                );
                const deptAllocatedRows = dept.rows.filter(r => r.is_allocated_to_projects);
                const hasDeptAllocated = deptAllocatedRows.length > 0;
                const hasBothDeptSections = deptProjectRows.length > 0
                  && (deptOverheadRows.length > 0 || hasDeptAllocated);

                const renderDeptRow = (row, idx) => (
                  <tr key={idx} className="table-row-dense">
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                      {row.row_type === 'project' ? (
                        <>
                          <div className="font-medium">{row.project_name || row.category}</div>
                          {row.project_code && (
                            <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                          )}
                        </>
                      ) : (
                        <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_hours)}</td>
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.salary_cost)}</td>
                  </tr>
                );

                const renderDeptSubtotal = (label, sectionRows) => {
                  const h = sectionRows.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                  const c = sectionRows.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                  return (
                    <tr className="bg-gray-100/80 border-t border-gray-200">
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(h)}</td>
                      <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(c)}</td>
                    </tr>
                  );
                };

                return (
                  <AccordionSection
                    key={dept.department_name}
                    expanded={!!expandedDepts[dept.department_name]}
                    onToggle={() => toggleDept(dept.department_name)}
                    theme="admin"
                    contentVariant="flush"
                    sticky
                    stickyTopClassName={APP_SHELL_STICKY_TOP_CLASS}
                    stickyExtra={
                      <table className="w-full table-fixed border-t border-gray-200">
                        <colgroup>
                          <col style={{ width: '45%' }} />
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '35%' }} />
                        </colgroup>
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell-compact">Project / Category</th>
                            <th className="table-header-cell-compact text-right">Hours</th>
                            <th className="table-header-cell-compact text-right">Salary Cost</th>
                          </tr>
                        </thead>
                      </table>
                    }
                    icon={
                      isUnassigned
                        ? <AlertTriangle className="w-4 h-4 text-amber-600" />
                        : (() => { const DI = getDepartmentIcon(dept.department_name); return <DI className={`w-4 h-4 ${getDepartmentColor(dept.department_name).text}`} />; })()
                    }
                    iconBg={isUnassigned ? 'bg-amber-100' : getDepartmentColor(dept.department_name).bg}
                    title={
                      <span className="inline-flex items-center gap-2">
                        <span>{dept.department_name}</span>
                        <CountBadge count={dept.staff_count} theme="neutral" size="md" />
                      </span>
                    }
                    trailing={
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-semibold text-gray-900 tabular-nums">{fmtCurrency(dept.subtotals?.salary_cost)}</span>
                      </div>
                    }
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed divide-y divide-gray-200">
                        <colgroup>
                          <col style={{ width: '45%' }} />
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '35%' }} />
                        </colgroup>

                        {hasBothDeptSections ? (
                          <>
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-gray-50">
                                <td colSpan={3} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <Briefcase className="w-3 h-3" />
                                    Projects
                                  </div>
                                </td>
                              </tr>
                              {(() => {
                                const { consolidated, regular } = partitionConsolidated(deptProjectRows);
                                const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                                  const accKey = `${dept.department_name}__cg_${label}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const h = allRows.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                                  const c = allRows.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  const codeKeys = Object.keys(byCode).sort((a, b) => {
                                    const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                    const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                    return bT - aT;
                                  });
                                  return (
                                    <React.Fragment key={`cg_${label}`}>
                                      <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(h)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(c)}</td>
                                      </tr>
                                      {isExpanded && codeKeys.map(code => {
                                        const codeRows = byCode[code];
                                        const ch = codeRows.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                                        const cc = codeRows.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                        const codeName = getProjectDisplayName(codeRows[0]) || code;
                                        return (
                                          <tr key={code} className="bg-gray-50/50 table-row-dense">
                                            <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                              <div className="font-medium">{codeName}</div>
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                            </td>
                                            <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ch)}</td>
                                            <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(cc)}</td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                });
                                const projectGroups = {};
                                regular.forEach(r => {
                                  const key = getRowIdentity(r);
                                  if (!projectGroups[key]) projectGroups[key] = [];
                                  projectGroups[key].push(r);
                                });
                                const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                                  const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  return bTotal - aTotal;
                                });
                                const regJsx = sortedKeys.map(key => {
                                  const group = projectGroups[key];
                                  if (group.length === 1) {
                                    const r = group[0];
                                    if (
                                      r.row_type === 'project'
                                      && r.parent_project_name
                                      && !MAIN_OFFICE_CODES.has(r.project_code)
                                    ) {
                                      r.project_name = r.parent_project_name;
                                    }
                                    return renderDeptRow(r, key);
                                  }
                                  const accKey = `${dept.department_name}__proj_${key}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const h = group.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                                  const c = group.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  const parentName = getProjectDisplayName(group[0]) || key;
                                  return (
                                    <React.Fragment key={key}>
                                      <tr
                                        className="table-row-dense cursor-pointer"
                                        onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                      >
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <div>
                                              <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(h)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(c)}</td>
                                      </tr>
                                      {isExpanded && group.map((row, idx) => (
                                        <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                          <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                            <div className="font-medium">{row.project_name || row.project_code}</div>
                                          </td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_hours)}</td>
                                          <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.salary_cost)}</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                });
                                return [...cgJsx, ...regJsx];
                              })()}
                              {renderDeptSubtotal('Projects Subtotal', deptProjectRows)}
                            </tbody>
                            {hasDeptAllocated && (
                              <tbody className="divide-y divide-gray-100">
                                <tr className="bg-blue-50/60">
                                  <td colSpan={3} className="px-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                      <CalendarDays className="w-3 h-3" />
                                      Allocated to Projects Above
                                    </div>
                                  </td>
                                </tr>
                                {(() => {
                                  // Two ghost shapes coexist here (see
                                  // renderAllocatedRowLabel above):
                                  //   1. Office-pool work redistribution
                                  //      ghosts (row_type='project',
                                  //      project_code in MAIN_OFFICE_CODES)
                                  //      — distinct work-site names like
                                  //      "18 Office" / "18 Factory" carry
                                  //      meaning, so we keep them as
                                  //      individual rows.
                                  //   2. Leave ghosts (e.g. OSL absorbed
                                  //      into 3 customer projects → 3
                                  //      backend rows, all with category
                                  //      'Outpatient Sick Leave').
                                  //      Mirrors the Leave-section
                                  //      consolidation below: group by
                                  //      category into one expandable
                                  //      "OSL · 3 projects" row so the
                                  //      reader doesn't see three
                                  //      identical-looking OSL chips with
                                  //      no project context.
                                  const officeGhostRows = deptAllocatedRows.filter(
                                    r => r.project_code
                                      && MAIN_OFFICE_CODES.has(r.project_code)
                                      && r.row_type === 'project'
                                  );
                                  const leaveGhostRows = deptAllocatedRows.filter(
                                    r => !(
                                      r.project_code
                                      && MAIN_OFFICE_CODES.has(r.project_code)
                                      && r.row_type === 'project'
                                    )
                                  );
                                  const leaveGhostMap = {};
                                  leaveGhostRows.forEach(r => {
                                    const cat = r.category || 'Leave';
                                    if (!leaveGhostMap[cat]) leaveGhostMap[cat] = [];
                                    leaveGhostMap[cat].push(r);
                                  });
                                  const leaveGhostGroups = Object.entries(leaveGhostMap);

                                  return (
                                    <>
                                      {officeGhostRows.map((row, idx) => {
                                        const hoursTaken = parseFloat(row.leave_hours_taken) || 0;
                                        return (
                                          <tr key={`dept_alloc_office_${idx}`} className="table-row-dense bg-blue-50/10">
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                              {renderAllocatedRowLabel(row)}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(hoursTaken)}</td>
                                            <td className="px-4 py-2 text-xs text-gray-500 text-right italic">allocated above</td>
                                          </tr>
                                        );
                                      })}
                                      {leaveGhostGroups.map(([category, rows]) => {
                                        const allocKey = `${dept.department_name}__alloc_${category}`;
                                        const isExpanded = !!expandedOslCategories[allocKey];
                                        const destinations = rows.reduce(
                                          (merged, allocationRow) => mergeAllocationDestinations(
                                            merged,
                                            allocationRow.allocation_destinations,
                                          ),
                                          [],
                                        );
                                        const detailRows = destinations.length > 0 ? destinations : rows;
                                        const totalH = rows.reduce(
                                          (s, r) => s + (parseFloat(r.leave_hours_taken) || 0),
                                          0,
                                        );
                                        return (
                                          <React.Fragment key={`dept_alloc_grp_${category}`}>
                                            <tr
                                              className="bg-blue-50/10 hover:bg-blue-50/40 cursor-pointer transition-colors"
                                              onClick={() => setExpandedOslCategories(prev => ({...prev, [allocKey]: !prev[allocKey]}))}
                                            >
                                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                  <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                  <span className="text-xs text-gray-400">· {detailRows.length} {detailRows.length !== 1 ? 'projects' : 'project'}</span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(totalH)}</td>
                                              <td className="px-4 py-2 text-xs text-gray-500 text-right italic">allocated above</td>
                                            </tr>
                                            {isExpanded && detailRows.map((row, idx) => {
                                              const hoursTaken = parseFloat(
                                                row.hours ?? row.leave_hours_taken
                                              ) || 0;
                                              const projDisplay = row.project_name || row.parent_project_name || row.project_code;
                                              return (
                                                <tr key={`dept_alloc_${category}_${idx}`} className="bg-blue-50/30 hover:bg-blue-50/50 transition-colors">
                                                  <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                    <div className="font-medium">{projDisplay}</div>
                                                    {row.project_code && (
                                                      <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                    )}
                                                  </td>
                                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(hoursTaken)}</td>
                                                  <td className="px-4 py-1.5 text-right tabular-nums">
                                                    {row.cost != null ? (
                                                      <>
                                                        <div className="text-sm font-semibold text-gray-700">{fmtCurrency(row.cost)}</div>
                                                        <div className="text-[10px] leading-tight text-blue-600">included above</div>
                                                      </>
                                                    ) : (
                                                      <span className="text-xs text-gray-400 italic">allocated above</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </tbody>
                            )}
                            {deptOverheadRows.length > 0 && (
                              <tbody className="divide-y divide-gray-100">
                                <tr className="bg-amber-50/60">
                                  <td colSpan={3} className="px-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                      <CalendarDays className="w-3 h-3" />
                                      Leave
                                    </div>
                                  </td>
                                </tr>
                                {(() => {
                                  const regularRows = deptOverheadRows.filter(r => !r.project_code);
                                  const oslGroupMap = {};
                                  deptOverheadRows.filter(r => r.project_code).forEach(r => {
                                    if (!oslGroupMap[r.category]) oslGroupMap[r.category] = [];
                                    oslGroupMap[r.category].push(r);
                                  });
                                  const oslGroups = Object.entries(oslGroupMap);

                                  return (
                                    <>
                                      {regularRows.map(renderDeptRow)}
                                      {oslGroups.map(([category, rows]) => {
                                        const oslKey = `${dept.department_name}__${category}`;
                                        const isExpanded = !!expandedOslCategories[oslKey];
                                        const h = rows.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                                        const c = rows.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                        return (
                                          <React.Fragment key={category}>
                                            <tr
                                              className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                              onClick={() => setExpandedOslCategories(prev => ({...prev, [oslKey]: !prev[oslKey]}))}
                                            >
                                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                  <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                  <span className="text-xs text-gray-400">· {rows.length} {rows.length !== 1 ? 'projects' : 'project'}</span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(h)}</td>
                                              <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(c)}</td>
                                            </tr>
                                            {isExpanded && rows.map((row, idx) => (
                                              <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                                <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                  <div className="font-medium">{row.project_name || row.project_code}</div>
                                                  {row.project_code && (
                                                    <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                  )}
                                                </td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_hours)}</td>
                                                <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.salary_cost)}</td>
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                                {renderDeptSubtotal('Leave Subtotal', deptOverheadRows)}
                              </tbody>
                            )}
                          </>
                        ) : (
                          <tbody className="divide-y divide-gray-100">
                            {dept.rows.map(renderDeptRow)}
                          </tbody>
                        )}

                        <tfoot>
                          <tr className="bg-red-50 border-t-2 border-red-200">
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800">SUBTOTAL</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(dept.subtotals?.total_hours)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(dept.subtotals?.salary_cost)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </AccordionSection>
                );
              })}
            </div>
          )}

          {/* ---- BY EMPLOYEE ---- */}
          {staffGrouping === 'employee' && !employeeLoading && filteredEmployees.length > 0 && (
            <div className="space-y-3">
              {filteredEmployees.map((emp) => {
                const isUnassigned = emp.department_name === 'Unassigned';
                // Office-pool ghost rows can have row_type='project' (the
                // P0000-018 / P0000-038 office rows) so we explicitly
                // exclude is_allocated_to_projects from the project lists
                // — those are rendered in the "Allocated to Projects
                // Above" section below.
                const empOnsiteRows = emp.rows.filter(
                  r => r.row_type === 'project' && !r.is_offsite && !r.is_allocated_to_projects
                );
                const empOffsiteRows = emp.rows.filter(
                  r => r.row_type === 'project' && r.is_offsite && !r.is_allocated_to_projects
                );
                const empProjectRows = emp.rows.filter(
                  r => r.row_type === 'project' && !r.is_allocated_to_projects
                );
                const empAllocatedLeaveRows = emp.rows.filter(r => r.is_allocated_to_projects);
                const empUntouchedOverheadRows = emp.rows.filter(
                  r => r.row_type !== 'project' && !r.is_allocated_to_projects
                );
                const hasAllocatedLeave = empAllocatedLeaveRows.length > 0;
                const hasUntouchedLeave = empUntouchedOverheadRows.length > 0;
                const hasBothEmpSections = empProjectRows.length > 0 && (hasAllocatedLeave || hasUntouchedLeave);

                const renderAllocatedSubline = (row) => {
                  const alloc = parseFloat(row.allocated_leave_hours) || 0;
                  const direct = parseFloat(row.direct_hours) || 0;
                  if (alloc <= 0 || direct <= 0) return null;
                  return (
                    <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
                      {fmtHours(direct)} direct + {fmtHours(alloc)} allocated leave
                    </div>
                  );
                };

                const sumLeaveBreakdown = (rowsArr) => {
                  const map = {};
                  rowsArr.forEach(r => {
                    (r.allocated_leave_breakdown || []).forEach(b => {
                      const cat = b.category || 'Leave';
                      if (!map[cat]) {
                        map[cat] = {
                          category: cat,
                          project_code: b.project_code,
                          project_name: b.project_name,
                          hours: 0,
                          cost: 0,
                        };
                      }
                      map[cat].hours += parseFloat(b.hours) || 0;
                      map[cat].cost += parseFloat(b.cost) || 0;
                    });
                  });
                  return Object.values(map)
                    .filter(b => b.hours > 0.001)
                    .sort((a, b) => b.hours - a.hours);
                };

                const renderLeaveBreakdownRows = (leaveBd, keyPrefix) =>
                  leaveBd.map((b, i) => (
                    <tr key={`${keyPrefix}-lbd-${i}`} className="bg-blue-50/40 table-row-dense">
                      <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {renderAllocatedRowLabel(b)}
                        </div>
                      </td>
                      <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(b.hours)}</td>
                      {/* Allocated-leave breakdown rows are already counted in
                          the parent project row's hours, so showing a % here
                          would visually double-count against 100%. Mirrors
                          the "(allocated above)" treatment in the XLSX. */}
                      <td className="px-4 py-1.5 text-xs text-gray-400 text-right italic">allocated above</td>
                      <td className="px-4 py-1.5 text-sm font-medium text-gray-700 text-right tabular-nums">{fmtCurrency(b.cost)}</td>
                    </tr>
                  ));

                // Denominator for the "% Allocation" column: the
                // employee's full-month salary cost (e.g. $2000 for a
                // standard $24k/yr office staff). Pulled from the
                // already-quantized subtotals so cell numbers tie
                // exactly to the displayed TOTAL row, regardless of
                // rounding drift.
                //
                // Cost-share (NOT hours-share) so Lentoria-style
                // contract consolidation -- where
                // ``apply_employee_cost_consolidation`` creates
                // synthetic 0-hour rows holding real cost -- renders
                // meaningful percentages on those rows.
                const empTotalCost = parseFloat(emp.subtotals?.salary_cost) || 0;

                const renderEmpRow = (row, idx) => (
                  <tr key={idx} className="table-row-dense">
                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                      {row.row_type === 'project' ? (
                        <>
                          <div className="font-medium">{getProjectDisplayName(row) || row.category}</div>
                          {row.project_code && (
                            <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                          )}
                          {renderAllocatedSubline(row)}
                        </>
                      ) : (
                        <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_hours)}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 text-right tabular-nums">{fmtPct(row.salary_cost, empTotalCost)}</td>
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.salary_cost)}</td>
                  </tr>
                );

                const renderEmpSubtotal = (label, sectionRows) => {
                  const h = sectionRows.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                  const c = sectionRows.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                  return (
                    <tr className="bg-gray-100/80 border-t border-gray-200">
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(h)}</td>
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtPct(c, empTotalCost)}</td>
                      <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(c)}</td>
                    </tr>
                  );
                };

                return (
                  <AccordionSection
                    key={emp.staff_id}
                    expanded={!!expandedEmployees[emp.staff_id]}
                    onToggle={() => toggleEmployee(emp.staff_id)}
                    theme="admin"
                    contentVariant="flush"
                    sticky
                    stickyTopClassName={APP_SHELL_STICKY_TOP_CLASS}
                    stickyExtra={
                      <table className="w-full table-fixed border-t border-gray-200">
                        <colgroup>
                          <col style={{ width: '40%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '30%' }} />
                        </colgroup>
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell-compact">Project / Category</th>
                            <th className="table-header-cell-compact text-right">Hours</th>
                            <th
                              className="table-header-cell-compact text-right"
                              title="Share of this employee's full-month salary cost (row cost / employee total). Includes Lentoria-style contract splits where hours stay on one project but cost is fanned out across siblings."
                            >
                              % Allocation
                            </th>
                            <th className="table-header-cell-compact text-right">Salary Cost</th>
                          </tr>
                        </thead>
                      </table>
                    }
                    icon={
                      <span className="text-xs font-medium text-blue-800">
                        {emp.name?.split(' ').filter(Boolean).map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
                      </span>
                    }
                    iconBg="bg-blue-100"
                    title={
                      <div className="flex items-start gap-2">
                        <div className="text-left">
                          <span>{emp.name}</span>
                          <div className="text-[11px] text-gray-400 font-mono leading-tight">{emp.staff_id}</div>
                        </div>
                        <InfoBadge color={isUnassigned ? 'amber' : getDepartmentBadgeColor(emp.department_name)} label={emp.department_name} />
                      </div>
                    }
                    trailing={
                      <span className="font-semibold text-gray-900 text-sm tabular-nums">{fmtCurrency(emp.subtotals?.salary_cost)}</span>
                    }
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed divide-y divide-gray-200">
                        <colgroup>
                          <col style={{ width: '40%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '30%' }} />
                        </colgroup>

                        {hasBothEmpSections ? (
                          <>
                            <tbody className="divide-y divide-gray-100">
                              <tr className="bg-gray-50">
                                <td colSpan={4} className="px-4 py-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <Briefcase className="w-3 h-3" />
                                    Projects
                                  </div>
                                </td>
                              </tr>
                              {(() => {
                                const projectGroups = {};
                                empOnsiteRows.forEach(r => {
                                  const key = getRowIdentity(r);
                                  if (!projectGroups[key]) projectGroups[key] = [];
                                  projectGroups[key].push(r);
                                });
                                const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                                  const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  return bTotal - aTotal;
                                });
                                return sortedKeys.map(key => {
                                  const group = projectGroups[key];
                                  const rep = group[0];
                                  const subRows = group.flatMap(r => r.direct_sub_rows || []);
                                  const leaveBd = sumLeaveBreakdown(group);
                                  const hasLeaveBd = leaveBd.length > 0;
                                  const hasMultiSub = subRows.length > 1;
                                  const needsAccordion = hasMultiSub || hasLeaveBd;

                                  if (!needsAccordion) {
                                    const r = group[0];
                                    if (
                                      r.row_type === 'project'
                                      && r.parent_project_name
                                      && !MAIN_OFFICE_CODES.has(r.project_code)
                                    ) {
                                      r.project_name = r.parent_project_name;
                                    }
                                    return renderEmpRow(r, key);
                                  }

                                  const accKey = `${emp.staff_id}__proj_${key}`;
                                  const isExpanded = !!expandedOslCategories[accKey];
                                  const h = group.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                                  const c = group.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                  const parentName = getProjectDisplayName(rep) || key;

                                  return (
                                    <React.Fragment key={key}>
                                      <tr
                                        className="table-row-dense cursor-pointer"
                                        onClick={() => setExpandedOslCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                      >
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <div className="flex items-center gap-2">
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                            <div>
                                              <div className="font-medium flex items-center gap-1.5">
                                                {parentName}
                                                {hasMultiSub && <CountBadge count={subRows.length} theme="admin" size="sm" />}
                                              </div>
                                              <div className="text-[11px] text-gray-400 font-mono leading-tight">{rep.project_code || key}</div>
                                              {renderAllocatedSubline(rep)}
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(h)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600 text-right tabular-nums">{fmtPct(c, empTotalCost)}</td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(c)}</td>
                                      </tr>
                                      {isExpanded && subRows.map((sr, idx) => (
                                        <tr key={`sub-${idx}`} className="bg-gray-50/50 table-row-dense">
                                          <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                              {sr.is_offsite ? (
                                                <LeaveTypeBadge leaveTypeName="External Activity" size="xs" />
                                              ) : (
                                                <span className="font-medium">{sr.label}</span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(sr.hours)}</td>
                                          <td className="px-4 py-1.5 text-sm text-gray-500 text-right tabular-nums">{fmtPct(sr.cost, empTotalCost)}</td>
                                          <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(sr.cost)}</td>
                                        </tr>
                                      ))}
                                      {isExpanded && hasLeaveBd && renderLeaveBreakdownRows(leaveBd, accKey)}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                              {empOffsiteRows.map((r, idx) => {
                                const typeName = (r.category?.split(' - ')[0]?.trim()) || 'External Activity';
                                const badgeName = typeName === 'External Activity' ? 'External Activity'
                                  : typeName === 'Work From Home' ? 'Work From Home'
                                  : typeName === 'On Course' ? 'On Course' : typeName;
                                const rawLabel = (r.direct_sub_rows && r.direct_sub_rows[0] && r.direct_sub_rows[0].label) || '';
                                const subLabel = (!rawLabel || rawLabel.toLowerCase() === badgeName.toLowerCase())
                                  ? 'Others'
                                  : rawLabel;
                                return (
                                  <tr key={`offsite-${r.project_code || idx}`} className="table-row-dense">
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <LeaveTypeBadge leaveTypeName={badgeName} size="xs" />
                                        <span className="text-sm text-gray-600">· {subLabel}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(r.total_hours)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600 text-right tabular-nums">{fmtPct(r.salary_cost, empTotalCost)}</td>
                                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(r.salary_cost)}</td>
                                  </tr>
                                );
                              })}
                              {renderEmpSubtotal('Projects Subtotal', empProjectRows)}
                            </tbody>
                            {hasAllocatedLeave && (
                              <tbody className="divide-y divide-gray-100">
                                <tr className="bg-blue-50/60">
                                  <td colSpan={4} className="px-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                      <CalendarDays className="w-3 h-3" />
                                      Allocated to Projects Above
                                    </div>
                                  </td>
                                </tr>
                                {(() => {
                                  // Mirrors the dept-view consolidation: keep
                                  // office-pool work redistribution ghosts as
                                  // individual rows (work-site names matter),
                                  // but collapse leave ghosts by category so
                                  // a leave absorbed across N projects renders
                                  // as one expandable "OSL · N projects" row.
                                  const officeGhostRows = empAllocatedLeaveRows.filter(
                                    r => r.project_code
                                      && MAIN_OFFICE_CODES.has(r.project_code)
                                      && r.row_type === 'project'
                                  );
                                  const leaveGhostRows = empAllocatedLeaveRows.filter(
                                    r => !(
                                      r.project_code
                                      && MAIN_OFFICE_CODES.has(r.project_code)
                                      && r.row_type === 'project'
                                    )
                                  );
                                  const leaveGhostMap = {};
                                  leaveGhostRows.forEach(r => {
                                    const cat = r.category || 'Leave';
                                    if (!leaveGhostMap[cat]) leaveGhostMap[cat] = [];
                                    leaveGhostMap[cat].push(r);
                                  });
                                  const leaveGhostGroups = Object.entries(leaveGhostMap);

                                  return (
                                    <>
                                      {officeGhostRows.map((row, idx) => {
                                        const hoursTaken = parseFloat(row.leave_hours_taken) || 0;
                                        return (
                                          <tr key={`alloc_office_${idx}`} className="table-row-dense bg-blue-50/10">
                                            <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                              {renderAllocatedRowLabel(row)}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(hoursTaken)}</td>
                                            {/* % Allocation deliberately BLANK on
                                                allocated-leave ghost rows -- these
                                                hours were already counted in the
                                                project rows above, so showing a %
                                                here would visually push the
                                                employee past 100%. */}
                                            <td className="px-4 py-2 text-xs text-gray-400 text-right italic">—</td>
                                            <td className="px-4 py-2 text-xs text-gray-500 text-right italic">allocated above</td>
                                          </tr>
                                        );
                                      })}
                                      {leaveGhostGroups.map(([category, rows]) => {
                                        const allocKey = `${emp.staff_id}__alloc_${category}`;
                                        const isExpanded = !!expandedOslCategories[allocKey];
                                        const destinations = rows.reduce(
                                          (merged, allocationRow) => mergeAllocationDestinations(
                                            merged,
                                            allocationRow.allocation_destinations,
                                          ),
                                          [],
                                        );
                                        const detailRows = destinations.length > 0 ? destinations : rows;
                                        const totalH = rows.reduce(
                                          (s, r) => s + (parseFloat(r.leave_hours_taken) || 0),
                                          0,
                                        );
                                        return (
                                          <React.Fragment key={`alloc_grp_${category}`}>
                                            <tr
                                              className="bg-blue-50/10 hover:bg-blue-50/40 cursor-pointer transition-colors"
                                              onClick={() => setExpandedOslCategories(prev => ({...prev, [allocKey]: !prev[allocKey]}))}
                                            >
                                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                  <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                  <span className="text-xs text-gray-400">· {detailRows.length} {detailRows.length !== 1 ? 'projects' : 'project'}</span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(totalH)}</td>
                                              <td className="px-4 py-2 text-xs text-gray-400 text-right italic">—</td>
                                              <td className="px-4 py-2 text-xs text-gray-500 text-right italic">allocated above</td>
                                            </tr>
                                            {isExpanded && detailRows.map((row, idx) => {
                                              const hoursTaken = parseFloat(
                                                row.hours ?? row.leave_hours_taken
                                              ) || 0;
                                              const projDisplay = getProjectDisplayName(row) || row.project_code;
                                              return (
                                                <tr key={`alloc_${category}_${idx}`} className="bg-blue-50/30 hover:bg-blue-50/50 transition-colors">
                                                  <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                    <div className="font-medium">{projDisplay}</div>
                                                    {row.project_code && (
                                                      <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                    )}
                                                  </td>
                                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(hoursTaken)}</td>
                                                  <td className="px-4 py-1.5 text-xs text-gray-400 text-right italic">—</td>
                                                  <td className="px-4 py-1.5 text-right tabular-nums">
                                                    {row.cost != null ? (
                                                      <>
                                                        <div className="text-sm font-semibold text-gray-700">{fmtCurrency(row.cost)}</div>
                                                        <div className="text-[10px] leading-tight text-blue-600">included above</div>
                                                      </>
                                                    ) : (
                                                      <span className="text-xs text-gray-400 italic">allocated above</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </tbody>
                            )}
                            {hasUntouchedLeave && (
                              <tbody className="divide-y divide-gray-100">
                                <tr className="bg-amber-50/60">
                                  <td colSpan={4} className="px-4 py-1.5">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                                      <CalendarDays className="w-3 h-3" />
                                      Leave
                                    </div>
                                  </td>
                                </tr>
                                {(() => {
                                  const regularRows = empUntouchedOverheadRows.filter(r => !r.project_code);
                                  const oslGroupMap = {};
                                  empUntouchedOverheadRows.filter(r => r.project_code).forEach(r => {
                                    if (!oslGroupMap[r.category]) oslGroupMap[r.category] = [];
                                    oslGroupMap[r.category].push(r);
                                  });
                                  const oslGroups = Object.entries(oslGroupMap);

                                  return (
                                    <>
                                      {regularRows.map(renderEmpRow)}
                                      {oslGroups.map(([category, rows]) => {
                                        const oslKey = `${emp.staff_id}__${category}`;
                                        const isExpanded = !!expandedOslCategories[oslKey];
                                        const h = rows.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0);
                                        const c = rows.reduce((s, r) => s + (parseFloat(r.salary_cost) || 0), 0);
                                        return (
                                          <React.Fragment key={category}>
                                            <tr
                                              className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                              onClick={() => setExpandedOslCategories(prev => ({...prev, [oslKey]: !prev[oslKey]}))}
                                            >
                                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                  <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                                  <span className="text-xs text-gray-400">· {rows.length} {rows.length !== 1 ? 'projects' : 'project'}</span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(h)}</td>
                                              <td className="px-4 py-2 text-sm text-gray-600 text-right tabular-nums">{fmtPct(c, empTotalCost)}</td>
                                              <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(c)}</td>
                                            </tr>
                                            {isExpanded && rows.map((row, idx) => (
                                              <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                                <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                                  <div className="font-medium">{getProjectDisplayName(row)}</div>
                                                  {row.project_code && (
                                                    <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                                  )}
                                                </td>
                                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_hours)}</td>
                                                <td className="px-4 py-1.5 text-sm text-gray-500 text-right tabular-nums">{fmtPct(row.salary_cost, empTotalCost)}</td>
                                                <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.salary_cost)}</td>
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                                {renderEmpSubtotal('Leave Subtotal', empUntouchedOverheadRows)}
                              </tbody>
                            )}
                          </>
                        ) : (
                          <tbody className="divide-y divide-gray-100">
                            {emp.rows.map(renderEmpRow)}
                          </tbody>
                        )}

                        <tfoot>
                          <tr className="bg-red-50 border-t-2 border-red-200">
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800">TOTAL</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtHours(emp.subtotals?.total_hours)}</td>
                            {/* Per-employee TOTAL: rows by construction
                                account for the employee's full salary cost,
                                so this always reads 100.00% -- gives HR a
                                one-glance sanity check that allocations add
                                up. */}
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtPct(emp.subtotals?.salary_cost, empTotalCost)}</td>
                            <td className="px-4 py-2.5 text-sm font-bold text-red-800 text-right tabular-nums">{fmtCurrency(emp.subtotals?.salary_cost)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </AccordionSection>
                );
              })}
            </div>
          )}

          {/* Empty state for employee view */}
          {staffGrouping === 'employee' && !employeeLoading && filteredEmployees.length === 0 && employeeReport && !searchTerm && (
            <div className="text-center py-8">
              <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No staff employees found for this period</p>
            </div>
          )}
        </>
      )}

      {/* Grand Total Bar */}
      {(workerSectionCount > 0 || staffSectionCount > 0) && (
        <div className="bg-gray-800 text-white rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Grand Total</p>
              <p className="text-xl font-bold">{fmtCurrency(totalCost)}</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-right">
              <p className="text-gray-400 text-xs">Workers</p>
              <p className="font-semibold tabular-nums">{fmtCurrency(workerCost)}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-xs">Staff</p>
              <p className="font-semibold tabular-nums">{fmtCurrency(staffCost)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SalaryCostSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center animate-pulse">
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="ml-4 flex-1">
              <div className="h-7 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between gap-3 animate-pulse">
      <div className="h-11 bg-gray-200 rounded-full flex-1 max-w-xl" />
      <div className="flex items-center gap-2">
        <div className="h-11 bg-gray-200 rounded-full w-48" />
        <div className="w-11 h-11 bg-gray-200 rounded-full" />
        <div className="w-11 h-11 bg-gray-200 rounded-full" />
      </div>
    </div>
    {[1, 2, 3].map(i => (
      <div key={i} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-gray-200 rounded" />
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="h-5 bg-gray-200 rounded w-32" />
          </div>
          <div className="flex items-center gap-3">
            <div className="h-5 bg-gray-200 rounded-full w-20" />
            <div className="h-5 bg-gray-200 rounded w-24" />
          </div>
        </div>
      </div>
    ))}
  </div>
);


// ============================================================================
// MOM LEVY BILL TAB
// ============================================================================

const MomLevyBillTab = ({ year, month, onMonthSync }) => {
  const [uploading, setUploading] = useState(false);
  const [billData, setBillData] = useState(null);
  const [billList, setBillList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('matched');
  const fileInputRef = useRef(null);

  const loadBillForMonth = useCallback(async () => {
    setLoading(true);
    try {
      const listRes = await staffAPI.plcGetLevyBillList();
      const allBills = listRes.data?.bills || [];
      setBillList(allBills);

      const activeBill = allBills.find(b => b.year === year && b.month === month && b.is_active);
      if (activeBill) {
        const detailRes = await staffAPI.plcGetLevyBillDetail(activeBill.id);
        setBillData(detailRes.data);
      } else {
        setBillData(null);
      }
    } catch {
      setBillData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadBillForMonth(); }, [loadBillForMonth]);

  const handleUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a valid CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await staffAPI.plcUploadLevyBill(formData);
      const data = res.data;

      if (data.upload) {
        onMonthSync(data.upload.year, data.upload.month);
      }

      setBillData({
        upload: data.upload,
        entries: [...(data.matched_entries || []), ...(data.unmatched_entries || [])],
      });

      toast.success(
        `Levy bill uploaded: ${data.upload.total_matched} matched, ${data.upload.total_unmatched} unmatched`
      );

      loadBillForMonth();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  if (loading) {
    return <MomLevyBillSkeleton />;
  }

  const upload = billData?.upload;
  const entries = billData?.entries || [];
  const matchedEntries = entries.filter(e => e.match_status === 'matched');
  const unmatchedEntries = entries.filter(e => e.match_status !== 'matched');

  const displayEntries = viewMode === 'matched' ? matchedEntries : unmatchedEntries;
  const filteredEntries = displayEntries.filter(e =>
    !searchTerm ||
    e.worker_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.fin_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.matched_staff_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Upload Zone — compact strip when a bill is already loaded */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileSelect}
      />
      {upload ? (
        <div
          className={`
            rounded-2xl border-2 px-4 py-3 transition-all cursor-pointer
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
            ${dragActive
              ? 'border-red-400 bg-red-50'
              : 'border-dashed border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
            }
          `}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
              <p className="text-sm text-gray-600">Parsing and matching workers...</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Upload className={`w-4 h-4 ${dragActive ? 'text-red-500' : 'text-gray-400'}`} />
              <span>Drop a new CSV to replace, or <span className="text-red-600 font-medium">click to browse</span></span>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`
            relative rounded-2xl border-2 p-6 text-center transition-all cursor-pointer
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
            ${dragActive
              ? 'border-red-400 bg-red-50'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
            }
          `}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
              <p className="text-sm text-gray-600">Parsing and matching workers...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-4">
              <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Upload className={`w-6 h-6 ${dragActive ? 'text-red-500' : 'text-gray-400'}`} />
              </div>
              <p className="text-sm font-medium text-gray-700">
                {`Upload MOM Levy Bill for ${MONTH_NAMES[month - 1]} ${year}`}
              </p>
              <p className="text-xs text-gray-500">
                Drag & drop CSV here, or click to browse. Month auto-detected from CSV header.
              </p>
            </div>
          )}
        </div>
      )}

      {/* No bill state */}
      {!upload && (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Levy Bill for {MONTH_NAMES[month - 1]} {year}
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Upload the MOM Levy Bill CSV downloaded from the MOM website. The system will
            auto-detect the billing month from the CSV header and match workers by FIN number.
          </p>
        </div>
      )}

      {/* Bill Summary + Data */}
      {upload && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
            <SummaryCard
              icon={Users}
              label="Workers in CSV"
              value={upload.total_entries}
              accent="gray"
            />
            <SummaryCard
              icon={CheckCircle2}
              label="Matched"
              value={upload.total_matched}
              accent="green"
            />
            <SummaryCard
              icon={AlertTriangle}
              label="Unmatched"
              value={upload.total_unmatched}
              accent={upload.total_unmatched > 0 ? 'amber' : 'green'}
            />
            <SummaryCard
              icon={DollarSign}
              label="Total Billed"
              value={fmtCurrency(upload.total_levy_amount)}
              accent="red"
            />
          </div>

          {/* Unmatched Warning */}
          {unmatchedEntries.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {unmatchedEntries.length} worker{unmatchedEntries.length > 1 ? 's' : ''} could not be matched
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Their FIN numbers were not found in the HR system. Check for typos, new workers not yet registered, or terminated workers still billed.
                </p>
              </div>
            </div>
          )}

          {/* Toolbar: Search + View */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search name, FIN..."
              theme="admin"
              className="w-full max-w-xl"
            />

            <div className="flex flex-wrap items-center justify-end gap-2">
              <PillToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'matched', label: 'Matched', icon: CheckCircle2, count: matchedEntries.length },
                  { id: 'unmatched', label: 'Unmatched', icon: AlertTriangle, count: unmatchedEntries.length },
                ]}
                variant="text"
                theme="admin"
                countMax={9999}
                ariaLabel="Filter levy bill entries by match status"
              />
            </div>
          </div>

          {/* Entries Table */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            {filteredEntries.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchTerm
                    ? 'No matching entries found'
                    : viewMode === 'unmatched'
                      ? 'All workers matched successfully'
                      : 'No entries to display'
                  }
                </p>
              </div>
            ) : (
              <>
                {/* Mobile cards (below md) */}
                <div className="md:hidden space-y-3 p-4">
                  {filteredEntries.map((entry, idx) => {
                    const isProrated = entry.billing_start_date && entry.billing_end_date &&
                      parseFloat(entry.levy_amount) < parseFloat(entry.monthly_rate);
                    const isTerminated = !!entry.termination_date;

                    return (
                      <div
                        key={entry.id || idx}
                        className={`border border-gray-200 rounded-xl p-4 ${viewMode === 'unmatched' ? 'bg-amber-50/50' : 'bg-white'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{entry.worker_name}</p>
                            {viewMode === 'matched' && entry.matched_staff_id && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                <span className="text-[11px] text-emerald-600 font-medium">{entry.matched_staff_id}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap justify-end gap-1 flex-shrink-0">
                            {isProrated && <span className="badge badge-info">PRORATED</span>}
                            {isTerminated && <span className="badge badge-secondary">TERMINATED</span>}
                          </div>
                        </div>
                        <div className="mt-3 space-y-1.5">
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">FIN (Last 4)</span>
                            <span className="text-gray-900 text-right truncate min-w-0 font-mono">{entry.fin_number}</span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Pass Type</span>
                            <span className="text-gray-900 text-right truncate min-w-0">{entry.pass_type}</span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Tier</span>
                            <span className="text-gray-900 text-right truncate min-w-0">{entry.tier || '-'}</span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Skill Level</span>
                            <span className="text-gray-900 text-right truncate min-w-0">{entry.skill_level || '-'}</span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Monthly Rate</span>
                            <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{fmtCurrency(entry.monthly_rate)}</span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Billed Levy</span>
                            <span className="text-gray-900 font-medium text-right truncate min-w-0 tabular-nums">{fmtCurrency(entry.levy_amount)}</span>
                          </div>
                          {viewMode === 'unmatched' && (
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Notes</span>
                              <span className="text-amber-700 text-right min-w-0">{entry.match_notes || 'FIN not found'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Desktop table (md and up) */}
                <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Worker Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      FIN (Last 4)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Pass Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Skill Level
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monthly Rate
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Billed Levy
                    </th>
                    {viewMode === 'unmatched' && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Notes
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEntries.map((entry, idx) => {
                      const isProrated = entry.billing_start_date && entry.billing_end_date &&
                        parseFloat(entry.levy_amount) < parseFloat(entry.monthly_rate);
                      const isTerminated = !!entry.termination_date;

                      return (
                        <tr
                          key={entry.id || idx}
                          className={`table-row-dense ${viewMode === 'unmatched' ? 'bg-amber-50/50' : ''}`}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{entry.worker_name}</span>
                              {isProrated && (
                                <span className="badge badge-info">PRORATED</span>
                              )}
                              {isTerminated && (
                                <span className="badge badge-secondary">TERMINATED</span>
                              )}
                            </div>
                            {viewMode === 'matched' && entry.matched_staff_id && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                <span className="text-[11px] text-emerald-600 font-medium">{entry.matched_staff_id}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                            {entry.fin_number}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {entry.pass_type}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {entry.tier || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {entry.skill_level || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">
                            {fmtCurrency(entry.monthly_rate)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right tabular-nums">
                            {fmtCurrency(entry.levy_amount)}
                          </td>
                          {viewMode === 'unmatched' && (
                            <td className="px-4 py-3 text-sm text-amber-700 whitespace-nowrap">
                              {entry.match_notes || 'FIN not found'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
                </div>
              </>
            )}
          </div>

          {/* Upload History */}
          {billList.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Upload History
              </h3>
              <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                {/* Mobile cards (below md) */}
                <div className="md:hidden space-y-3 p-4">
                  {billList.map((bill) => (
                    <div key={bill.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-medium text-gray-900 truncate min-w-0">
                          {MONTH_NAMES[bill.month - 1]} {bill.year}
                        </p>
                        <div className="flex-shrink-0">
                          {bill.is_active ? (
                            <span className="badge badge-success">ACTIVE</span>
                          ) : (
                            <span className="badge badge-secondary">REPLACED</span>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Filename</span>
                          <span className="text-gray-900 text-right truncate min-w-0">{bill.original_filename}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Workers</span>
                          <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{bill.total_entries}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Matched</span>
                          <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{bill.total_matched}/{bill.total_entries}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Total Levy</span>
                          <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{fmtCurrency(bill.total_levy_amount)}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Uploaded</span>
                          <span className="text-gray-900 text-right truncate min-w-0">
                            {new Date(bill.uploaded_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table (md and up) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Period
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Filename
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Workers
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Matched
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Total Levy
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Uploaded
                        </th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {billList.map((bill) => (
                        <tr key={bill.id} className="table-row-dense">
                          <td className="px-4 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">
                            {MONTH_NAMES[bill.month - 1]} {bill.year}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 max-w-[200px] truncate">
                            {bill.original_filename}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
                            {bill.total_entries}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
                            {bill.total_matched}/{bill.total_entries}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
                            {fmtCurrency(bill.total_levy_amount)}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">
                            {new Date(bill.uploaded_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {bill.is_active ? (
                              <span className="badge badge-success">ACTIVE</span>
                            ) : (
                              <span className="badge badge-secondary">REPLACED</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MomLevyBillSkeleton = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 bg-gray-200 rounded-full" />
        <div className="h-4 bg-gray-200 rounded w-48" />
        <div className="h-3 bg-gray-200 rounded w-64" />
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center animate-pulse">
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="ml-4 flex-1">
              <div className="h-7 bg-gray-200 rounded w-12 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="p-4 space-y-3 animate-pulse">
        <div className="h-8 bg-gray-100 rounded w-full" />
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-10 bg-gray-50 rounded w-full" />
        ))}
      </div>
    </div>
  </div>
);


// ============================================================================
// DORMITORY COST TAB
// ============================================================================

const DORM_COST_FIELDS = [
  'total_normal_hours', 'total_weekday_ot_hours', 'total_rest_day_ot_hours',
  'workday_cost', 'weekday_ot_cost', 'sunday_ot_cost', 'total_cost',
];

const DormCostTab = ({ year, month }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [xlsxExporting, setXlsxExporting] = useState(false);

  const handleExportXlsx = async () => {
    if (xlsxExporting) return;
    setXlsxExporting(true);
    try {
      const res = await staffAPI.plcExportDormCostReport({ year, month });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      a.download = `PLC_Dorm_Cost_${monthNames[month - 1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XLSX export failed', err);
      toast.error('Export failed — please try again');
    } finally {
      setXlsxExporting(false);
    }
  };

  const fetchReport = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = { year, month };
      if (refresh) params.refresh = 1;
      const res = await staffAPI.plcGetDormCostReport(params);
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dormitory cost report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const {
    rows = [], grand_totals: gt = {}, source_info, is_partial_month,
    unmatched_residents = [], vacancy_rows = [],
    total_vacancy_cost: vacancyCostStr = '0', combined_grand_total: combinedStr = '0',
  } = report || {};

  const vacancyCost = parseFloat(vacancyCostStr) || 0;
  const combinedGrandTotal = parseFloat(combinedStr) || 0;

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(row =>
      row.category?.toLowerCase().includes(term) ||
      row.project_name?.toLowerCase().includes(term) ||
      (row.workers || []).some(w =>
        w.name?.toLowerCase().includes(term) ||
        w.staff_id?.toLowerCase().includes(term)
      )
    );
  }, [rows, searchTerm]);

  const projectRows = useMemo(() => filteredRows.filter(r => r.row_type === 'project'), [filteredRows]);
  const overheadRows = useMemo(() => filteredRows.filter(r => r.row_type !== 'project'), [filteredRows]);
  const allocatedOverheadRows = useMemo(
    () => overheadRows.filter(r => r.is_allocated_to_projects),
    [overheadRows],
  );
  const standaloneOverheadRows = useMemo(
    () => overheadRows.filter(r => !r.is_allocated_to_projects),
    [overheadRows],
  );
  const hasProjectSection = projectRows.length > 0;
  const hasOverheadSection = overheadRows.length > 0;
  const hasVacancySection = vacancy_rows.length > 0;
  const hasMultipleSections = [hasProjectSection, hasOverheadSection, hasVacancySection].filter(Boolean).length > 1;

  const { projectTotal, overheadTotal } = useMemo(() => {
    let proj = 0, over = 0;
    for (const row of rows) {
      const cost = parseFloat(row.total_cost) || 0;
      if (row.row_type === 'project') proj += cost;
      else over += cost;
    }
    return { projectTotal: proj, overheadTotal: over };
  }, [rows]);

  const projectPct = combinedGrandTotal > 0 ? ((projectTotal / combinedGrandTotal) * 100).toFixed(1) : '0.0';
  const overheadPct = combinedGrandTotal > 0 ? ((overheadTotal / combinedGrandTotal) * 100).toFixed(1) : '0.0';
  const vacancyPct = combinedGrandTotal > 0 ? ((vacancyCost / combinedGrandTotal) * 100).toFixed(1) : '0.0';

  if (loading) return <DormCostSkeleton />;

  if (error && !report?.has_data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Dormitory Cost</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">{error}</p>
        <button
          onClick={() => fetchReport(true)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded-lg px-3 py-1.5"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  if (report?.has_data && rows.length === 0 && unmatched_residents.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Building2 className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Dormitory Cost Data</h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto">
          No third-party dormitory costs found for {MONTH_NAMES[month - 1]} {year}.
          This could mean all workers are in company-owned dormitories or no dorm stays overlap this period.
        </p>
      </div>
    );
  }

  const AUDIT_CATEGORIES = ['Others', 'Absent', 'Unpaid Leave'];

  const renderDataRow = (row, idx) => {
    if (row.is_allocated_to_projects) {
      const allocationKey = `dorm__row_${row.category}_${idx}`;
      return (
        <AllocationAuditRows
          key={allocationKey}
          row={row}
          rowKey={allocationKey}
          isExpanded={!!expandedCategories[allocationKey]}
          onToggle={() => setExpandedCategories(prev => ({
            ...prev,
            [allocationKey]: !prev[allocationKey],
          }))}
          costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
        />
      );
    }
    const workers = row.workers || [];
    const showWorkerAudit = row.row_type !== 'project' && AUDIT_CATEGORIES.includes(row.category) && workers.length > 0;

    return (
      <tr key={idx} className="table-row-dense">
        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
          {row.row_type === 'project' ? (
            <>
              <div className="font-medium">{row.project_name || row.category}</div>
              {row.project_code && (
                <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <LeaveTypeBadge leaveTypeName={row.category} size="xs" />
                {workers.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                    <Users className="w-3 h-3" />
                    {workers.length}
                  </span>
                )}
              </div>
              <AllocatedToProjectsHint row={row} />
              {showWorkerAudit && (
                <div className="mt-0.5 space-y-0">
                  {workers.slice(0, 5).map((w, i) => (
                    <div key={i} className="text-[11px] text-gray-400 leading-tight">
                      {w.name || w.staff_id}
                      {w.employment_end_date && (
                        <span className="ml-1 text-amber-500 font-medium">
                          (left {new Date(w.employment_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})
                        </span>
                      )}
                    </div>
                  ))}
                  {workers.length > 5 && (
                    <div className="text-[11px] text-gray-400">+{workers.length - 5} more</div>
                  )}
                </div>
              )}
            </>
          )}
        </td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
        <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
      </tr>
    );
  };

  const renderSectionSubtotal = (label, sectionRows) => {
    const t = sumSection(sectionRows, DORM_COST_FIELDS);
    return (
      <tr className="bg-gray-100/80 border-t border-gray-200">
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
        <td className="px-4 py-2 text-xs font-semibold text-gray-600 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
        <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Partial Month Warning */}
      {is_partial_month && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Partial Month Estimate</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Costs for {MONTH_NAMES[month - 1]} {year} are estimates based on dorm stays up to today.
              Final figures are available after month-close when all stay segments are finalised.
            </p>
          </div>
        </div>
      )}

      {/* Unmatched Residents — data-quality alert only when degraded */}
      {unmatched_residents.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">
              {unmatched_residents.length} Unmatched Resident{unmatched_residents.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-amber-600">
              (staff_id not found in HR — cost not allocated to projects)
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {unmatched_residents.slice(0, 12).map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-1.5 text-sm border border-amber-100">
                <span className="text-gray-700 truncate mr-2">{r.full_name || r.staff_id}</span>
                <span className="text-amber-700 font-medium whitespace-nowrap">{fmtCurrency(r.dorm_cost)}</span>
              </div>
            ))}
            {unmatched_residents.length > 12 && (
              <div className="text-xs text-amber-600 flex items-center px-3">
                +{unmatched_residents.length - 12} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <SummaryCard
          icon={Building2}
          label="Total Dorm Cost"
          value={fmtCurrency(combinedGrandTotal)}
          subtitle={`Dormitory Portal · ${source_info?.total_matched || 0} residents + vacancy`}
          accent="red"
        />
        <SummaryCard
          icon={Briefcase}
          label="Project Allocation"
          value={fmtCurrency(projectTotal)}
          subtitle={`${projectPct}% of total`}
          accent="blue"
        />
        <SummaryCard
          icon={CalendarDays}
          label="Leave"
          value={fmtCurrency(overheadTotal)}
          subtitle={`${overheadPct}% of total`}
          accent="amber"
        />
        <SummaryCard
          icon={BedDouble}
          label="Unoccupied Beds"
          value={fmtCurrency(vacancyCost)}
          subtitle={`${vacancyPct}% of total`}
          accent="gray"
        />
      </div>

      {/* Toolbar: Search + Refresh + Export */}
      <div className="flex items-center justify-between gap-3">
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={setSearchTerm}
          placeholder="Search name, staff ID, project..."
          theme="admin"
          className="max-w-xl"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchReport(true)}
            className="icon-button-admin"
            title="Refresh from Dorm Portal"
            aria-label="Refresh from Dorm Portal"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleExportXlsx}
            className="icon-button-admin"
            title="Export Excel (.xlsx)"
            aria-label="Export Excel"
            disabled={xlsxExporting || filteredRows.length === 0}
          >
            {xlsxExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Allocation Table */}
      {filteredRows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchTerm ? 'No matching categories found' : 'No dormitory cost allocation data for this period'}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-x-auto md:overflow-visible">
          <table className="w-full min-w-[700px] md:min-w-0 table-fixed divide-y divide-gray-200">
            <colgroup>
              <col style={{ width: '27%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '11.5%' }} />
              <col style={{ width: '11.5%' }} />
              <col style={{ width: '11.5%' }} />
              <col style={{ width: '11.5%' }} />
            </colgroup>
            <CostAllocationTableHeader costLabel="Workday Cost" sticky />

            {hasMultipleSections ? (
              <>
                {hasProjectSection && (
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-gray-50">
                      <td colSpan={8} className="px-4 py-1.5">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <Briefcase className="w-3 h-3" />
                          Projects
                        </div>
                      </td>
                    </tr>
                    {(() => {
                      const { consolidated, regular } = partitionConsolidated(projectRows);
                      const cgJsx = consolidated.map(({ label, allRows, byCode }) => {
                        const accKey = `dorm__cg_${label}`;
                        const isExpanded = !!expandedCategories[accKey];
                        const t = sumSection(allRows, DORM_COST_FIELDS);
                        const codeKeys = Object.keys(byCode).sort((a, b) => {
                          const aT = byCode[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                          const bT = byCode[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                          return bT - aT;
                        });
                        return (
                          <React.Fragment key={`cg_${label}`}>
                            <tr className="table-row-dense cursor-pointer" onClick={() => setExpandedCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}>
                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  <div className="font-medium flex items-center gap-1.5">{label} <CountBadge count={codeKeys.length} theme="admin" size="sm" /></div>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                              <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                            </tr>
                            {isExpanded && codeKeys.map(code => {
                              const codeRows = byCode[code];
                              const ct = sumSection(codeRows, DORM_COST_FIELDS);
                              const codeName = getProjectDisplayName(codeRows[0]) || code;
                              return (
                                <tr key={code} className="bg-gray-50/50 table-row-dense">
                                  <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                    <div className="font-medium">{codeName}</div>
                                    <div className="text-[11px] text-gray-400 font-mono leading-tight">{code}</div>
                                  </td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_normal_hours)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(ct.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.workday_cost)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.weekday_ot_cost)}</td>
                                  <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(ct.sunday_ot_cost)}</td>
                                  <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(ct.total_cost)}</td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                      const projectGroups = {};
                      regular.forEach(r => {
                        const key = getRowIdentity(r);
                        if (!projectGroups[key]) projectGroups[key] = [];
                        projectGroups[key].push(r);
                      });
                      const sortedKeys = Object.keys(projectGroups).sort((a, b) => {
                        const aTotal = projectGroups[a].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                        const bTotal = projectGroups[b].reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
                        return bTotal - aTotal;
                      });
                      const regJsx = sortedKeys.map(key => {
                        const group = projectGroups[key];
                        if (group.length === 1) {
                          const r = group[0];
                          if (
                            r.row_type === 'project'
                            && r.parent_project_name
                            && !MAIN_OFFICE_CODES.has(r.project_code)
                          ) {
                            r.project_name = r.parent_project_name;
                          }
                          return renderDataRow(r, key);
                        }
                        const accKey = `dorm__proj_${key}`;
                        const isExpanded = !!expandedCategories[accKey];
                        const t = sumSection(group, DORM_COST_FIELDS);
                        const parentName = getProjectDisplayName(group[0]) || key;
                        return (
                          <React.Fragment key={key}>
                            <tr
                              className="table-row-dense cursor-pointer"
                              onClick={() => setExpandedCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                            >
                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  <div>
                                    <div className="font-medium flex items-center gap-1.5">{parentName} <CountBadge count={group.length} theme="admin" size="sm" /></div>
                                    <div className="text-[11px] text-gray-400 font-mono leading-tight">{key}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                              <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                            </tr>
                            {isExpanded && group.map((row, idx) => (
                              <tr key={idx} className="bg-gray-50/50 table-row-dense">
                                <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                  <div className="font-medium">{row.project_name || row.project_code}</div>
                                </td>
                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      });
                      return [...cgJsx, ...regJsx];
                    })()}
                    {renderSectionSubtotal('Projects Subtotal', projectRows)}
                  </tbody>
                )}
                {allocatedOverheadRows.length > 0 && (
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-blue-50/60">
                      <td colSpan={8} className="px-4 py-1.5">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                          <CalendarDays className="w-3 h-3" />
                          Allocated to Projects Above
                        </div>
                      </td>
                    </tr>
                    {allocatedOverheadRows.map((row, idx) => {
                      const allocationKey = `dorm__allocated_${row.category}_${idx}`;
                      return (
                        <AllocationAuditRows
                          key={allocationKey}
                          row={row}
                          rowKey={allocationKey}
                          isExpanded={!!expandedCategories[allocationKey]}
                          onToggle={() => setExpandedCategories(prev => ({
                            ...prev,
                            [allocationKey]: !prev[allocationKey],
                          }))}
                          costComponentFields={['workday_cost', 'weekday_ot_cost', 'sunday_ot_cost']}
                        />
                      );
                    })}
                  </tbody>
                )}
                {standaloneOverheadRows.length > 0 && (
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-amber-50/60">
                      <td colSpan={8} className="px-4 py-1.5">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                          <CalendarDays className="w-3 h-3" />
                          Leave
                        </div>
                      </td>
                    </tr>
                    {(() => {
                      const regularRows = standaloneOverheadRows.filter(r => !r.project_code);
                      const projectCodeGroupMap = {};
                      standaloneOverheadRows.filter(r => r.project_code).forEach(r => {
                        if (!projectCodeGroupMap[r.category]) projectCodeGroupMap[r.category] = [];
                        projectCodeGroupMap[r.category].push(r);
                      });
                      const projectCodeGroups = Object.entries(projectCodeGroupMap);

                      const consolidatedMap = {};
                      regularRows.forEach(r => {
                        const consolidationKey = getLeaveConsolidationKey(r);
                        if (!consolidatedMap[consolidationKey]) {
                          consolidatedMap[consolidationKey] = { ...r, workers: [...(r.workers || [])] };
                        } else {
                          DORM_COST_FIELDS.forEach(f => {
                            consolidatedMap[consolidationKey][f] =
                              (parseFloat(consolidatedMap[consolidationKey][f]) || 0) + (parseFloat(r[f]) || 0);
                          });
                          const existing = consolidatedMap[consolidationKey];
                          mergeAllocationAuditFields(existing, r);
                          const seen = new Set((existing.workers || []).map(w => w.staff_id));
                          (r.workers || []).forEach(w => {
                            if (!seen.has(w.staff_id)) { existing.workers.push(w); seen.add(w.staff_id); }
                          });
                        }
                      });
                      const flatRows = Object.values(consolidatedMap)
                        .sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));

                      return (
                        <>
                          {flatRows.map(renderDataRow)}
                          {projectCodeGroups.map(([category, catRows]) => {
                            const accKey = `dorm__leave_${category}`;
                            const isExpanded = !!expandedCategories[accKey];
                            const t = sumSection(catRows, DORM_COST_FIELDS);
                            const sortedCatRows = [...catRows].sort((a, b) => (parseFloat(b.total_cost) || 0) - (parseFloat(a.total_cost) || 0));
                            return (
                              <React.Fragment key={category}>
                                <tr
                                  className={getLeaveTypeGroupRowClass(category, { role: 'parent' })}
                                  onClick={() => setExpandedCategories(prev => ({ ...prev, [accKey]: !prev[accKey] }))}
                                >
                                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                      <LeaveTypeBadge leaveTypeName={category} size="xs" />
                                      <span className="text-xs text-gray-400">· {sortedCatRows.length} {sortedCatRows.length !== 1 ? 'projects' : 'project'}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_normal_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_weekday_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtHours(t.total_rest_day_ot_hours)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.workday_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.weekday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">{fmtCurrency(t.sunday_ot_cost)}</td>
                                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(t.total_cost)}</td>
                                </tr>
                                {isExpanded && sortedCatRows.map((row, idx) => (
                                  <tr key={idx} className={getLeaveTypeGroupRowClass(category, { role: 'child' })}>
                                    <td className="pl-10 pr-4 py-1.5 text-sm text-gray-700 whitespace-nowrap">
                                      <div className="font-medium">{getProjectDisplayName(row)}</div>
                                      {row.project_code && (
                                        <div className="text-[11px] text-gray-400 font-mono leading-tight">{row.project_code}</div>
                                      )}
                                    </td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_normal_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_weekday_ot_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtHours(row.total_rest_day_ot_hours)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.workday_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.weekday_ot_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm text-gray-600 text-right tabular-nums">{fmtCurrency(row.sunday_ot_cost)}</td>
                                    <td className="px-4 py-1.5 text-sm font-semibold text-gray-700 text-right tabular-nums">{fmtCurrency(row.total_cost)}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </>
                      );
                    })()}
                    {renderSectionSubtotal('Leave Subtotal', standaloneOverheadRows)}
                  </tbody>
                )}
                {hasVacancySection && (
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-gray-100/80">
                      <td colSpan={8} className="px-4 py-1.5">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <BedDouble className="w-3 h-3" />
                          Unoccupied Beds
                        </div>
                      </td>
                    </tr>
                    {vacancy_rows.map((vr, idx) => {
                      const occupancyPct = vr.bed_count > 0 && vr.days_in_month > 0
                        ? Math.round(((vr.bed_count * vr.days_in_month - vr.vacant_bed_days) / (vr.bed_count * vr.days_in_month)) * 100)
                        : null;
                      return (
                      <tr key={idx} className="table-row-dense">
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                          <div className="font-medium">{vr.dorm_name}</div>
                          <div className="text-[11px] text-gray-400 leading-tight">
                            {vr.vacant_beds_equivalent} avg empty beds · {occupancyPct != null ? `${occupancyPct}% occupied · ` : ''}{vr.bed_count} total · {fmtCurrency(vr.cost_per_bed_monthly)}/bed/mo
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-400 text-right">-</td>
                        <td className="px-4 py-2 text-sm text-gray-400 text-right">-</td>
                        <td className="px-4 py-2 text-sm text-gray-400 text-right">-</td>
                        <td className="px-4 py-2 text-sm text-gray-400 text-right">-</td>
                        <td className="px-4 py-2 text-sm text-gray-400 text-right">-</td>
                        <td className="px-4 py-2 text-sm text-gray-400 text-right">-</td>
                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right tabular-nums">{fmtCurrency(vr.vacancy_cost)}</td>
                      </tr>
                      );
                    })}
                    <tr className="bg-gray-100/80 border-t border-gray-200">
                      <td className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">Vacancy Subtotal</td>
                      <td className="px-4 py-2 text-xs text-gray-400 text-right">-</td>
                      <td className="px-4 py-2 text-xs text-gray-400 text-right">-</td>
                      <td className="px-4 py-2 text-xs text-gray-400 text-right">-</td>
                      <td className="px-4 py-2 text-xs text-gray-400 text-right">-</td>
                      <td className="px-4 py-2 text-xs text-gray-400 text-right">-</td>
                      <td className="px-4 py-2 text-xs text-gray-400 text-right">-</td>
                      <td className="px-4 py-2 text-xs font-bold text-gray-700 text-right tabular-nums">{fmtCurrency(vacancyCost)}</td>
                    </tr>
                  </tbody>
                )}
              </>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map(renderDataRow)}
              </tbody>
            )}

            <tfoot>
              <tr className="bg-gray-900 text-white">
                <td className="px-4 py-2.5 text-sm font-bold uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    Grand Total
                    {hasVacancySection && (
                      <span className="relative group">
                        <InfoBadge
                          text="Includes worker-allocated costs (snapshot rates) + vacancy costs (current dorm rates). Totals may not perfectly reconcile due to rate changes."
                          size="sm"
                        />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums">{fmtHours(gt.total_normal_hours)}</td>
                <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums">{fmtHours(gt.total_weekday_ot_hours)}</td>
                <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums">{fmtHours(gt.total_rest_day_ot_hours)}</td>
                <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums">{fmtCurrency(gt.workday_cost)}</td>
                <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums">{fmtCurrency(gt.weekday_ot_cost)}</td>
                <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums">{fmtCurrency(gt.sunday_ot_cost)}</td>
                <td className="px-4 py-2.5 text-sm font-bold text-right tabular-nums">{fmtCurrency(combinedGrandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

    </div>
  );
};


const DormCostSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-gray-100 flex-shrink-0" />
            <div className="ml-2 sm:ml-4 min-w-0 flex-1">
              <div className="h-6 sm:h-7 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-20 bg-gray-100 rounded mb-1" />
              <div className="h-3 w-28 bg-gray-100 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="divide-y divide-gray-100">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="px-4 py-3 flex items-center gap-4 animate-pulse">
            <div className="h-4 w-40 bg-gray-200 rounded" />
            <div className="flex-1" />
            {[1, 2, 3, 4].map(j => (
              <div key={j} className="h-4 w-16 bg-gray-100 rounded" />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);


// ============================================================================
// PCP (PRIMARY CARE PLAN) TAB
// ============================================================================

const PCP_PAGE_SIZE = 25;

// Ledger facts, not always-on inputs. HR selects a name or FIN to copy it.
// A focused input painted a full-width admin-red underline under that text,
// which reads as "this worker is wrong". A real field opens only to correct
// a value: Enter / double-click, or a click when the cell is empty.
const PcpEditableCell = ({
  value,
  onCommit,
  label,
  displayClassName,
  inputClassName = '',
  align = 'left',
  activateOnClick = false,
  transform,
  inputMode,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const closeRef = useRef(false);
  const shown = value == null || value === '' ? '' : String(value);
  const clickEdits = activateOnClick || shown === '';

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const open = () => {
    closeRef.current = false;
    setDraft(shown);
    setEditing(true);
  };

  const finish = (save) => {
    if (closeRef.current) return;
    closeRef.current = true;
    setEditing(false);
    if (save && draft !== shown) onCommit(draft);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode={inputMode}
        value={draft}
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        aria-label={label}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(transform ? transform(next) : next);
        }}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false);
          }
        }}
        size={Math.min(48, Math.max(draft.length + 1, 10))}
        className={`input-admin !w-auto ${align === 'right' ? 'text-right' : ''} ${inputClassName}`}
      />
    );
  }

  return (
    <span
      tabIndex={0}
      role={clickEdits ? 'button' : undefined}
      title={shown === '' ? `Enter ${label}` : undefined}
      aria-label={
        shown
          ? `${label}: ${shown}. ${clickEdits ? 'Activate to edit.' : 'Press Enter or double-click to edit.'}`
          : `${label}: empty. Activate to enter a value.`
      }
      onClick={() => {
        if (clickEdits) open();
      }}
      onDoubleClick={(e) => {
        e.preventDefault();
        open();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || (clickEdits && e.key === ' ')) {
          e.preventDefault();
          open();
        }
      }}
      className={`max-w-full rounded-md outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${
        activateOnClick
          ? `inline-flex min-h-11 min-w-0 items-center ${
              shown === '' ? 'min-w-11 justify-center' : align === 'right' ? 'justify-end' : ''
            }`
          : 'inline-block'
      } ${
        clickEdits
          ? `cursor-pointer${activateOnClick ? '' : ' px-2 py-0.5 -mx-2 hover:bg-white'}`
          : 'cursor-text select-text'
      }`}
    >
      <span className={`${shown ? displayClassName : 'text-sm text-gray-400'} ${activateOnClick ? 'min-w-0 truncate' : ''}`}>
        {shown || '—'}
      </span>
    </span>
  );
};

const PCPTab = ({ year, month }) => {
  const [providers, setProviders] = useState([]);
  const [uploadingProvider, setUploadingProvider] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [monthData, setMonthData] = useState(null);
  const [uploadList, setUploadList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragActiveProvider, setDragActiveProvider] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [editableEntries, setEditableEntries] = useState([]);
  const [hasEdits, setHasEdits] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRefs = useRef({});

  const PCP_STATUS_MESSAGES = [
    'Uploading document...',
    'Running OCR extraction...',
    'Analyzing table structure...',
    'Extracting worker data...',
    'Matching against employee records...',
  ];

  useEffect(() => {
    staffAPI.pcpProviders().then(res => {
      setProviders(res.data?.providers || []);
    }).catch(() => {});
  }, []);

  const loadMonthData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, listRes] = await Promise.all([
        staffAPI.pcpMonthSummary(year, month),
        staffAPI.pcpGetList(),
      ]);
      const summary = summaryRes.data;
      setMonthData(summary);
      setUploadList(listRes.data?.uploads || []);
      setEditableEntries((summary?.entries || []).map(e => ({ ...e })));
      setHasEdits(false);
    } catch {
      setMonthData(null);
      setEditableEntries([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadMonthData(); }, [loadMonthData]);

  useEffect(() => { setCurrentPage(1); }, [viewMode, searchTerm]);

  const handleUpload = async (file, providerId) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'xlsx', 'csv'].includes(ext)) {
      toast.error('Please select a PDF, XLSX, or CSV file');
      return;
    }

    setUploadingProvider(providerId);
    setUploading(true);
    setUploadError(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year);
    formData.append('month', month);
    formData.append('provider_id', providerId);

    try {
      const res = await staffAPI.pcpUpload(formData);
      const data = res.data;
      setUploading(false);
      setUploadingProvider(null);

      toast.success(
        `${data.upload.provider_name}: ${data.upload.total_matched} matched, ${data.upload.total_unmatched} unmatched`
      );
      loadMonthData();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.');
    }
  };

  const handleDrop = (e, providerId) => {
    e.preventDefault();
    setDragActiveProvider(null);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file, providerId);
  };

  const handleFileSelect = (e, providerId) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file, providerId);
    e.target.value = '';
  };

  const updateEntry = (entryId, field, value) => {
    setEditableEntries(prev => prev.map(e =>
      e.id === entryId ? { ...e, [field]: value } : e
    ));
    setHasEdits(true);
  };

  const handleSaveEdits = async () => {
    if (!monthData?.uploads?.length) return;
    setSaving(true);
    try {
      const uploadIds = [...new Set(editableEntries.map(e => e.upload_id))];
      for (const uid of uploadIds) {
        const entriesToSave = editableEntries
          .filter(e => e.upload_id === uid)
          .map(e => ({ id: e.id, worker_name: e.worker_name, id_number: e.id_number, total: e.total, matched_staff_id: e.matched_staff_id || '' }));
        if (entriesToSave.length > 0) {
          await staffAPI.pcpSave(uid, entriesToSave);
        }
      }
      setHasEdits(false);
      toast.success('Changes saved successfully');
      loadMonthData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyMonth = async () => {
    setVerifying(true);
    try {
      await staffAPI.pcpVerifyMonth(year, month);
      toast.success(`PCP data for ${MONTH_NAMES[month - 1]} ${year} verified`);
      loadMonthData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleExportExcel = async () => {
    if (hasEdits) {
      toast.warning('Save your PCP changes before exporting');
      return;
    }
    if (!editableEntries.length) {
      toast.error('No data to export');
      return;
    }
    try {
      const response = await staffAPI.pcpExportXlsx(year, month);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PCP_${MONTH_NAMES[month - 1]}_${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PCP report exported to Excel');
    } catch {
      toast.error('Failed to export Excel file');
    }
  };

  if (loading) return <PCPSkeleton />;

  const dismissUploadError = () => {
    setUploadError(null);
    setUploading(false);
    setUploadingProvider(null);
  };

  const uploads = monthData?.uploads || [];
  const hasData = uploads.length > 0;
  const batchStatus = monthData?.batch_status || 'empty';
  const totalEntries = monthData?.total_entries || 0;
  const totalMatched = monthData?.total_matched || 0;
  const totalUnmatched = monthData?.total_unmatched || 0;
  const totalAmount = monthData?.total_amount || '0';
  const crossProviderCount = monthData?.cross_provider_count || 0;

  const matchedEntries = editableEntries.filter(e => e.match_status === 'matched');
  const unmatchedEntries = editableEntries.filter(e => e.match_status !== 'matched');

  const displayEntries = viewMode === 'matched'
    ? matchedEntries
    : viewMode === 'unmatched'
      ? unmatchedEntries
      : editableEntries;

  const filteredEntries = displayEntries.filter(e =>
    !searchTerm ||
    e.worker_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.id_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.matched_staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.provider_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredEntries.length / PCP_PAGE_SIZE);
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * PCP_PAGE_SIZE,
    currentPage * PCP_PAGE_SIZE
  );

  const uploadingProviderObj = providers.find(p => p.id === uploadingProvider);

  return (
    <div className="space-y-4">
      {/* Processing Modal */}
      <ProcessingModal
        isOpen={uploading}
        title="Processing PCP Invoice"
        subtitle={`${uploadingProviderObj?.name || 'Provider'} — ${MONTH_NAMES[month - 1]} ${year}`}
        theme="admin"
        statusMessages={PCP_STATUS_MESSAGES}
        rotateInterval={8000}
        error={uploadError}
        onDismissError={dismissUploadError}
      />

      {/* Per-provider upload zones — one per provider, side by side */}
      <div className={`grid gap-3 ${providers.length > 2 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
        {providers.map(p => {
          const providerUpload = uploads.find(u => u.provider_id === p.id);
          const isDragActive = dragActiveProvider === p.id;
          return (
            <div key={p.id} className="relative">
              <input
                ref={(el) => { fileInputRefs.current[p.id] = el; }}
                type="file"
                accept=".pdf,.xlsx,.csv"
                className="hidden"
                onChange={(e) => handleFileSelect(e, p.id)}
              />
              <div
                className={`
                  rounded-2xl border-2 px-4 py-3 transition-all cursor-pointer
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
                  ${isDragActive
                    ? 'border-red-400 bg-red-50'
                    : providerUpload
                      ? 'border-solid border-gray-200 bg-white hover:border-gray-300'
                      : 'border-dashed border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                onDragOver={(e) => { if (!uploading) { e.preventDefault(); setDragActiveProvider(p.id); } }}
                onDragLeave={() => setDragActiveProvider(null)}
                onDrop={(e) => { if (!uploading) handleDrop(e, p.id); }}
                onClick={() => { if (!uploading) fileInputRefs.current[p.id]?.click(); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (!uploading && (e.key === 'Enter' || e.key === ' ')) fileInputRefs.current[p.id]?.click(); }}
              >
                <div className="flex items-center gap-3">
                  <Upload className={`w-4 h-4 flex-shrink-0 ${isDragActive ? 'text-red-500' : 'text-gray-400'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{p.name}</span>
                      {providerUpload && (
                        <StatusBadge status={providerUpload.status === 'verified' ? 'verified' : 'draft'} />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {providerUpload
                        ? `${providerUpload.total_entries} entries · ${fmtCurrency(providerUpload.total_amount)}`
                        : `Drop invoice or click to upload`
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {!hasData && (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <HeartPulse className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No PCP Data for {MONTH_NAMES[month - 1]} {year}
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Select a provider above and upload their PCP invoice. You can upload invoices from multiple providers for the same month.
          </p>
        </div>
      )}

      {/* Data loaded — summary + editable table */}
      {hasData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
            <SummaryCard icon={Users} label="Total Workers" value={totalEntries} accent="gray" />
            <SummaryCard icon={CheckCircle2} label="Matched" value={totalMatched} accent="green" />
            <SummaryCard
              icon={AlertTriangle}
              label="Unmatched"
              value={totalUnmatched}
              accent={totalUnmatched > 0 ? 'amber' : 'green'}
              subtitle={totalUnmatched > 0 ? 'Edit IDs below to re-match' : undefined}
            />
            <SummaryCard icon={DollarSign} label="Total Amount" value={fmtCurrency(totalAmount)} accent="red" />
          </div>

          {/* Toolbar: Search + View + Actions + Export */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search name, ID, provider..."
              theme="admin"
              className="w-full max-w-xl"
            />

            <div className="flex flex-wrap items-center justify-end gap-2">
              <PillToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'all', label: 'All', icon: Users, count: editableEntries.length },
                  { id: 'matched', label: 'Matched', icon: CheckCircle2, count: matchedEntries.length },
                  { id: 'unmatched', label: 'Unmatched', icon: AlertTriangle, count: unmatchedEntries.length },
                ]}
                variant="text"
                theme="admin"
                ariaLabel="Filter PCP entries by match status"
              />
              {hasEdits && (
                <button
                  onClick={handleSaveEdits}
                  disabled={saving}
                  className="btn btn-admin-secondary self-center flex items-center gap-2 whitespace-nowrap"
                >
                  {saving ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4" /> Save Edits</>
                  )}
                </button>
              )}
              {batchStatus !== 'verified' && (
                <button
                  onClick={handleVerifyMonth}
                  disabled={verifying || hasEdits}
                  title={hasEdits ? 'Save your edits first' : ''}
                  className="btn btn-admin-primary self-center flex items-center gap-2 whitespace-nowrap"
                >
                  {verifying ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Verifying...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4" /> Verify All</>
                  )}
                </button>
              )}
              <button
                onClick={handleExportExcel}
                className="icon-button-admin"
                title={hasEdits
                  ? 'Save pending changes before exporting'
                  : 'Export all entries to Excel, one sheet per provider'}
                aria-label="Export Excel"
                disabled={editableEntries.length === 0 || hasEdits || saving}
              >
                <FileSpreadsheet className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Combined Entries Table — Column order follows HR mental model:
               Name (anchor) → Staff ID (confirmation) → FIN (technical) → Amount → Provider → Status */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            {filteredEntries.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  {searchTerm
                    ? 'No matching entries found'
                    : viewMode === 'unmatched'
                      ? 'All workers matched successfully'
                      : 'No entries to display'
                  }
                </p>
              </div>
            ) : (
              <>
                {/* Mobile cards (below md) */}
                <div className="md:hidden space-y-3 p-4">
                  {paginatedEntries.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className={`border border-gray-200 rounded-xl p-4 ${
                        entry.match_status !== 'matched' ? 'bg-orange-50/40' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-gray-400 tabular-nums">#{(currentPage - 1) * PCP_PAGE_SIZE + idx + 1}</p>
                          <PcpEditableCell
                            label="Worker name"
                            value={entry.worker_name}
                            onCommit={(v) => updateEntry(entry.id, 'worker_name', v)}
                            displayClassName="text-sm font-medium text-gray-900"
                            inputClassName="font-medium"
                            activateOnClick
                          />
                        </div>
                        <div className="flex-shrink-0">
                          <StatusBadge status={entry.match_status} />
                        </div>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        <div className={`flex justify-between gap-3 text-sm ${entry.matched_staff_name ? 'items-start' : 'items-center'}`}>
                          <span className="text-gray-500 flex-shrink-0">Staff ID</span>
                          <span className="min-w-0 text-right">
                            <PcpEditableCell
                              label="Staff ID"
                              value={entry.matched_staff_id || ''}
                              onCommit={(v) => updateEntry(entry.id, 'matched_staff_id', v)}
                              transform={(v) => v.toUpperCase()}
                              displayClassName="text-sm font-medium text-green-700"
                              inputClassName="font-medium"
                              align="right"
                              activateOnClick
                            />
                            {entry.matched_staff_name && (
                              <p className="text-[11px] text-gray-500 truncate max-w-[160px]">{entry.matched_staff_name}</p>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">FIN / NRIC</span>
                          <PcpEditableCell
                            label="FIN or NRIC"
                            value={entry.id_number}
                            onCommit={(v) => updateEntry(entry.id, 'id_number', v)}
                            displayClassName="text-sm text-gray-700 font-mono"
                            inputClassName="font-mono"
                            align="right"
                            activateOnClick
                          />
                        </div>
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Amount ($)</span>
                          <div className="flex items-center justify-end gap-0.5 min-w-0">
                            <span className="text-sm text-gray-900 font-medium">$</span>
                            <PcpEditableCell
                              label="Amount"
                              value={entry.total}
                              onCommit={(v) => updateEntry(entry.id, 'total', v)}
                              displayClassName="text-sm text-gray-900 font-medium tabular-nums"
                              inputClassName="font-medium tabular-nums"
                              align="right"
                              inputMode="decimal"
                              activateOnClick
                            />
                          </div>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Provider</span>
                          <span className="flex items-center justify-end gap-1 min-w-0">
                            <StatusBadge status="active" label={entry.provider_code || '—'} />
                            {entry.is_cross_provider && (
                              <StatusBadge status="in_progress" label="MULTI" />
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table (md and up) */}
                <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell-compact w-8">#</th>
                    <th className="table-header-cell-compact">Worker Name</th>
                    <th className="table-header-cell-compact">Staff ID</th>
                    <th className="table-header-cell-compact">FIN / NRIC</th>
                    <th className="table-header-cell-compact text-right">Amount ($)</th>
                    <th className="table-header-cell-compact text-center">Provider</th>
                    <th className="table-header-cell-compact text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {paginatedEntries.map((entry, idx) => (
                      <tr
                        key={entry.id}
                        className={`table-row-dense ${
                          entry.match_status !== 'matched' ? 'bg-orange-50/40' : ''
                        }`}
                      >
                        {/* # — global index across pages */}
                        <td className="table-cell-compact text-gray-400 tabular-nums">{(currentPage - 1) * PCP_PAGE_SIZE + idx + 1}</td>

                        {/* Worker name — selectable fact; edit on Enter or double-click */}
                        <td className="table-cell-compact">
                          <PcpEditableCell
                            label="Worker name"
                            value={entry.worker_name}
                            onCommit={(v) => updateEntry(entry.id, 'worker_name', v)}
                            displayClassName="text-sm text-gray-900"
                          />
                        </td>

                        {/* Staff ID — empty cell is the re-match action */}
                        <td className="table-cell-compact">
                          <PcpEditableCell
                            label="Staff ID"
                            value={entry.matched_staff_id || ''}
                            onCommit={(v) => updateEntry(entry.id, 'matched_staff_id', v)}
                            transform={(v) => v.toUpperCase()}
                            displayClassName="text-sm font-medium text-green-700"
                            inputClassName="font-medium"
                          />
                          {entry.matched_staff_name && (
                            <p className="text-[11px] text-gray-500 truncate max-w-[140px]">{entry.matched_staff_name}</p>
                          )}
                        </td>

                        {/* FIN / NRIC */}
                        <td className="table-cell-compact">
                          <PcpEditableCell
                            label="FIN or NRIC"
                            value={entry.id_number}
                            onCommit={(v) => updateEntry(entry.id, 'id_number', v)}
                            displayClassName="text-sm text-gray-700 font-mono"
                            inputClassName="font-mono"
                          />
                        </td>

                        {/* Amount (editable) */}
                        <td className="table-cell-compact text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <span className="text-sm text-gray-900 font-medium">$</span>
                            <PcpEditableCell
                              label="Amount"
                              value={entry.total}
                              onCommit={(v) => updateEntry(entry.id, 'total', v)}
                              displayClassName="text-sm text-gray-900 font-medium tabular-nums"
                              inputClassName="font-medium tabular-nums"
                              align="right"
                              inputMode="decimal"
                            />
                          </div>
                        </td>

                        {/* Provider */}
                        <td className="table-cell-compact text-center">
                          <div className="flex items-center justify-center gap-1">
                            <StatusBadge status="active" label={entry.provider_code || '—'} />
                            {entry.is_cross_provider && (
                              <StatusBadge status="in_progress" label="MULTI" />
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="table-cell-compact text-center">
                          <StatusBadge status={entry.match_status} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
                </div>
              </>
            )}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={filteredEntries.length}
              pageSize={PCP_PAGE_SIZE}
              theme="admin"
              noun="entries"
            />
          </div>

          {/* Upload History */}
          {uploadList.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Upload History
              </h3>
              <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                {/* Mobile cards (below md) */}
                <div className="md:hidden space-y-3 p-4">
                  {uploadList.map((item) => (
                    <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.provider_name || item.provider_code || '—'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {MONTH_NAMES[item.month - 1]} {item.year}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <StatusBadge
                            status={!item.is_active ? 'replaced' : item.status === 'verified' ? 'verified' : 'draft'}
                          />
                        </div>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Filename</span>
                          <span className="text-gray-900 text-right truncate min-w-0">{item.original_filename}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Matched</span>
                          <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{item.total_matched}/{item.total_entries}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Total</span>
                          <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{fmtCurrency(item.total_amount)}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Uploaded</span>
                          <span className="text-gray-900 text-right truncate min-w-0">
                            {new Date(item.uploaded_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table (md and up) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell-compact">Provider</th>
                        <th className="table-header-cell-compact">Period</th>
                        <th className="table-header-cell-compact">Filename</th>
                        <th className="table-header-cell-compact text-right">Matched</th>
                        <th className="table-header-cell-compact text-right">Total</th>
                        <th className="table-header-cell-compact">Uploaded</th>
                        <th className="table-header-cell-compact text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {uploadList.map((item) => (
                        <tr key={item.id} className="table-row-dense">
                          <td className="table-cell-compact font-medium text-gray-900 whitespace-nowrap">
                            {item.provider_name || item.provider_code || '—'}
                          </td>
                          <td className="table-cell-compact text-gray-700 whitespace-nowrap">
                            {MONTH_NAMES[item.month - 1]} {item.year}
                          </td>
                          <td className="table-cell-compact text-gray-600 max-w-[200px] truncate" title={item.original_filename}>
                            {item.original_filename}
                          </td>
                          <td className="table-cell-compact text-gray-700 text-right tabular-nums">
                            {item.total_matched}/{item.total_entries}
                          </td>
                          <td className="table-cell-compact text-gray-700 text-right tabular-nums">
                            {fmtCurrency(item.total_amount)}
                          </td>
                          <td className="table-cell-compact text-gray-500 whitespace-nowrap">
                            {new Date(item.uploaded_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="table-cell-compact text-center">
                            <StatusBadge
                              status={!item.is_active ? 'replaced' : item.status === 'verified' ? 'verified' : 'draft'}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PCPSkeleton = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 bg-gray-200 rounded-full" />
        <div className="h-4 w-48 bg-gray-200 rounded" />
        <div className="h-3 w-64 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
          <div className="h-3 w-20 bg-gray-200 rounded mb-3" />
          <div className="h-6 w-16 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden animate-pulse">
      <div className="p-4 space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex gap-4">
            <div className="h-4 w-8 bg-gray-200 rounded" />
            <div className="h-4 w-40 bg-gray-200 rounded" />
            <div className="h-4 w-24 bg-gray-200 rounded" />
            <div className="h-4 w-16 bg-gray-200 rounded" />
            <div className="h-4 w-20 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);


// ============================================================================
// SHARED COMPONENTS
// ============================================================================

const SummaryCard = ({ icon: Icon, label, value, subtitle, accent = 'gray' }) => {
  const accents = {
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${accents[accent] || accents.gray}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};


export default ProjectLabourCostPage;
